import React, { useState, useEffect } from "react";
import { Card, Button, Spinner, Badge, Modal, Form, ProgressBar, Dropdown } from "react-bootstrap";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { Activity, Package, Bell, History, ChevronDown, ChevronUp, Calendar, Clock, Plus, Trash2 } from "lucide-react";
import QuickQuizService, { QuizDetails, LookupData } from "../../_services/QuickQuizService";
import Config from "../../_config/config";
import QuestionTagSelector, { getSelectedTagIds } from "./QuestionTagSelector";
import ElementGuard from "../../access-control/ElementGuard";
import DvsPaperAllocationPanel from "./DvsPaperAllocationPanel";
import AnswerKeyModal from "./AnswerKeyModal";
import { useAccessControl } from "../../access-control/AccessControlContext";
import { useITeachConfig } from "../../_context/PresetContext";
import "./quick-quiz-maker.css";

const PAGE_KEY = "quick-quiz-maker";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Format date string like "14-Mar-2026" */
const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  return `${day}-${mon}-${year}`;
};

/**
 * Value for an <input type="date"> (wants "YYYY-MM-DD").
 *
 * A date-only / zone-less value ("2026-08-04", "2026-08-04 00:00:00") is the
 * tenant's calendar date already — take it verbatim rather than parsing it into
 * a Date, which would re-interpret it against the browser's timezone. Only a
 * genuinely zoned value (trailing Z or ±HH:MM) is converted, using LOCAL
 * getters — never toISOString(), which shifts IST midnight back a day.
 */
const toDateInputValue = (value?: string | null): string => {
  if (!value) return "";
  const raw = String(value).trim();
  const datePart = /^(\d{4}-\d{2}-\d{2})(?:[T ]|$)/.exec(raw);
  const isZoned = /(?:Z|[+-]\d{2}:?\d{2})$/.test(raw);
  if (datePart && !isZoned) return datePart[1]!;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Format datetime string like "14-Mar-2026 13:02" (24-hour) */
const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${day}-${mon}-${year} ${hours}:${mins}`;
};

interface QuizViewPanelProps {
  testId: number;
  onEdit: (testId: number) => void;
  onBack: () => void;
  onMonitor?: (testId: number) => void;
  onResult?: (testId: number) => void;
  onModerate?: (testId: number) => void;
  onTestQuestions?: (testId: number) => void;
  /**
   * When true, automatically open the "Edit Test Info" modal once the
   * quiz has finished loading. Used by the row-level Edit pencil from
   * the list, which deep-links to view+edit in one click.
   */
  autoOpenEdit?: boolean;
  /** Fired the first time autoOpenEdit is consumed, so the parent can clear the flag. */
  onAutoOpenEditConsumed?: () => void;
}

const labelStyle: React.CSSProperties = {
  fontSize: "0.75rem",
  color: "var(--text-muted)",
  fontWeight: 600,
  marginBottom: "2px",
  textTransform: "uppercase",
};

const valueStyle: React.CSSProperties = {
  fontSize: "0.85rem",
  marginBottom: "10px",
};

const InfoField: React.FC<{ label: string; value?: any }> = ({ label, value }) => (
  <div className="col-md-6 col-lg-4">
    <div style={labelStyle}>{label}</div>
    <div style={valueStyle}>{value ?? "-"}</div>
  </div>
);

/** Read-only display of an HTML rich-text field (instructions, declaration message, etc.). */
const RichInfoField: React.FC<{ label: string; html?: string | null }> = ({ label, html }) => {
  const hasContent = !!(html && String(html).trim() && String(html).replace(/<[^>]*>/g, "").trim());
  return (
    <div
      style={{
        height: "100%",
        border: "1px solid #eef1f6",
        borderRadius: 10,
        background: "var(--surface-page)",
        padding: "12px 14px",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.05)",
      }}
    >
      <div style={{ ...labelStyle, marginBottom: 6 }}>{label}</div>
      {hasContent ? (
        <div
          style={{ fontSize: "0.85rem", maxHeight: 220, overflowY: "auto", lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: html as string }}
        />
      ) : (
        <div style={{ fontSize: "0.85rem", color: "var(--text-faint)", fontStyle: "italic" }}>Not set</div>
      )}
    </div>
  );
};

/**
 * Shared inline style for every input inside the Edit Test Info modal.
 * Bootstrap's default Form.Control is functional but visually flat — this
 * gives the inputs a consistent height, soft border, and subtle background
 * so they look like a coherent set rather than a mix of form widgets.
 *
 * `width: 100%` + `boxSizing: border-box` are required so a native
 * `<select>` renders the same width as a sibling `<input>` (otherwise the
 * browser sizes the select to its longest option and it visibly shrinks
 * inside its grid column).
 */
const editInputStyle: React.CSSProperties = {
  fontSize: "0.875rem",
  padding: "8px 12px",
  border: "1px solid var(--border-default)",
  borderRadius: 8,
  background: "var(--surface-card)",
  color: "var(--text-strong)",
  width: "100%",
  height: 38,
  boxSizing: "border-box",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
};

/**
 * Variant for `<Form.Select>` — same as text inputs but with extra
 * right-padding so the browser-rendered chevron doesn't crowd the value.
 */
const editSelectStyle: React.CSSProperties = {
  ...editInputStyle,
  paddingRight: 32,
};

/** Field label style matching EditField — used for standalone modal fields (e.g. CKEditor blocks). */
const editFieldLabelStyle: React.CSSProperties = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "var(--text-body)",
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: 4,
};

/** Card-style section wrapper for the Edit Test Info modal. */
const EditSection: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div
    style={{
      background: "var(--surface-card)",
      border: "1px solid var(--border-default)",
      borderRadius: 12,
      padding: "14px 16px",
      marginBottom: 14,
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
    }}
  >
    <div
      style={{
        fontSize: "0.68rem",
        fontWeight: 700,
        color: "var(--brand-primary)",
        textTransform: "uppercase",
        letterSpacing: 1,
        marginBottom: 10,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span
        style={{
          width: 4,
          height: 14,
          borderRadius: 2,
          background: "var(--gradient-brand)",
          display: "inline-block",
        }}
      />
      {title}
    </div>
    <div className="row" style={{ rowGap: 12 }}>
      {children}
    </div>
  </div>
);

/** Single labelled field with an optional helper hint underneath. */
const EditField: React.FC<{
  label: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, hint, children }) => (
  <div className="col-md-6">
    <div
      style={{
        fontSize: "0.72rem",
        fontWeight: 700,
        color: "var(--text-body)",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        marginBottom: 4,
      }}
    >
      {label}
    </div>
    {children}
    {hint && (
      <div style={{ fontSize: "0.72rem", color: "var(--text-faint)", marginTop: 4, lineHeight: 1.4 }}>
        {hint}
      </div>
    )}
  </div>
);

/** Pill-style toggle used in place of stock checkboxes for the flag row. */
const PillToggle: React.FC<{
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}> = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 14px",
      borderRadius: 999,
      border: checked ? "1.5px solid #667eea" : "1px solid #e2e8f0",
      background: checked
        ? "linear-gradient(135deg, #eef2ff 0%, #ede9fe 100%)"
        : "#fff",
      color: checked ? "#4338ca" : "#475569",
      fontSize: "0.82rem",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 120ms",
      boxShadow: checked ? "0 2px 6px rgba(99, 102, 241, 0.18)" : "none",
    }}
  >
    <span
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        background: checked
          ? "var(--gradient-brand)"
          : "#fff",
        border: checked ? "none" : "1.5px solid #cbd5e1",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "var(--text-on-brand)",
        fontSize: 11,
        lineHeight: 1,
      }}
    >
      {checked && "✓"}
    </span>
    {label}
  </button>
);

const DOWNLOAD_BEFORE_OPTIONS = [
  { value: 0, label: "Anytime" },
  { value: 1, label: "Test StartTime" },
  { value: 2, label: "5 mins" },
  { value: 3, label: "10 mins" },
  { value: 4, label: "15 mins" },
  { value: 5, label: "30 mins" },
  { value: 6, label: "1 hr" },
  { value: 7, label: "2 hr" },
  { value: 8, label: "3 hr" },
  { value: 9, label: "1 day" },
  { value: 10, label: "1 week" },
];

const QuizViewPanel: React.FC<QuizViewPanelProps> = ({
  testId,
  // onEdit is still accepted on the props interface (parent passes it)
  // but no UI in this panel calls it now that "Edit Questions" was hidden.
  onBack,
  onMonitor,
  onResult,
  onModerate,
  onTestQuestions,
  autoOpenEdit,
  onAutoOpenEditConsumed,
}) => {
  const [quiz, setQuiz] = useState<QuizDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lookups, setLookups] = useState<LookupData | null>(null);

  // Edit Test Info modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [editYearIds, setEditYearIds] = useState<number[]>([]);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Section questions toggle & edit state
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  const [showSectionEditModal, setShowSectionEditModal] = useState(false);
  const [sectionEditForm, setSectionEditForm] = useState<any>({});
  const [sectionEditSaving, setSectionEditSaving] = useState(false);
  const [sectionEditError, setSectionEditError] = useState<string | null>(null);

  // Duration edit modal state
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [durationForm, setDurationForm] = useState<{ testTime: string; sections: Array<{ sectionId: number; name: string; time: string }> }>({ testTime: "", sections: [] });
  const [durationSaving, setDurationSaving] = useState(false);
  const [durationError, setDurationError] = useState<string | null>(null);

  // Publish modal states
  const [publishLookups, setPublishLookups] = useState<any>(null);
  const [publishStatuses, setPublishStatuses] = useState<any[]>([]);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [schoolForm, setSchoolForm] = useState<{ schoolId: string; tagId: string; parentTagId: string; issueType: "school" | "tag"; testDate: string }>({ schoolId: "", tagId: "", parentTagId: "", issueType: "school", testDate: "" });
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [courseForm, setCourseForm] = useState<{ courseId: string; courseIds: string[]; publishStatusId: string; batchId: string; programId: string; termId: string; isMultiple: boolean }>({ courseId: "", courseIds: [], publishStatusId: "1", batchId: "", programId: "", termId: "", isMultiple: false });
  const [showBatchPickerModal, setShowBatchPickerModal] = useState(false);
  const [batchPickerCourseId, setBatchPickerCourseId] = useState<number | null>(null);
  const [batchPickerSelected, setBatchPickerSelected] = useState<string>("");
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [studentForm, setStudentForm] = useState<{ type: "enquiryId" | "studentCode"; ids: string }>({ type: "enquiryId", ids: "" });
  const [existingStudents, setExistingStudents] = useState<Array<{ mappingId: number; enquiryId: number; firstName?: string; lastName?: string; studentCode?: string }>>([]);
  const [publishSaving, setPublishSaving] = useState(false);
  const [publishMsg, setPublishMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Answer Key (PDF) upload modal
  const [showAnswerKeyModal, setShowAnswerKeyModal] = useState(false);

  // Hall ticket generation state
  const [hallTicketLoading, setHallTicketLoading] = useState(false);
  // Two-step preview flow: open modal with HTML, then download PDF on confirm.
  const [showHallTicketPreview, setShowHallTicketPreview] = useState(false);
  const [hallTicketPreviewHtml, setHallTicketPreviewHtml] = useState<string | null>(null);
  const [hallTicketPdfLoading, setHallTicketPdfLoading] = useState(false);
  const [hallTicketStudentCount, setHallTicketStudentCount] = useState<number>(0);
  // Photo variant — admin toggles "with photos" / "without photos" in the
  // preview modal; both the preview and the downloaded PDF honour it.
  const [hallTicketWithPhoto, setHallTicketWithPhoto] = useState<boolean>(true);

  // Mark Foil — feature is gated on customer_detail_master.JsonConfig.markFoil.enableStatus,
  // surfaced via the iTeach config context. When not 1, the dropdown item is
  // hidden and the backend would refuse anyway.
  const { config: iTeachConfig } = useITeachConfig();
  const markFoilEnabled = Number(iTeachConfig?.markFoil?.enableStatus) === 1;

  // Mark Foil generation — mirrors the hall-ticket two-step pattern (preview HTML,
  // then confirm to download PDF). One foil page per course mapped to the test.
  const [markFoilLoading, setMarkFoilLoading] = useState(false);
  const [showMarkFoilPreview, setShowMarkFoilPreview] = useState(false);
  const [markFoilPreviewHtml, setMarkFoilPreviewHtml] = useState<string | null>(null);
  const [markFoilPdfLoading, setMarkFoilPdfLoading] = useState(false);
  const [markFoilPageCount, setMarkFoilPageCount] = useState<number>(0);
  const [markFoilError, setMarkFoilError] = useState<string | null>(null);
  // Batch picker: shown when the test has more than HALL_TICKET_BATCH_THRESHOLD
  // mapped students. Admin selects a single batch and the rest of the flow
  // (preview + PDF) is filtered to that batch.
  const HALL_TICKET_BATCH_THRESHOLD = 1000;
  const [showBatchPicker, setShowBatchPicker] = useState(false);
  const [batchPickerLoading, setBatchPickerLoading] = useState(false);
  const [hallTicketBatches, setHallTicketBatches] = useState<
    Array<{ batchId: number; batchName: string; studentCount: number }>
  >([]);
  const [hallTicketTotalCount, setHallTicketTotalCount] = useState<number>(0);
  const [hallTicketSelectedBatchId, setHallTicketSelectedBatchId] = useState<number | null>(null);

  // Audit section state
  const [showBundleAudit, setShowBundleAudit] = useState(false);
  const [showAlertsAudit, setShowAlertsAudit] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);

  // Question Management state
  const [showQMModal, setShowQMModal] = useState(false);
  const [qmSectionId, setQmSectionId] = useState(0);
  const [qmData, setQmData] = useState<any>(null);
  const [qmLoading, setQmLoading] = useState(false);
  // Edit Question modal (embeds /question-bank?mode=edit&id=X&embed=1 in an iframe)
  const [editQuestionId, setEditQuestionId] = useState<number | null>(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceForm, setReplaceForm] = useState<{ mappingId: number; questionId: number; index: number; newQuestionId: string }>({ mappingId: 0, questionId: 0, index: 0, newQuestionId: "" });
  const [addTagsQuestionId, setAddTagsQuestionId] = useState(0);
  const [showRemoveTagsModal, setShowRemoveTagsModal] = useState(false);
  const [removeTagsData, setRemoveTagsData] = useState<any>(null);
  // Full Add Tags modal state (like Java)
  const [showFullAddTagsModal, setShowFullAddTagsModal] = useState(false);
  const [fullTagTypes, setFullTagTypes] = useState<Array<{ id: number; classId: number; name: string; displayName: string; typeId: number; tags: Array<{ id: number; name: string }> }>>([]);
  const [qTagSelections, setQTagSelections] = useState<Record<number, Array<{ level: number; tagId: number; options: Array<{ id: number; name: string }> }>>>({});
  const [addedTagIds, setAddedTagIds] = useState<Array<{ typeId: number; tagId: number; tagName: string }>>([]);
  // Add Questions modal state
  const [showAddQuestionsModal, setShowAddQuestionsModal] = useState(false);
  const [addQForm, setAddQForm] = useState<{ questionIds: string; marks: string; negativeMarks: string }>({ questionIds: "", marks: "", negativeMarks: "0" });
  const [addQSaving, setAddQSaving] = useState(false);
  const [addQMsg, setAddQMsg] = useState<{ type: "success" | "danger"; text: string } | null>(null);
  // Question tag mapping import state
  const [qmImportFile, setQmImportFile] = useState<File | null>(null);
  const [qmImporting, setQmImporting] = useState(false);
  const [qmTemplateDownloading, setQmTemplateDownloading] = useState(false);
  const [qmImportMsg, setQmImportMsg] = useState<{ type: "success" | "danger"; text: string } | null>(null);

  // Sets modal state
  const [showSetsModal, setShowSetsModal] = useState(false);
  const [testSets, setTestSets] = useState<Array<{ id: number; name: string; consumed: boolean; json?: string }>>([]);
  const [setsLoading, setSetsLoading] = useState(false);

  // Question Rules modal state
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesData, setRulesData] = useState<any>(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleForm, setRuleForm] = useState<{ sectionId: number; numberOfQuestion: number; marksPerQuestion: number; negativeMarks: number; questionCategories: number[]; tags: number[]; _tagSelections?: Record<number, any> } | null>(null);
  const [ruleSaving, setRuleSaving] = useState(false);
  const [showPapersView, setShowPapersView] = useState(false);
  const [showMakePapersModal, setShowMakePapersModal] = useState(false);
  const [numPapers, setNumPapers] = useState(1);
  const [makingPapers, setMakingPapers] = useState(false);
  const [selectedSet, setSelectedSet] = useState<{ set: any; sections: any[] } | null>(null);
  const [setViewMode, setSetViewMode] = useState<"list" | "view" | "edit" | "create">("list");
  const [setFormName, setSetFormName] = useState("");
  const [setFormSections, setSetFormSections] = useState<Array<{ sectionId: number; sectionName: string; questions: Array<{ mappingId: number; questionName: string; stemStatement: string; setOrder: number }> }>>([]);
  const [setSaving, setSetSaving] = useState(false);

  // Clone modal state
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneForm, setCloneForm] = useState<{
    name: string; testDate: string; startDate: string; endDate: string;
    excludeQuestions: boolean; excludeCourseMappings: boolean;
  }>({ name: "", testDate: "", startDate: "", endDate: "", excludeQuestions: false, excludeCourseMappings: false });
  const [cloneSaving, setCloneSaving] = useState(false);
  const [cloneResult, setCloneResult] = useState<{ testId: number; name: string; totalQuestions: number } | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Tag mapping state
  const [tagLookups, setTagLookups] = useState<{
    tagTypes: Array<{ id: number; name: string; code: string }>;
    tags: Array<{ id: number; name: string; typeId: number }>;
    mappedTags: Array<{ mappingId: number; tagId: number; tagName: string; typeId: number }>;
  } | null>(null);
  const [tagSelections, setTagSelections] = useState<Record<number, string>>({});
  const [tagSaving, setTagSaving] = useState(false);

  const openQuestionManagement = async (sectionId: number) => {
    setQmSectionId(sectionId);
    setQmLoading(true);
    setShowQMModal(true);
    setQmImportFile(null);
    setQmImportMsg(null);
    try {
      const data = await QuickQuizService.getSectionQuestions(testId, sectionId);
      setQmData(data);
    } catch { /* ignore */ }
    setQmLoading(false);
  };

  const refreshQM = async () => {
    if (!qmSectionId) return;
    try {
      const data = await QuickQuizService.getSectionQuestions(testId, qmSectionId);
      setQmData(data);
    } catch { /* ignore */ }
  };

  const handleDownloadTagMappingTemplate = async () => {
    if (!qmSectionId) return;
    setQmTemplateDownloading(true);
    setQmImportMsg(null);
    try {
      await QuickQuizService.downloadQuestionTagMappingTemplate(testId, qmSectionId);
    } catch (err: any) {
      setQmImportMsg({ type: "danger", text: err?.response?.data?.message || err?.message || "Failed to download template" });
    } finally {
      setQmTemplateDownloading(false);
    }
  };

  const handleImportTagMapping = async () => {
    if (!qmSectionId || !qmImportFile) return;
    setQmImporting(true);
    setQmImportMsg(null);
    try {
      const result = await QuickQuizService.uploadQuestionTagMapping(testId, qmSectionId, qmImportFile);
      const s = result.summary;
      const detail = s ? ` (${s.rows} rows · ${s.tagsAdded} tags added · ${s.answersUpdated} answers updated)` : "";
      setQmImportMsg({ type: "success", text: (result.message || "Imported successfully") + detail });
      setQmImportFile(null);
      await refreshQM();
    } catch (err: any) {
      setQmImportMsg({ type: "danger", text: err?.response?.data?.message || err?.message || "Import failed" });
    } finally {
      setQmImporting(false);
    }
  };

  const handleReplaceQuestion = async () => {
    if (!replaceForm.newQuestionId) return;
    try {
      await QuickQuizService.replaceQuestion(testId, qmSectionId, replaceForm.mappingId, replaceForm.questionId, Number(replaceForm.newQuestionId));
      setShowReplaceModal(false);
      refreshQM();
    } catch { /* ignore */ }
  };

  const handleDeleteQuestion = async (mappingId: number) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    try {
      await QuickQuizService.deleteSpecificQuestion(testId, qmSectionId, mappingId);
      refreshQM();
    } catch { /* ignore */ }
  };

  const handleDeleteAllQuestions = async () => {
    if (!window.confirm("Are you sure you want to delete ALL questions from this section?")) return;
    try {
      await QuickQuizService.deleteAllQuestions(testId, qmSectionId);
      refreshQM();
    } catch { /* ignore */ }
  };

  // Full Add Tags modal (like Java — all tag types together)
  const openFullAddTagsModal = async (questionId: number) => {
    setAddTagsQuestionId(questionId);
    setAddedTagIds([]);
    try {
      const data = await QuickQuizService.getQuestionTagTypes();
      setFullTagTypes(data.tagTypes || []);
      // Initialize selections: each tag type gets level 0 dropdown
      const initial: Record<number, Array<{ level: number; tagId: number; options: Array<{ id: number; name: string }> }>> = {};
      for (const tt of (data.tagTypes || [])) {
        initial[tt.classId] = [{ level: 0, tagId: 0, options: tt.tags || [] }];
      }
      setQTagSelections(initial);
    } catch { setFullTagTypes([]); setQTagSelections({}); }
    setShowFullAddTagsModal(true);
  };

  const handleTagDropdownChange = async (classId: number, level: number, tagId: number) => {
    const current = [...(qTagSelections[classId] || [])];
    current[level] = { ...current[level], tagId };
    const trimmed = current.slice(0, level + 1);
    if (tagId > 0) {
      try {
        const data = await QuickQuizService.getChildTags(tagId);
        if (data.tags && data.tags.length > 0) {
          trimmed.push({ level: level + 1, tagId: 0, options: data.tags });
        }
      } catch { /* no children */ }
    }
    setQTagSelections({ ...qTagSelections, [classId]: trimmed });
  };

  const handleAddTagForType = (classId: number) => {
    const levels = qTagSelections[classId] || [];
    let deepestTagId = 0;
    for (const lvl of levels) {
      if (lvl.tagId > 0) deepestTagId = lvl.tagId;
    }
    if (!deepestTagId) return;
    let tagName = "";
    for (const lvl of levels) {
      const opt = lvl.options.find((o) => o.id === lvl.tagId);
      if (opt) tagName = opt.name;
    }
    const tt = fullTagTypes.find((t) => t.classId === classId);
    setAddedTagIds((prev) => [...prev, { typeId: tt?.typeId || classId, tagId: deepestTagId, tagName }]);
    if (tt) {
      setQTagSelections((prev) => ({ ...prev, [classId]: [{ level: 0, tagId: 0, options: tt.tags || [] }] }));
    }
  };

  const handleSaveAllTags = async () => {
    if (addedTagIds.length === 0) return;
    const tagIdStr = addedTagIds.map((t) => t.tagId).join(",");
    try {
      await QuickQuizService.addQuestionTags(testId, qmSectionId, addTagsQuestionId, tagIdStr);
      setShowFullAddTagsModal(false);
      refreshQM();
    } catch { /* ignore */ }
  };

  // Section marks capacity. TotalMarks ("Section Marks") is the defined cap;
  // the sum of question marks must not exceed it. A cap of 0/NULL means the
  // section is uncapped. Mirrors the server-side check in addQuestionsToSection.
  const sectionCapacity = () => {
    const cap = Number(qmData?.section?.TotalMarks) || 0;
    const current = (qmData?.questions || []).reduce((sum: number, q: any) => sum + (Number(q.marks) || 0), 0);
    const capDefined = cap > 0;
    return { cap, current, capDefined, remaining: capDefined ? cap - current : Infinity, full: capDefined && current >= cap };
  };

  // Add Questions handlers
  const openAddQuestionsModal = () => {
    if (qmData?.testTaken) {
      // eslint-disable-next-line no-alert
      window.alert("Some students have already started taking the Test.\nHence you are not allowed to add more questions.");
      return;
    }
    setAddQForm({ questionIds: "", marks: "", negativeMarks: "0" });
    const { full, current, cap } = sectionCapacity();
    setAddQMsg(full
      ? { type: "danger", text: `This section is full: its questions already total ${current} marks, which meets the section's total marks (${cap}). Increase the section's marks before adding more questions.` }
      : null);
    setShowAddQuestionsModal(true);
  };

  const handleAddQuestions = async () => {
    setAddQSaving(true);
    setAddQMsg(null);
    try {
      const result = await QuickQuizService.addQuestionsToSection(
        testId, qmSectionId, addQForm.questionIds, Number(addQForm.marks), Number(addQForm.negativeMarks || 0),
      );
      setAddQMsg({ type: "success", text: result.message });
      setAddQForm({ questionIds: "", marks: addQForm.marks, negativeMarks: addQForm.negativeMarks });
      refreshQM();
    } catch (err: any) {
      const apiMsg = err?.response?.data?.error?.message || err?.response?.data?.message;
      setAddQMsg({ type: "danger", text: apiMsg || "Failed to add questions" });
    } finally { setAddQSaving(false); }
  };

  const openRemoveTags = async (questionId: number) => {
    try {
      const data = await QuickQuizService.getQuestionTags(questionId);
      setRemoveTagsData(data);
      setShowRemoveTagsModal(true);
    } catch { /* ignore */ }
  };

  const handleQMRemoveTag = async (qtmId: number) => {
    try {
      await QuickQuizService.deleteQuestionTag(qtmId);
      // Refresh remove tags modal data
      if (removeTagsData?.questionId) {
        const data = await QuickQuizService.getQuestionTags(removeTagsData.questionId);
        setRemoveTagsData(data);
      }
      refreshQM();
    } catch { /* ignore */ }
  };

  const loadTestSets = async () => {
    setSetsLoading(true);
    try {
      const data = await QuickQuizService.getTestSets(testId);
      setTestSets(data.sets || []);
    } catch { /* ignore */ }
    setSetsLoading(false);
  };

  // Question Rules handlers
  const loadQuestionRules = async () => {
    setRulesLoading(true);
    try {
      const data = await QuickQuizService.getQuestionRules(testId);
      setRulesData(data);
    } catch { /* ignore */ }
    setRulesLoading(false);
  };

  const openRulesModal = () => {
    setRuleForm(null);
    setShowRulesModal(true);
    loadQuestionRules();
  };

  const handleSaveRule = async () => {
    if (!ruleForm) return;
    setRuleSaving(true);
    try {
      // Collect selected tags from cascading dropdowns
      const collectedTags = getSelectedTagIds(ruleForm._tagSelections || {});
      const payload = { ...ruleForm, tags: collectedTags };
      delete payload._tagSelections;
      await QuickQuizService.saveQuestionRule(testId, payload);
      setRuleForm(null);
      loadQuestionRules();
    } catch (err: any) {
      // eslint-disable-next-line no-restricted-globals
      window.alert(err?.response?.data?.message || "Failed to save rule");
    }
    setRuleSaving(false);
  };

  const handleDeleteRule = async (ruleId: number) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Delete this rule?")) return;
    try {
      await QuickQuizService.deleteQuestionRule(testId, ruleId);
      loadQuestionRules();
    } catch (err: any) {
      // eslint-disable-next-line no-restricted-globals
      window.alert(err?.response?.data?.message || "Failed to delete rule");
    }
  };

  const handleDeletePapers = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Delete all generated papers for this test?")) return;
    try {
      const result = await QuickQuizService.deleteTestPapers(testId);
      // eslint-disable-next-line no-restricted-globals
      window.alert(result?.message || "Papers deleted");
      loadQuestionRules();
    } catch (err: any) {
      // eslint-disable-next-line no-restricted-globals
      window.alert(err?.response?.data?.message || "Failed to delete papers");
    }
  };

  const handleMakeTestPapers = async () => {
    if (numPapers <= 0) return;
    setMakingPapers(true);
    try {
      const result = await QuickQuizService.makeTestPapers(testId, numPapers);
      // eslint-disable-next-line no-restricted-globals
      window.alert(result?.message || "Test papers generated successfully");
      setShowMakePapersModal(false);
      loadQuestionRules();
    } catch (err: any) {
      // eslint-disable-next-line no-restricted-globals
      window.alert(err?.response?.data?.message || "Failed to generate test papers");
    }
    setMakingPapers(false);
  };

  const openSetsModal = () => {
    setSetViewMode("list");
    setSelectedSet(null);
    setShowSetsModal(true);
    loadTestSets();
  };

  const viewSetQuestions = async (setId: number, mode: "view" | "edit") => {
    setSetsLoading(true);
    try {
      const data = await QuickQuizService.getSetQuestions(testId, setId);
      setSelectedSet(data);
      setSetFormName(data.set.name);
      setSetFormSections(data.sections.map((s: any) => ({
        ...s,
        questions: s.questions.map((q: any) => ({ ...q })),
      })));
      setSetViewMode(mode);
    } catch { /* ignore */ }
    setSetsLoading(false);
  };

  const startCreateSet = async () => {
    // Load all sections/questions from quiz to create a new set
    setSetsLoading(true);
    try {
      const sections = (quiz.sections || []).map((s: any) => ({
        sectionId: s.sectionId,
        sectionName: s.name,
        questions: (s.questions || []).map((q: any, i: number) => ({
          mappingId: q.mappingId,
          questionName: q.questionName || "",
          stemStatement: q.stemStatement || "",
          setOrder: i + 1,
        })),
      }));
      // Shuffle question orders randomly
      for (const sec of sections) {
        let globalOffset = 0;
        for (const s2 of sections) {
          if (s2.sectionId === sec.sectionId) break;
          globalOffset += s2.questions.length;
        }
        const orders = sec.questions.map((_: any, i: number) => globalOffset + i + 1);
        for (let i = orders.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [orders[i], orders[j]] = [orders[j], orders[i]];
        }
        sec.questions.forEach((q: any, i: number) => { q.setOrder = orders[i]; });
      }
      setSetFormName("");
      setSetFormSections(sections);
      setSetViewMode("create");
    } catch { /* ignore */ }
    setSetsLoading(false);
  };

  const handleSaveSet = async () => {
    if (!setFormName.trim()) return;
    setSetSaving(true);
    try {
      const sections = setFormSections.map((s) => ({
        sectionId: s.sectionId,
        questions: s.questions.map((q) => ({ mappingId: q.mappingId, order: q.setOrder })),
      }));
      if (setViewMode === "create") {
        await QuickQuizService.createSet(testId, setFormName, sections);
      } else if (setViewMode === "edit" && selectedSet) {
        await QuickQuizService.updateSet(testId, selectedSet.set.id, setFormName, sections);
      }
      setSetViewMode("list");
      loadTestSets();
    } catch { /* ignore */ }
    setSetSaving(false);
  };

  const handleDeleteSet = async (setId: number) => {
    try {
      await QuickQuizService.deleteSet(testId, setId);
      loadTestSets();
    } catch { /* ignore */ }
  };

  const shuffleSectionQuestions = (sectionIdx: number) => {
    setSetFormSections((prev) => {
      const updated = [...prev];
      const sec = { ...updated[sectionIdx], questions: [...updated[sectionIdx].questions] };
      // Calculate global offset
      let offset = 0;
      for (let i = 0; i < sectionIdx; i++) offset += updated[i].questions.length;
      const orders = sec.questions.map((_: any, i: number) => offset + i + 1);
      for (let i = orders.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [orders[i], orders[j]] = [orders[j], orders[i]];
      }
      sec.questions.forEach((q: any, i: number) => { q.setOrder = orders[i]; });
      updated[sectionIdx] = sec;
      return updated;
    });
  };

  const shuffleAllSections = () => {
    setSetFormSections((prev) => {
      let offset = 0;
      return prev.map((section) => {
        const questions = [...section.questions];
        const orders = questions.map((_: any, i: number) => offset + i + 1);
        for (let i = orders.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [orders[i], orders[j]] = [orders[j], orders[i]];
        }
        questions.forEach((q: any, i: number) => { q.setOrder = orders[i]; });
        offset += questions.length;
        return { ...section, questions };
      });
    });
  };

  const resetSectionOrder = (sectionIdx: number) => {
    setSetFormSections((prev) => {
      const updated = [...prev];
      let offset = 0;
      for (let i = 0; i < sectionIdx; i++) offset += updated[i].questions.length;
      const sec = { ...updated[sectionIdx], questions: [...updated[sectionIdx].questions] };
      sec.questions.forEach((q: any, i: number) => { q.setOrder = offset + i + 1; });
      updated[sectionIdx] = sec;
      return updated;
    });
  };

  const loadTagLookups = async () => {
    try {
      const data = await QuickQuizService.getTagLookups(testId);
      setTagLookups(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadTagLookups(); }, [testId]);
  useEffect(() => { loadPublishStatuses(); }, [testId]);

  // Listen for messages from the embedded /question-bank edit form (iframe).
  // The embedded page posts `qb-edit-saved` after a successful save and
  // `qb-edit-close` when the user closes its modal — in both cases we close
  // the iframe wrapper and refresh the question list so changes are visible.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e.data;
      if (!data || typeof data !== "object") return;
      if (data.type === "qb-edit-saved") {
        setEditQuestionId(null);
        refreshQM();
      } else if (data.type === "qb-edit-close") {
        setEditQuestionId(null);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qmSectionId]);

  const handleTestTagAdd = async (typeId: number) => {
    const tagId = Number(tagSelections[typeId]);
    if (!tagId) return;
    setTagSaving(true);
    try {
      await QuickQuizService.addTagMapping(testId, tagId);
      setTagSelections((prev) => ({ ...prev, [typeId]: "" }));
      await loadTagLookups();
    } catch { /* ignore */ }
    setTagSaving(false);
  };

  const handleTestTagRemove = async (mappingId: number) => {
    try {
      await QuickQuizService.removeTagMapping(testId, mappingId);
      setTagLookups((prev) => prev ? { ...prev, mappedTags: prev.mappedTags.filter((t) => t.mappingId !== mappingId) } : prev);
    } catch { /* ignore */ }
  };

  const loadPublishLookups = async () => {
    if (publishLookups) return;
    try {
      const data = await QuickQuizService.getPublishLookups(testId);
      setPublishLookups(data);
    } catch { /* ignore */ }
  };

  const loadPublishStatuses = async () => {
    try {
      const data = await QuickQuizService.getPublishLookups(testId);
      if (data?.publishStatuses) {
        setPublishStatuses(data.publishStatuses);
      }
    } catch { /* ignore */ }
  };

  const handleIssueSchool = async () => {
    setPublishSaving(true); setPublishMsg(null);
    try {
      const tagIdToUse = schoolForm.tagId || schoolForm.parentTagId;
      const base = schoolForm.issueType === "school" ? { schoolId: Number(schoolForm.schoolId) } : { tagId: Number(tagIdToUse) };
      const payload = schoolForm.testDate ? { ...base, testDate: schoolForm.testDate } : base;
      const result = await QuickQuizService.issueToSchool(testId, payload);
      setPublishMsg({ type: result.success ? "success" : "error", text: result.message });
      if (result.success) { setShowSchoolModal(false); loadQuiz(); }
    } catch (err: any) { setPublishMsg({ type: "error", text: err?.response?.data?.message || err.message }); }
    finally { setPublishSaving(false); }
  };

  const handleRemoveSchool = async (mappingId: number) => {
    if (!window.confirm("Remove this school mapping?")) return; // eslint-disable-line no-restricted-globals
    try { await QuickQuizService.removeSchoolMapping(testId, mappingId); loadQuiz(); }
    catch (err: any) { alert(err?.response?.data?.message || "Failed to remove"); }
  };

  const handleIssueCourse = async () => {
    // Program-based tenants: term is mandatory whenever a program is chosen
    if (courseForm.programId && !courseForm.termId) {
      setPublishMsg({ type: "error", text: "Term is required when a program is selected." });
      return;
    }
    setPublishSaving(true); setPublishMsg(null);
    try {
      const payload: any = { publishStatusId: Number(courseForm.publishStatusId) || 1 };
      if (courseForm.isMultiple) { payload.courseIds = courseForm.courseIds.map(Number); }
      else { payload.courseId = Number(courseForm.courseId); }
      if (courseForm.batchId) payload.batchId = Number(courseForm.batchId);
      if (courseForm.programId) payload.programId = Number(courseForm.programId);
      if (courseForm.termId) payload.termId = Number(courseForm.termId);
      const result = await QuickQuizService.issueToCourse(testId, payload);
      setPublishMsg({ type: result.success ? "success" : "error", text: result.message });
      if (result.success) { setShowCourseModal(false); loadQuiz(); }
    } catch (err: any) { setPublishMsg({ type: "error", text: err?.response?.data?.message || err.message }); }
    finally { setPublishSaving(false); }
  };

  const handleRemoveCourse = async (mappingId: number) => {
    if (!window.confirm("Remove this course mapping?")) return; // eslint-disable-line no-restricted-globals
    try { await QuickQuizService.removeCourseMapping(testId, mappingId); loadQuiz(); }
    catch (err: any) { alert(err?.response?.data?.message || "Failed to remove"); }
  };

  const handleAddStudents = async () => {
    setPublishSaving(true); setPublishMsg(null);
    try {
      const result = await QuickQuizService.addStudents(testId, studentForm.type, studentForm.ids);
      setPublishMsg({ type: result.success ? "success" : "error", text: result.message });
      if (result.success) {
        setStudentForm({ ...studentForm, ids: "" });
        QuickQuizService.getTestStudents(testId).then((d) => setExistingStudents(d.students || [])).catch(() => {});
      }
    } catch (err: any) { setPublishMsg({ type: "error", text: err?.response?.data?.message || err.message }); }
    finally { setPublishSaving(false); }
  };

  /**
   * Update test-level publish status (test_master.PublishStatusId) from the
   * Add Students modal. Mirrors the legacy Java modal behavior: changing the
   * dropdown immediately persists and triggers an incremental S3 re-bundle
   * (so the player JSON reflects the new publish state).
   */
  const handleUpdateTestPublishStatus = async (publishStatusId: number) => {
    setPublishSaving(true); setPublishMsg(null);
    try {
      const result = await QuickQuizService.updateTestPublishStatus(testId, publishStatusId);
      setPublishMsg({ type: result.success ? "success" : "error", text: result.message });
      if (result.success) loadQuiz();
    } catch (err: any) { setPublishMsg({ type: "error", text: err?.response?.data?.message || err.message }); }
    finally { setPublishSaving(false); }
  };

  // Bundle modal state
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [downloadBefore, setDownloadBefore] = useState(0);
  const [bundling, setBundling] = useState(false);
  const [bundleResult, setBundleResult] = useState<{ success: boolean; message: string } | null>(null);
  const [bundleProgress, setBundleProgress] = useState<{
    status: string;
    progressPercentage: number;
    totalPapers: number;
    papersCompleted: number;
  } | null>(null);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);

  const loadQuiz = () => {
    setLoading(true);
    setError(null);
    QuickQuizService.getQuiz(testId)
      .then(setQuiz)
      .catch((err) => setError(err.message || "Failed to load test details"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadQuiz();
    QuickQuizService.getLookups()
      .then(setLookups)
      .catch(() => {});
  }, [testId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Convert ISO/MySQL datetime to "YYYY-MM-DDTHH:mm" for datetime-local input
  const toDateTimeLocal = (dateStr?: string | null): string => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Convert a datetime-local input value ("YYYY-MM-DDTHH:mm") to the MySQL
  // DATETIME literal MySQL accepts ("YYYY-MM-DD HH:mm:ss"). The <input
  // type="datetime-local"> control emits a 'T' separator and omits seconds;
  // MySQL rejects the 'T', so sending the raw value silently fails to store.
  // Returns null for empty input so the column can be cleared.
  const toMysqlDateTime = (value?: string | null): string | null => {
    if (!value) return null;
    const s = String(value).trim();
    if (!s) return null;
    const withSpace = s.replace("T", " ");
    return /\d{2}:\d{2}:\d{2}$/.test(withSpace) ? withSpace : `${withSpace}:00`;
  };

  // --- Edit Test Info Modal ---
  const openEditModal = () => {
    if (!quiz) return;
    setEditForm({
      name: quiz.name || "",
      displayName: quiz.displayName || "",
      testDate: quiz.testDate ? quiz.testDate.split("T")[0] : "",
      testTime: quiz.testTime || "",
      categoryId: quiz.categoryId || "",
      testTypeId: quiz.testTypeId || "",
      playerId: quiz.playerId || "",
      testResultTypeId: quiz.testResultTypeId || "",
      startDate: toDateTimeLocal(quiz.startDate),
      endDate: toDateTimeLocal(quiz.endDate),
      testCutOffTime: toDateTimeLocal(quiz.testCutOffTime),
      sortOrder: quiz.sortOrder || "",
      choiceSequence: quiz.choiceSequence || "",
      remarks: quiz.remarks || "",
      instructions: quiz.instructions || "",
      mobileInstructions: quiz.mobileInstructions || "",
      testDeclarationRemarks: quiz.testDeclarationRemarks || "",
      studentAnnouncementRemarks: quiz.studentAnnouncementRemarks || "",
      password: quiz.testMasterPassword || "",
      rankDefinition: quiz.rankDefinition || false,
      keywordId: quiz.keywordId || "",
      supplementaryIndex: quiz.supplementaryIndexId || null,
      isBacklog: !!quiz.isBacklog,
      // Seed Total Marks from the stored value on the root section. If the
      // tenant's data never set it, fall back to the computed sum so the
      // user sees a meaningful starting value instead of 0.
      totalMarks:
        quiz.totalMarks !== undefined && quiz.totalMarks !== null
          ? quiz.totalMarks
          : quiz.sections.reduce((sum, s) => sum + (s.totalMarks || 0), 0),
    } as any);
    setEditYearIds(quiz.yearIds || []);
    setEditError(null);
    setShowEditModal(true);
  };

  // Auto-pop the Edit Test Info modal when the parent flagged a quick-edit.
  // Runs once: openEditModal needs `quiz`, and the parent clears the flag via
  // onAutoOpenEditConsumed so the effect doesn't re-fire.
  useEffect(() => {
    if (autoOpenEdit && quiz && !showEditModal) {
      openEditModal();
      onAutoOpenEditConsumed?.();
    }
  }, [autoOpenEdit, quiz]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditSave = async () => {
    // Mandatory fields. These match the columns the test_master row + root
    // test_section_master row cannot meaningfully exist without — the API
    // would otherwise either reject the row or silently no-op on a blank
    // field. Validating up front keeps a single consolidated error message
    // in the modal so the user knows exactly what's blocking the save.
    const missing: string[] = [];
    if (!editForm.name || String(editForm.name).trim() === "") missing.push("Test Name");
    if (!editForm.testDate) missing.push("Test Date");
    if (!editForm.categoryId) missing.push("Category");
    if (!editForm.keywordId) missing.push("Keyword");
    if (!editForm.testTypeId) missing.push("Test Type");
    if (!editForm.playerId) missing.push("Player");
    if (missing.length > 0) {
      setEditError(
        missing.length === 1
          ? `${missing[0]} is required.`
          : `Please fill the required fields: ${missing.join(", ")}.`,
      );
      return;
    }

    // Client-side gate for the totalMarks invariant. The backend enforces
    // the same rule and is authoritative, but failing fast here avoids a
    // round-trip and keeps the inline error attached to the field.
    if (editForm.totalMarks !== "" && editForm.totalMarks !== undefined) {
      const entered = Number(editForm.totalMarks);
      const sectionSum = quiz?.sections.reduce(
        (sum, s) => sum + (s.totalMarks || 0),
        0,
      ) ?? 0;
      if (!Number.isFinite(entered) || entered < 0) {
        setEditError("Total Marks must be a non-negative number.");
        return;
      }
      if (entered < sectionSum) {
        setEditError(
          `Total Marks (${entered}) cannot be less than the sum of section marks (${sectionSum}).`,
        );
        return;
      }
    }
    setEditSaving(true);
    setEditError(null);
    try {
      const payload: any = {};
      if (editForm.name) payload.name = editForm.name;
      // Always send displayName so clearing it persists (backend updates on !== undefined).
      payload.displayName = editForm.displayName || "";
      if (editForm.testDate) payload.testDate = editForm.testDate;
      if (editForm.testTime) payload.testTime = Number(editForm.testTime);
      if (editForm.categoryId) payload.categoryId = Number(editForm.categoryId);
      if (editForm.testTypeId) payload.testTypeId = Number(editForm.testTypeId);
      if (editForm.playerId) payload.playerId = Number(editForm.playerId);
      // Always send testResultTypeId - when Default selected (empty string), send 0 (backend converts to NULL)
      payload.testResultTypeId = editForm.testResultTypeId ? Number(editForm.testResultTypeId) : 0;
      // Always send these so a blank field clears the column (the backend
      // updates on `!== undefined`). Normalize the datetime-local 'T' value to
      // the MySQL literal so the write actually persists; null clears it.
      payload.startDate = toMysqlDateTime(editForm.startDate);
      payload.endDate = toMysqlDateTime(editForm.endDate);
      payload.testCutOffTime = toMysqlDateTime(editForm.testCutOffTime);
      payload.sortOrder = editForm.sortOrder ? Number(editForm.sortOrder) : undefined;
      payload.choiceSequence = editForm.choiceSequence || undefined;
      payload.remarks = editForm.remarks || undefined;
      // Rich-text fields: always send (even when blank) so the backend's
      // `!== undefined` check clears a removed value instead of leaving it.
      payload.instructions = editForm.instructions || "";
      payload.mobileInstructions = editForm.mobileInstructions || "";
      payload.testDeclarationRemarks = editForm.testDeclarationRemarks || "";
      payload.studentAnnouncementRemarks = editForm.studentAnnouncementRemarks || "";
      payload.password = editForm.password || undefined;
      payload.rankDefinition = editForm.rankDefinition;
      // keywordId — explicit null means "clear the keyword", so always send.
      payload.keywordId = editForm.keywordId ? Number(editForm.keywordId) : null;
      payload.supplementaryIndex = (editForm as any).supplementaryIndex || null;
      payload.isBacklog = (editForm as any).isBacklog || false;
      // totalMarks — only send when the user actually entered a value
      // (empty string means "don't touch"). Validation above already
      // ensured it's a finite number >= section sum.
      if (editForm.totalMarks !== "" && editForm.totalMarks !== undefined) {
        payload.totalMarks = Number(editForm.totalMarks);
      }
      payload.yearIds = editYearIds;

      const result = await QuickQuizService.updateTestInfo(testId, payload);
      if (result.success) {
        setShowEditModal(false);
        loadQuiz(); // Reload quiz data
      } else {
        setEditError(result.message || "Failed to update");
      }
    } catch (err: any) {
      setEditError(err?.response?.data?.message || err.message || "Failed to update test info");
    } finally {
      setEditSaving(false);
    }
  };

  // --- Bundle ---
  const startPolling = (tid: number) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const result = await QuickQuizService.getBundleStatus(tid);
        if (result.success && result.data) {
          setBundleProgress({
            status: result.data.status,
            progressPercentage: result.data.progressPercentage,
            totalPapers: result.data.totalPapers,
            papersCompleted: result.data.papersCompleted,
          });
          if (result.data.status === "COMPLETED" || result.data.status === "FAILED") {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setBundling(false);
            setBundleResult({
              success: result.data.status === "COMPLETED",
              message: result.data.status === "COMPLETED"
                ? "Bundle generation completed successfully."
                : result.data.errorMessage || "Bundle generation failed.",
            });
          }
        }
      } catch (err) {
        console.error("Error polling bundle status:", err);
      }
    }, 3000);
  };

  const handleFinalizeBundle = async () => {
    setBundling(true);
    setBundleResult(null);
    setBundleProgress(null);
    try {
      const result = await QuickQuizService.finalizeAndBundle(testId, downloadBefore);
      if (!result.success) {
        setBundling(false);
        setBundleResult({ success: false, message: result.message });
        return;
      }
      setBundleResult({ success: true, message: result.message });
      startPolling(testId);
    } catch (err: any) {
      setBundling(false);
      setBundleResult({
        success: false,
        message: err?.response?.data?.message || err.message || "Request failed",
      });
    }
  };

  const closeBundleModal = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setShowBundleModal(false);
    setBundling(false);
    setBundleResult(null);
    setBundleProgress(null);
  };

  const toggleSection = (sectionId: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const openSectionEditModal = (section: any) => {
    // The API returns examStartTime / examEndTime parsed from
    // SectionConfig JSON; fall back to parsing the raw blob if a
    // caller passed it through unparsed.
    let examStartTime: string = section.examStartTime || "";
    let examEndTime: string = section.examEndTime || "";
    // Per-subject schedule rows (e.g. Hindi / French / Sanskrit on different
    // dates within one language section) live in SectionConfig.schedules.
    let schedules: Array<{ subject: string; date: string; startTime: string; endTime: string }> = [];
    if (section.sectionConfig) {
      try {
        const cfg = typeof section.sectionConfig === "string"
          ? JSON.parse(section.sectionConfig)
          : section.sectionConfig;
        if (!examStartTime) examStartTime = cfg?.examStartTime || "";
        if (!examEndTime) examEndTime = cfg?.examEndTime || "";
        if (Array.isArray(cfg?.schedules)) {
          schedules = cfg.schedules.map((r: any) => ({
            subject: String(r?.subject ?? ""),
            date: String(r?.date ?? ""),
            startTime: String(r?.startTime ?? "").slice(0, 5),
            endTime: String(r?.endTime ?? "").slice(0, 5),
          }));
        }
      } catch { /* ignore */ }
    }
    // <input type="time"> wants HH:MM. If the source had HH:MM:SS, trim seconds.
    examStartTime = String(examStartTime).slice(0, 5);
    examEndTime = String(examEndTime).slice(0, 5);

    const testSectionDate = toDateInputValue(section.testSectionDate);
    setSectionEditForm({
      sectionId: section.sectionId,
      name: section.name || "",
      totalMarks: section.totalMarks ?? "",
      passMark: section.passMark ?? 0,
      keywordId: section.keywordId || "",
      isRankComputation: section.isRankComputation || false,
      sectionalTestTime: section.sectionalTestTime || "",
      negativeMarks: section.negativeMarks || "",
      testSectionDate,
      examStartTime,
      examEndTime,
      schedules,
    });
    setSectionEditError(null);
    setShowSectionEditModal(true);
  };

  const handleSectionEditSave = async () => {
    // Client-side gate for the section-marks invariant: the sum of all
    // sibling section marks (with this section's new value substituted) must
    // not exceed the test's total marks. The backend enforces the same rule
    // and is authoritative, but failing fast here keeps the error inline on
    // the field and avoids a round-trip. Only enforced when the test has an
    // explicitly-stored total (otherwise the test total just mirrors the sum).
    if (
      sectionEditForm.totalMarks !== "" &&
      sectionEditForm.totalMarks !== undefined &&
      sectionEditForm.totalMarks !== null
    ) {
      const entered = Number(sectionEditForm.totalMarks);
      if (!Number.isFinite(entered) || entered < 0) {
        setSectionEditError("Section Marks must be a non-negative number.");
        return;
      }
      const testTotal = quiz?.totalMarks;
      if (testTotal !== undefined && testTotal !== null) {
        const siblingSum = (quiz?.sections ?? []).reduce(
          (sum, s) => sum + (s.sectionId === sectionEditForm.sectionId ? 0 : s.totalMarks || 0),
          0,
        );
        const projected = siblingSum + entered;
        if (projected > testTotal) {
          setSectionEditError(
            `Section Marks (${entered}) would make the sum of all section marks (${projected}) exceed the test's total marks (${testTotal}). Reduce this section's marks or increase the test total first.`,
          );
          return;
        }
      }
    }
    setSectionEditSaving(true);
    setSectionEditError(null);
    try {
      const payload: any = {};
      if (sectionEditForm.name) payload.name = sectionEditForm.name;
      if (sectionEditForm.totalMarks !== "" && sectionEditForm.totalMarks !== undefined && sectionEditForm.totalMarks !== null) {
        payload.totalMarks = Number(sectionEditForm.totalMarks);
      }
      payload.passMark = Number(sectionEditForm.passMark) || 0;
      payload.keywordId = sectionEditForm.keywordId ? Number(sectionEditForm.keywordId) : null;
      payload.isRankComputation = sectionEditForm.isRankComputation;
      payload.sectionalTestTime = sectionEditForm.sectionalTestTime ? Number(sectionEditForm.sectionalTestTime) : null;
      payload.negativeMarks = sectionEditForm.negativeMarks ? Number(sectionEditForm.negativeMarks) : null;
      payload.testSectionDate = sectionEditForm.testSectionDate || null;
      // Keep only fully/partly filled subject-schedule rows: a row needs at
      // least a subject to be meaningful. Blank trailing rows are dropped.
      const cleanedSchedules = (Array.isArray(sectionEditForm.schedules) ? sectionEditForm.schedules : [])
        .map((r: any) => ({
          subject: String(r?.subject ?? "").trim(),
          date: String(r?.date ?? "").trim(),
          startTime: String(r?.startTime ?? "").trim(),
          endTime: String(r?.endTime ?? "").trim(),
        }))
        .filter((r: any) => r.subject || r.date || r.startTime || r.endTime);
      const cfg: Record<string, unknown> = {};
      if (sectionEditForm.examStartTime) cfg.examStartTime = sectionEditForm.examStartTime;
      if (sectionEditForm.examEndTime) cfg.examEndTime = sectionEditForm.examEndTime;
      if (cleanedSchedules.length > 0) cfg.schedules = cleanedSchedules;
      payload.sectionConfig = Object.keys(cfg).length > 0 ? JSON.stringify(cfg) : null;

      const result = await QuickQuizService.updateSectionInfo(sectionEditForm.sectionId, payload);
      if (result.success) {
        setShowSectionEditModal(false);
        loadQuiz();
      } else {
        setSectionEditError(result.message || "Failed to update section");
      }
    } catch (err: any) {
      setSectionEditError(err?.response?.data?.message || err.message || "Failed to update section");
    } finally {
      setSectionEditSaving(false);
    }
  };

  // Send Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertTemplates, setAlertTemplates] = useState<Array<{ id: number; name: string }>>([]);
  const [alertForm, setAlertForm] = useState({
    templateId: "",
    sms: false,
    email: false,
    whatsapp: false,
    student: false,
    father: false,
    mother: false,
    guardian: false,
    present: false,
    absent: false,
  });
  const [alertSending, setAlertSending] = useState(false);
  const [alertError, setAlertError] = useState<string | null>(null);
  const [alertSuccess, setAlertSuccess] = useState<string | null>(null);

  const openDurationModal = () => {
    if (!quiz) return;
    setDurationForm({
      testTime: String(quiz.testTime || ""),
      sections: quiz.sections.map((s) => ({
        sectionId: s.sectionId,
        name: s.name,
        time: String(s.sectionalTestTime || ""),
      })),
    });
    setDurationError(null);
    setShowDurationModal(true);
  };

  const handleDurationSave = async () => {
    setDurationSaving(true);
    setDurationError(null);
    try {
      // Update test time
      const newTestTime = Number(durationForm.testTime) || undefined;
      if (newTestTime !== quiz?.testTime) {
        await QuickQuizService.updateTestInfo(testId, { testTime: newTestTime });
      }
      // Update each section's sectional test time
      for (const sec of durationForm.sections) {
        const original = quiz?.sections.find((s) => s.sectionId === sec.sectionId);
        const newTime = sec.time ? Number(sec.time) : null;
        if (newTime !== (original?.sectionalTestTime || null)) {
          await QuickQuizService.updateSectionInfo(sec.sectionId, { sectionalTestTime: newTime });
        }
      }
      setShowDurationModal(false);
      loadQuiz();
    } catch (err: any) {
      setDurationError(err?.response?.data?.message || err.message || "Failed to update duration");
    } finally {
      setDurationSaving(false);
    }
  };

  const [retakeLoading, setRetakeLoading] = useState(false);
  // "Make Solutions Public" toggle — online tests only. State reflects the S3
  // ACL of the solution JSON files (see solutions-public.controller.ts).
  const [solutionsPublic, setSolutionsPublic] = useState(false);
  const [solPublicLoading, setSolPublicLoading] = useState(false);
  const [solPublicError, setSolPublicError] = useState<string | null>(null);
  const [computeRankLoading, setComputeRankLoading] = useState(false);
  const [populateLoading, setPopulateLoading] = useState(false);
  const [showPopulateModal, setShowPopulateModal] = useState(false);
  const [markAbsentLoading, setMarkAbsentLoading] = useState(false);
  const [showMarkAbsentModal, setShowMarkAbsentModal] = useState(false);
  const [markAbsentBatches, setMarkAbsentBatches] = useState<Array<{ batchId: number; batchName: string; studentCount: number }>>([]);
  const [markAbsentBatchesLoading, setMarkAbsentBatchesLoading] = useState(false);
  const [markAbsentSelectedIds, setMarkAbsentSelectedIds] = useState<number[]>([]);
  const [markAbsentResult, setMarkAbsentResult] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Root users and page admins may choose the destructive full-erase populate —
  // the same gate the page-access settings icon uses (ElementGuard adminOnly →
  // canAdminPage). Everyone else goes straight to the safe partial populate.
  const { canAdminPage, userRoleIds, isAdmin } = useAccessControl();

  // ── Digital Evaluation Allocation (DVS) visibility ──
  // Mirrors Java's gating: iTeach-config enable + per-role, the test category's
  // needsManualEvaluation flag, and evaluation type ∈ {null, 1, 3}.
  const dvsConfig = iTeachConfig.digitalEvaluation;
  const dvsEnabled = dvsConfig?.enableStatus === 1;
  const dvsCategoryOk = !!quiz?.needsManualEvaluation;
  const dvsEvalTypeOk =
    quiz?.evaluationTypeId == null || quiz.evaluationTypeId === 1 || quiz.evaluationTypeId === 3;
  const dvsCanUpload = isAdmin || (dvsConfig?.uploadRoles ?? []).some((r) => userRoleIds.includes(r));
  const dvsCanManage = isAdmin || (dvsConfig?.manageRoles ?? []).some((r) => userRoleIds.includes(r));
  const showDvsAllocation =
    dvsEnabled && dvsCategoryOk && dvsEvalTypeOk && (dvsCanUpload || dvsCanManage);
  const canChoosePopulateMode = canAdminPage(PAGE_KEY);

  // Open the batch-picker modal and load the batches mapped to this test.
  // Mirrors the legacy Java page, which prompted the user to pick one or more
  // batches before auto-marking absentees.
  const handleMarkAbsentees = async () => {
    setShowMarkAbsentModal(true);
    setMarkAbsentResult(null);
    setMarkAbsentSelectedIds([]);
    setMarkAbsentBatches([]);
    setMarkAbsentBatchesLoading(true);
    try {
      const res = await QuickQuizService.getMarkAbsenteeBatches(testId);
      setMarkAbsentBatches(res.batches || []);
      if (!res.batches?.length) {
        setMarkAbsentResult({ type: "error", text: "No batches are mapped to this test." });
      }
    } catch (err: any) {
      setMarkAbsentResult({
        type: "error",
        text: err?.response?.data?.message || err.message || "Failed to load batches",
      });
    }
    setMarkAbsentBatchesLoading(false);
  };

  const toggleMarkAbsentBatch = (batchId: number) => {
    setMarkAbsentSelectedIds((prev) =>
      prev.includes(batchId) ? prev.filter((id) => id !== batchId) : [...prev, batchId],
    );
  };

  const allMarkAbsentSelected =
    markAbsentBatches.length > 0 && markAbsentSelectedIds.length === markAbsentBatches.length;

  const toggleMarkAbsentSelectAll = () => {
    setMarkAbsentSelectedIds(allMarkAbsentSelected ? [] : markAbsentBatches.map((b) => b.batchId));
  };

  const confirmMarkAbsentees = async () => {
    if (markAbsentSelectedIds.length === 0) return;
    setMarkAbsentLoading(true);
    setMarkAbsentResult(null);
    try {
      const result = await QuickQuizService.markAbsentees(testId, markAbsentSelectedIds);
      setMarkAbsentResult({
        type: result.success ? "success" : "error",
        text: result.message || (result.success ? "Absentees marked." : "Failed to mark absentees."),
      });
      if (result.success) loadQuiz();
    } catch (err: any) {
      setMarkAbsentResult({
        type: "error",
        text: err?.response?.data?.message || err.message || "Failed to mark absentees",
      });
    }
    setMarkAbsentLoading(false);
  };

  // Full (destructive) populate — erases all RDS marks for the test (except
  // Excel / data-entry / auto-absent) and re-aggregates every student from
  // question-level data. Root-only, exposed via the choice modal.
  const runFullPopulate = async () => {
    setShowPopulateModal(false);
    setPopulateLoading(true);
    try {
      const result = await QuickQuizService.populateTestSections(testId);
      alert(result.message || (result.success ? "Populated successfully" : "Failed to populate"));
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = data?.message || err.message || "Failed to populate";
      const detail = data?.error;
      alert(detail ? `${msg}\n\nDetails: ${detail}` : msg);
    }
    setPopulateLoading(false);
  };

  // Partial (non-destructive) populate — adds ONLY students present in DynamoDB
  // but missing from RDS. Existing records are left untouched — nothing is deleted.
  const runPartialPopulate = async () => {
    setShowPopulateModal(false);
    setPopulateLoading(true);
    try {
      const result = await QuickQuizService.populateMissingStudents(testId);
      // Friendly, jargon-free summary for end users (the API message mentions RDS/DynamoDB).
      const added = result.populated ?? 0;
      const friendly = added > 0
        ? `Added ${added} student${added === 1 ? "" : "s"} to the results.`
        : "No new students to add — everyone who took the test is already in the results.";
      alert(result.success ? friendly : (result.message || "Failed to populate"));
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = data?.message || err.message || "Failed to populate";
      const detail = data?.error;
      alert(detail ? `${msg}\n\nDetails: ${detail}` : msg);
    }
    setPopulateLoading(false);
  };

  // Repair students whose sheet was finalized entirely blank (zero marks) while
  // their real answers are still in DynamoDB. Non-destructive to anyone who already
  // has real answers.
  const runRepairBlankSheets = async () => {
    setShowPopulateModal(false);
    setPopulateLoading(true);
    try {
      const result = await QuickQuizService.repairBlankSheets(testId);
      const fixed = result.repaired ?? 0;
      const friendly = fixed > 0
        ? `Re-synced ${fixed} student${fixed === 1 ? "" : "s"} from their saved answers.` +
          (result.readFailed ? ` ${result.readFailed} could not be read — please retry those.` : "")
        : (result.blankStudents ?? 0) === 0
          ? "No blank sheets found — nothing to repair."
          : "No answers were found in DynamoDB for the blank sheets (genuine non-attempts).";
      alert(result.success ? friendly : (result.message || "Failed to repair"));
    } catch (err: any) {
      const data = err?.response?.data;
      const msg = data?.message || err.message || "Failed to repair blank sheets";
      const detail = data?.error;
      alert(detail ? `${msg}\n\nDetails: ${detail}` : msg);
    }
    setPopulateLoading(false);
  };

  const handlePopulate = async () => {
    // Root users and page admins pick full-erase vs partial via the modal;
    // everyone else goes straight to the safe partial populate.
    if (canChoosePopulateMode) {
      setShowPopulateModal(true);
      return;
    }
    if (!window.confirm(
      "Add missing students to the results?\n\n" +
      "This brings in students who took the test online but whose marks haven't " +
      "appeared in the results yet. Existing students and their marks are not changed."
    )) return;
    await runPartialPopulate();
  };

  const handleComputeRanksGrades = async () => {
    if (!window.confirm("Are you sure you want to compute ranks and grades for this test?")) return;
    setComputeRankLoading(true);
    try {
      const result = await QuickQuizService.computeRanksAndGrades(testId);
      alert(result.message || (result.success ? "Ranks and grades computed successfully" : "Failed to compute ranks and grades"));
    } catch (err: any) {
      alert(err?.response?.data?.message || err.message || "Failed to compute ranks and grades");
    } finally {
      setComputeRankLoading(false);
    }
  };

  const handleEnableRetake = async () => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm("Are you sure you want to enable retake for this test?")) return;
    setRetakeLoading(true);
    try {
      const result = await QuickQuizService.enableRetake(testId);
      alert(result.message || (result.success ? "Retake enabled successfully" : "Failed to enable retake"));
    } catch (err: any) {
      alert(err?.response?.data?.message || err.message || "Failed to enable retake");
    } finally {
      setRetakeLoading(false);
    }
  };

  // Seed the "Make Solutions Public" toggle from the current S3 ACL — only for
  // online tests (the toggle is hidden otherwise).
  useEffect(() => {
    if (!quiz?.onlineFlag) return;
    let cancelled = false;
    QuickQuizService.getSolutionsPublicState(testId)
      .then((r) => { if (!cancelled) setSolutionsPublic(!!r?.isPublic); })
      .catch(() => { /* leave toggle off if state can't be read */ });
    return () => { cancelled = true; };
  }, [testId, quiz?.onlineFlag]);

  const handleToggleSolutionsPublic = async () => {
    const next = !solutionsPublic;
    setSolPublicLoading(true);
    setSolPublicError(null);
    try {
      const result = await QuickQuizService.setSolutionsPublic(testId, next ? "Public" : "Private");
      if (result.success) {
        setSolutionsPublic(!!result.isPublic);
      } else {
        setSolPublicError(result.message || "Failed to update solutions visibility");
      }
    } catch (err: any) {
      setSolPublicError(err?.response?.data?.message || err?.message || "Failed to update solutions visibility");
    } finally {
      setSolPublicLoading(false);
    }
  };

  // Loads the HTML preview for the test (optionally filtered to a single batch)
  // and opens the preview modal. Extracted so we can call it both from the
  // initial click and from the batch picker.
  const loadHallTicketPreview = async (batchId: number | null, withPhoto: boolean = hallTicketWithPhoto) => {
    setHallTicketLoading(true);
    setHallTicketPreviewHtml(null);
    setHallTicketStudentCount(0);
    setShowHallTicketPreview(true);
    try {
      const { html, studentCount } = await QuickQuizService.previewHallTicket(
        testId,
        batchId || undefined,
        withPhoto,
      );
      setHallTicketPreviewHtml(html);
      setHallTicketStudentCount(studentCount);
    } catch (err: any) {
      let msg = err?.message || "Failed to load hall ticket preview";
      try {
        const data = err?.response?.data;
        if (typeof data === "string" && data.startsWith("{")) {
          try { const p = JSON.parse(data); msg = p?.message || msg; } catch { /* keep */ }
        } else if (data?.message) {
          msg = data.message;
        }
      } catch { /* keep msg */ }
      setShowHallTicketPreview(false);
      alert(msg);
    } finally {
      setHallTicketLoading(false);
    }
  };

  // Step 1 — always show the batch picker so the admin must pick a batch
  // before generating the PDF. Prevents an unbounded all-students download
  // and keeps each PDF scoped to one batch.
  const handleGenerateHallTickets = async () => {
    setBatchPickerLoading(true);
    try {
      const { studentCount, batches } = await QuickQuizService.listHallTicketBatches(testId);
      setHallTicketTotalCount(studentCount);
      setHallTicketBatches(batches);
      setHallTicketSelectedBatchId(null);
      setHallTicketWithPhoto(true);
      setShowBatchPicker(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to fetch batch list";
      alert(msg);
    } finally {
      setBatchPickerLoading(false);
    }
  };

  const handleConfirmBatchSelection = async () => {
    if (!hallTicketSelectedBatchId) return;
    setShowBatchPicker(false);
    await loadHallTicketPreview(hallTicketSelectedBatchId);
  };

  // Step 2 — actually generate + download the PDF from the modal.
  const handleConfirmHallTicketPdf = async () => {
    setHallTicketPdfLoading(true);
    try {
      await QuickQuizService.generateHallTicket(
        testId,
        hallTicketSelectedBatchId || undefined,
        hallTicketWithPhoto,
      );
      setShowHallTicketPreview(false);
    } catch (err: any) {
      let msg = err?.message || "Failed to generate hall ticket PDF";
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === "function") {
          const text = await blob.text();
          try { const parsed = JSON.parse(text); msg = parsed?.message || msg; } catch { /* keep msg */ }
        }
      } catch { /* keep msg */ }
      alert(msg);
    } finally {
      setHallTicketPdfLoading(false);
    }
  };

  // Mark Foil — Step 1: fetch HTML preview and open modal.
  const handleGenerateMarkFoil = async () => {
    setMarkFoilLoading(true);
    setMarkFoilError(null);
    setMarkFoilPreviewHtml(null);
    setMarkFoilPageCount(0);
    setShowMarkFoilPreview(true);
    try {
      const { html, pageCount } = await QuickQuizService.previewMarkFoil(testId);
      setMarkFoilPreviewHtml(html);
      setMarkFoilPageCount(pageCount);
    } catch (err: any) {
      let msg = err?.message || "Failed to load Mark Foil preview";
      try {
        const data = err?.response?.data;
        if (typeof data === "string" && data.startsWith("{")) {
          const parsed = JSON.parse(data);
          msg = parsed?.message || msg;
        } else if (data?.message) {
          msg = data.message;
        }
      } catch { /* keep msg */ }
      setMarkFoilError(msg);
    } finally {
      setMarkFoilLoading(false);
    }
  };

  // Mark Foil — Step 2: generate + download PDF.
  const handleConfirmMarkFoilPdf = async () => {
    setMarkFoilPdfLoading(true);
    try {
      await QuickQuizService.generateMarkFoil(testId);
      setShowMarkFoilPreview(false);
    } catch (err: any) {
      let msg = err?.message || "Failed to generate Mark Foil PDF";
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === "function") {
          const text = await blob.text();
          try { const parsed = JSON.parse(text); msg = parsed?.message || msg; } catch { /* keep msg */ }
        }
      } catch { /* keep msg */ }
      alert(msg);
    } finally {
      setMarkFoilPdfLoading(false);
    }
  };

  const openCloneModal = () => {
    setCloneForm({
      name: quiz ? `${quiz.name} - Copy` : "",
      testDate: "",
      startDate: "",
      endDate: "",
      excludeQuestions: false,
      excludeCourseMappings: false,
    });
    setCloneResult(null);
    setCloneError(null);
    setShowCloneModal(true);
  };

  const handleClone = async () => {
    setCloneSaving(true);
    setCloneError(null);
    setCloneResult(null);
    try {
      const result = await QuickQuizService.cloneTest(testId, {
        name: cloneForm.name.trim() || undefined,
        testDate: cloneForm.testDate || undefined,
        // Normalize the datetime-local 'T' value to the MySQL literal so the
        // value is stored as-is in tenant-local time (same as the edit flow).
        // Do NOT send a UTC-shifted value — that's what corrupted the dates.
        startDate: toMysqlDateTime(cloneForm.startDate) || undefined,
        endDate: toMysqlDateTime(cloneForm.endDate) || undefined,
        excludeQuestions: cloneForm.excludeQuestions,
        excludeCourseMappings: cloneForm.excludeCourseMappings,
      });
      if (result.success && result.data) {
        setCloneResult({
          testId: result.data.testId,
          name: result.data.name,
          totalQuestions: result.data.totalQuestions,
        });
      } else {
        setCloneError(result.message || "Clone failed");
      }
    } catch (err: any) {
      setCloneError(err?.response?.data?.message || err.message || "Failed to clone test");
    } finally {
      setCloneSaving(false);
    }
  };

  const openAlertModal = async () => {
    setAlertForm({
      templateId: "",
      sms: false, email: false, whatsapp: false,
      student: false, father: false, mother: false, guardian: false,
      present: false, absent: false,
    });
    setAlertError(null);
    setAlertSuccess(null);
    setShowAlertModal(true);
    try {
      const data = await QuickQuizService.getAlertTemplates(testId);
      setAlertTemplates(data.templates || []);
    } catch {
      setAlertTemplates([]);
    }
  };

  const handleSendAlert = async () => {
    setAlertSending(true);
    setAlertError(null);
    setAlertSuccess(null);
    try {
      const result = await QuickQuizService.sendAlert(testId, {
        templateId: alertForm.templateId ? Number(alertForm.templateId) : undefined,
        alertVia: { sms: alertForm.sms, email: alertForm.email, whatsapp: alertForm.whatsapp },
        sendTo: { student: alertForm.student, father: alertForm.father, mother: alertForm.mother, guardian: alertForm.guardian },
        filterByAttendance: { present: alertForm.present, absent: alertForm.absent },
      });
      if (result.success) {
        setAlertSuccess(result.message || "Alert sent successfully");
        setTimeout(() => { setShowAlertModal(false); loadQuiz(); }, 1500);
      } else {
        setAlertError(result.message || "Failed to send alert");
      }
    } catch (err: any) {
      setAlertError(err?.response?.data?.message || err.message || "Failed to send alert");
    } finally {
      setAlertSending(false);
    }
  };

  const openJavaPage = (reqCode: string, extraParams = "") => {
    const url = `${Config.baseUrl}testCreation.do?reqCode=${reqCode}&testId=${testId}${extraParams}`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner role="status" animation="border" />
        <p className="text-muted mt-2">Loading test details...</p>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <Card>
        <Card.Body className="text-center py-4">
          <p className="text-danger">{error || "Test not found"}</p>
          <Button variant="secondary" size="sm" onClick={onBack} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>
            Back to Manage Tests
          </Button>
        </Card.Body>
      </Card>
    );
  }

  // Section-sum is the floor (the backend rejects any test-level total
  // below it). The displayed Total Marks prefers the explicitly-stored
  // root-section value when present — that lets users set the test total
  // higher than the section sum (buffer marks, unmapped marks, etc.).
  const sectionTotalSum = quiz.sections.reduce(
    (sum, s) => sum + (s.totalMarks || 0),
    0,
  );
  const totalMarks =
    quiz.totalMarks !== undefined && quiz.totalMarks !== null
      ? quiz.totalMarks
      : sectionTotalSum;
  const totalQuestions = quiz.sections.reduce((sum, s) => sum + (s.totalQuestions || 0), 0);

  return (
    <>
      <style>{`
        .quiz-sets-modal-xl { max-width: 95vw !important; }
        .quiz-sets-modal-xl .modal-content { width: 95vw; border: none; border-radius: 14px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); }
        .quiz-sets-modal-xl .modal-header { background: linear-gradient(135deg, #1e293b 0%, #334155 100%) !important; border-bottom: none; padding: 16px 24px !important; }
        .quiz-sets-modal-xl .modal-header .modal-title { font-weight: 600; letter-spacing: 0.3px; }
        .quiz-sets-modal-xl .modal-header .btn-close { filter: invert(1) brightness(2); opacity: 0.8; }
        .quiz-sets-modal-xl .modal-body { background: #f8fafc; padding: 24px !important; }
        .quiz-sets-modal-xl .modal-footer { background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 14px 24px !important; }
        .qs-set-card { transition: all 0.2s ease; cursor: default; }
        .qs-set-card:hover { transform: translateY(-2px); box-shadow: 0 12px 24px -8px rgba(15,23,42,0.15) !important; border-color: #6366f1 !important; }
        .qs-action-btn { transition: all 0.15s ease; }
        .qs-action-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 8px -2px rgba(0,0,0,0.1); }
        .qs-stat-card { transition: all 0.2s ease; }
        .qs-add-btn { background: var(--gradient-brand) !important; border: none !important; box-shadow: 0 4px 12px rgba(99,102,241,0.35); transition: all 0.2s ease; }
        .qs-add-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 16px rgba(99,102,241,0.45) !important; }
      `}</style>
      {/* Header */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <strong>Test View</strong>
            <span style={{ margin: "0 8px" }}>[ Id : {quiz.testId} ]</span>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.editTest">
              <a
                href="#edit-test"
                onClick={(e) => { e.preventDefault(); openEditModal(); }}
                title="Edit Test Info (name, dates, etc.)"
                style={{ fontSize: "1rem" }}
              >
                &#9998;
              </a>
            </ElementGuard>
          </span>
          <div className="d-flex gap-2">
            {/* Edit Questions button hidden — edit flow now goes through the
                Edit Test Info modal (opened from the row pencil or the title
                marker icon) plus inline section/question controls below. */}
            {/* <ElementGuard pageKey={PAGE_KEY} elementKey="action.editQuestions">
              <Button variant="primary" size="sm" onClick={() => onEdit(testId)} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}>
                Edit Questions
              </Button>
            </ElementGuard> */}
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.refreshTest">
              <Button variant="outline-primary" size="sm" onClick={loadQuiz} style={{ backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}>
                Refresh
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.backToList">
              <Button variant="outline-secondary" size="sm" onClick={onBack} style={{ backgroundColor: "var(--surface-card)", color: "var(--text-muted)", border: "1px solid #6c757d", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}>
                Back
              </Button>
            </ElementGuard>
          </div>
        </Card.Header>
      </Card>

      {/* Action Buttons Row - Placed prominently near the top */}
      <Card className="mb-3">
        <Card.Body style={{ padding: "8px 12px" }}>
          <div className="d-flex flex-wrap gap-1 align-items-center">
            {!!quiz?.allowQuestionRules ? (
              <ElementGuard pageKey={PAGE_KEY} elementKey="action.rulesModal">
                <Button
                  variant="info"
                  size="sm"
                  style={{ backgroundColor: "var(--status-info)", color: "var(--text-on-brand)", border: "1px solid var(--status-info)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                  onClick={openRulesModal}
                >
                  Rules for Question Generation
                </Button>
              </ElementGuard>
            ) : (
              <ElementGuard pageKey={PAGE_KEY} elementKey="action.setsModal">
                <Button
                  variant="warning"
                  size="sm"
                  style={{ backgroundColor: "#e0a800", color: "var(--text-strong)", border: "1px solid #e0a800", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                  onClick={openSetsModal}
                >
                  Sets
                </Button>
              </ElementGuard>
            )}
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.finaliseBundle">
              <Button variant="success" size="sm"
                style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={() => { setDownloadBefore(0); setBundleResult(null); setBundleProgress(null); setShowBundleModal(true); }}
              >
                Finalise and Bundle
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.answerKeyChange">
              <Button variant="primary" size="sm"
                style={{ backgroundColor: "#2c3e50", color: "var(--text-on-brand)", border: "1px solid #2c3e50", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={() => onTestQuestions ? onTestQuestions(testId) : openJavaPage("testQuestions")}
              >
                Answer Key Change
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.populate">
              <Button variant="primary" size="sm"
                style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={handlePopulate} disabled={populateLoading}
              >
                {populateLoading ? "Populating..." : "Populate"}
              </Button>
            </ElementGuard>

            {/* Root-only: choose between full-erase and partial populate. */}
            <Modal show={showPopulateModal} onHide={() => setShowPopulateModal(false)} centered>
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: "1rem" }}>Populate marks — choose mode</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ border: "1px solid var(--border-default)", borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ fontWeight: 700, color: "var(--brand-primary)", marginBottom: 4 }}>Partial populate (safe)</div>
                    <div style={{ fontSize: "0.85rem", color: "var(--text-body)" }}>
                      Adds ONLY students present in DynamoDB but missing from RDS. Existing records
                      are left untouched — nothing is deleted.
                    </div>
                    <Button variant="primary" size="sm" disabled={populateLoading} onClick={runPartialPopulate}
                      style={{ marginTop: 10, backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)" }}>
                      Run partial populate
                    </Button>
                  </div>
                  <div style={{ border: "1px solid var(--border-default)", borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ fontWeight: 700, color: "var(--brand-primary)", marginBottom: 4 }}>Repair blank sheets (zero-mark fix)</div>
                    <div style={{ fontSize: "0.85rem", color: "var(--text-body)" }}>
                      Fixes students showing 0 marks / all-unattempted whose answers are still in
                      DynamoDB — re-reads and re-grades only the blank sheets. Students with real
                      answers and genuine non-attempts are left untouched.
                    </div>
                    <Button variant="primary" size="sm" disabled={populateLoading} onClick={runRepairBlankSheets}
                      style={{ marginTop: 10, backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)" }}>
                      Repair blank sheets
                    </Button>
                  </div>
                  <div style={{ border: "1px solid var(--status-danger-border)", borderRadius: 8, padding: "12px 14px", background: "var(--status-danger-bg)" }}>
                    <div style={{ fontWeight: 700, color: "var(--status-danger)", marginBottom: 4 }}>Full populate (erase &amp; rebuild)</div>
                    <div style={{ fontSize: "0.85rem", color: "var(--status-danger-text)" }}>
                      Deletes ALL existing marks for this test (except Excel / data-entry / auto-absent)
                      and re-aggregates every student from question-level data. Irreversible.
                    </div>
                    <Button variant="danger" size="sm" disabled={populateLoading} onClick={runFullPopulate}
                      style={{ marginTop: 10, backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "1px solid var(--status-danger)" }}>
                      Run full populate
                    </Button>
                  </div>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" size="sm" disabled={populateLoading} onClick={() => setShowPopulateModal(false)}
                  style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d" }}>
                  Cancel
                </Button>
              </Modal.Footer>
            </Modal>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.markAbsentees">
              <Button
                variant="primary"
                size="sm"
                className="mark-absent"
                style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={handleMarkAbsentees}
                disabled={markAbsentLoading}
                title="Mark non-attempted students as absent"
              >
                {markAbsentLoading ? "Marking..." : "Mark Absentees"}
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.computeRankGrade">
              <Button variant="warning" size="sm"
                style={{ backgroundColor: "#e67e22", color: "var(--text-on-brand)", border: "1px solid #e67e22", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={handleComputeRanksGrades} disabled={computeRankLoading}
              >
                {computeRankLoading ? "Computing..." : "Compute Rank & Grade"}
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.viewResults">
              <Button variant="primary" size="sm"
                style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={() => onResult ? onResult(testId) : openJavaPage("result")}
              >
                Result
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.marksModeration">
              <Button variant="info" size="sm"
                style={{ backgroundColor: "var(--status-info)", color: "var(--text-on-brand)", border: "1px solid var(--status-info)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={() => onModerate ? onModerate(testId) : openJavaPage("marksModeration")}
              >
                Marks Moderation
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.sendAlert">
              <Button variant="danger" size="sm"
                style={{ backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "1px solid var(--status-danger)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={openAlertModal}
              >
                Send Alert
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.cloneTest">
              <Button variant="secondary" size="sm"
                style={{ backgroundColor: "#6f42c1", color: "var(--text-on-brand)", border: "1px solid #6f42c1", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={openCloneModal}
              >
                Clone Test
              </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.monitoring">
              <Button variant="primary" size="sm"
                style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
                onClick={() => onMonitor ? onMonitor(testId) : openJavaPage("monitoring")}
              >
                Monitoring
              </Button>
            </ElementGuard>
            {quiz?.allowRetake ? (
              <ElementGuard pageKey={PAGE_KEY} elementKey="action.enableRetake">
                <Button variant="warning" size="sm"
                  style={{
                    backgroundColor: quiz?.retakeAlreadyEnabled ? "#6c757d" : "#ffc107",
                    color: quiz?.retakeAlreadyEnabled ? "#fff" : "#212529",
                    border: `1px solid ${quiz?.retakeAlreadyEnabled ? "#6c757d" : "#ffc107"}`,
                    margin: "1px", fontSize: "0.8rem", padding: "5px 12px",
                  }}
                  onClick={handleEnableRetake} disabled={retakeLoading || quiz?.retakeAlreadyEnabled}
                >
                  {retakeLoading ? "Enabling..." : quiz?.retakeAlreadyEnabled ? "Already Enabled Retake Test" : "Enable Retake Test"}
                </Button>
              </ElementGuard>
            ) : null}
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.admissionTicket">
            <Button variant="success" size="sm"
              style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", margin: "1px", fontSize: "0.8rem", padding: "5px 12px" }}
              onClick={handleGenerateHallTickets}
              disabled={hallTicketLoading || batchPickerLoading}
            >
              {(hallTicketLoading || batchPickerLoading) ? (
                <><Spinner animation="border" size="sm" className="me-1" />{batchPickerLoading ? "Checking..." : "Generating..."}</>
              ) : "Generate Admission Ticket"}
            </Button>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.supportedDocs">
            <Dropdown style={{ display: "inline-block", margin: "2px" }}>
              <Dropdown.Toggle variant="primary" size="sm" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", fontSize: "0.8rem", padding: "5px 12px" }}>
                Supported Docs
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/excel-attendance`, "_blank")}>
                  Generate Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/exam-attendance`, "_blank")}>
                  Exam Attendance
                </Dropdown.Item>
                <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/consolidated-sheet`, "_blank")}>
                  Consolidated Sheet
                </Dropdown.Item>
                <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/answer-sheet`, "_blank")}>
                  Answer Sheet
                </Dropdown.Item>
                <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/cover-sheet`, "_blank")}>
                  Cover Sheet
                </Dropdown.Item>
                {markFoilEnabled && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleGenerateMarkFoil} disabled={markFoilLoading}>
                      {markFoilLoading ? "Loading Mark Foil…" : "Mark Foil"}
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
            </ElementGuard>
            <ElementGuard pageKey={PAGE_KEY} elementKey="action.answerKey">
            <Button variant="primary" size="sm"
              style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px", fontSize: "0.8rem", padding: "5px 12px" }}
              onClick={() => setShowAnswerKeyModal(true)}
            >
              Answer Key
            </Button>
            </ElementGuard>
            {/* Make Solutions Public — online tests only (test_type_master.OnlineFlag).
                Grants/revokes public read on the `_with_solutions.json` S3 files. */}
            {!!quiz?.onlineFlag ? (
              <ElementGuard pageKey={PAGE_KEY} elementKey="action.makeSolutionsPublic">
                <span
                  className="d-inline-flex align-items-center"
                  style={{ margin: "1px", padding: "0 8px", gap: "6px" }}
                >
                  <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>Make Solutions Public</span>
                  {/* Custom inline-styled toggle. We avoid react-bootstrap's
                      `Form.Check type="switch"` here because the app's global CSS
                      clobbers `.form-switch`, making it fall back to a checkbox. */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={solutionsPublic}
                    disabled={solPublicLoading}
                    onClick={handleToggleSolutionsPublic}
                    title={solutionsPublic ? "Solutions are public" : "Solutions are private"}
                    style={{
                      position: "relative",
                      width: "40px",
                      height: "22px",
                      borderRadius: "11px",
                      border: "none",
                      padding: 0,
                      cursor: solPublicLoading ? "not-allowed" : "pointer",
                      backgroundColor: solutionsPublic ? "#28a745" : "#ced4da",
                      transition: "background-color 0.2s ease",
                      opacity: solPublicLoading ? 0.6 : 1,
                      flex: "0 0 auto",
                    }}
                  >
                    <span
                      style={{
                        position: "absolute",
                        top: "2px",
                        left: solutionsPublic ? "20px" : "2px",
                        width: "18px",
                        height: "18px",
                        borderRadius: "50%",
                        backgroundColor: "var(--surface-card)",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                        transition: "left 0.2s ease",
                      }}
                    />
                  </button>
                  {solPublicLoading ? <Spinner animation="border" size="sm" /> : null}
                </span>
              </ElementGuard>
            ) : null}
          </div>
        </Card.Body>
      </Card>

      {/* Error feedback for the Make Solutions Public toggle (e.g. "Finalise and
          Bundle first"). Modal per app convention — no window.alert. */}
      <Modal show={!!solPublicError} onHide={() => setSolPublicError(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Make Solutions Public</Modal.Title>
        </Modal.Header>
        <Modal.Body>{solPublicError}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSolPublicError(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Digital Evaluation Allocation — gated by iTeach config (enable + roles),
          the category's needsManualEvaluation flag, and evaluation type. */}
      {showDvsAllocation && (
        <DvsPaperAllocationPanel testId={Number(testId)} canUpload={dvsCanUpload} canManage={dvsCanManage} />
      )}

      {/* Test Details + Section Table */}
      <div className="row mb-3">
        {/* Left Panel - All Test Info */}
        <div className="col-md-5">
          <Card>
            <Card.Body style={{ fontSize: "0.85rem" }}>
              <div className="row">
                <InfoField label="Test Name" value={quiz.name} />
                <InfoField label="Test Display Name" value={quiz.displayName} />
                <InfoField label="Test Date" value={formatDate(quiz.testDate)} />
                <InfoField label="Total Marks" value={totalMarks} />
                <InfoField label="Keyword" value={quiz.keywordName || quiz.keywordCode} />
                <InfoField label="Supplementary Index" value={quiz.supplementaryIndexName || (quiz.supplementaryIndexId ? `#${quiz.supplementaryIndexId}` : "-")} />
                <InfoField label="Backlog" value={quiz.isBacklog ? "Yes" : "No"} />
                <InfoField label="Rank Definition Req." value={quiz.rankDefinition ? "true" : "false"} />
                <InfoField label="Test Type" value={quiz.testTypeName} />
                <InfoField label="Player" value={quiz.playerName} />
                <InfoField label="Category" value={quiz.categoryName} />
                <InfoField label="Test Time" value={quiz.testTime ? `${quiz.testTime} mins` : undefined} />
                <InfoField label="Start Date" value={formatDateTime(quiz.startDate)} />
                <InfoField label="End Date" value={formatDateTime(quiz.endDate)} />
                <InfoField label="Test CutOff Time" value={formatDateTime(quiz.testCutOffTime)} />
                <InfoField label="Sort Order" value={quiz.sortOrder} />
                <InfoField label="Choice Sequence" value={quiz.choiceSequence} />
                <InfoField label="Remarks" value={quiz.remarks} />
                <InfoField label="Test Master Password" value={quiz.testMasterPassword} />
                <InfoField
                  label="Test Result Type"
                  value={
                    quiz.testResultTypeId === 1
                      ? "Instant Result"
                      : quiz.testResultTypeId
                        ? `#${quiz.testResultTypeId}`
                        : "Default"
                  }
                />
                <InfoField label="Total Questions" value={totalQuestions} />
              </div>
              {quiz.yearIds && quiz.yearIds.length > 0 && lookups?.years && (
                <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-subtle)' }}>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 10 }}>Years</span>
                  {quiz.yearIds.map(id => {
                    const y = lookups.years.find(yr => yr.id === id);
                    return y ? (
                      <span key={id} style={{
                        display: 'inline-block', padding: '2px 10px', margin: '2px 4px',
                        borderRadius: 12, fontSize: '0.75rem', fontWeight: 500,
                        background: 'var(--brand-primary-subtle)', color: 'var(--brand-primary-hover)', border: '1px solid var(--brand-primary-subtle)',
                      }}>{y.name}</span>
                    ) : null;
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Right Panel - Subject / Section Table */}
        <div className="col-md-7">
          <Card>
            <Card.Body style={{ padding: "8px" }}>
              <div style={{ fontSize: "0.8rem", overflowX: "auto" }}>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Subject Name</th>
                      <th className="text-center">Marks</th>
                      <th className="text-center">Pass Mark</th>
                      <th className="text-center">Keyword</th>
                      <th className="text-center">Rank Computation</th>
                      <th className="text-center">Test Subject</th>
                      <th className="text-center">
                        <a href="#edit-duration" onClick={(e) => { e.preventDefault(); openDurationModal(); }} title="Edit Test Duration" style={{ fontSize: "0.85rem" }}>&#9998;</a> Mins
                      </th>
                      <th className="text-center" style={{ minWidth: "130px" }}>Questions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quiz.sections.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center text-muted py-3">
                          No sections found
                        </td>
                      </tr>
                    ) : (
                      <>
                        <tr>
                          <td><strong>{quiz.name}</strong></td>
                          <td className="text-center">{totalMarks}</td>
                          <td className="text-center">0</td>
                          <td className="text-center">
                            <select className="form-select form-select-sm"
                              key={`test-kw-${quiz.keywordId ?? ''}-${lookups?.keywords?.length ?? 0}`}
                              defaultValue={quiz.keywordId || ""}
                              style={{ fontSize: "0.78rem", padding: "2px 4px", border: "1px solid transparent", background: "transparent", cursor: "pointer", textAlign: "center" }}
                              onFocus={(e) => { e.target.style.border = "1px solid var(--brand-primary)"; e.target.style.background = "var(--surface-card)"; }}
                              onBlur={(e) => { e.target.style.border = "1px solid transparent"; e.target.style.background = "transparent"; }}
                              onChange={async (e) => {
                                try {
                                  await QuickQuizService.updateTestInfo(testId, { keywordId: Number(e.target.value) || null });
                                  loadQuiz();
                                } catch { /* ignore */ }
                              }}>
                              <option value="">--</option>
                              {lookups?.keywords.map((k) => (
                                <option key={k.id} value={k.id}>{k.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="text-center"></td>
                          <td className="text-center"></td>
                          <td className="text-center">{quiz.testTime || ""}</td>
                          <td className="text-center"></td>
                        </tr>
                        {quiz.sections.map((section) => (
                          <React.Fragment key={section.sectionId}>
                            <tr style={{ backgroundColor: "var(--surface-page)" }}>
                              <td style={{ paddingLeft: "20px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                  <span>{section.name}</span>
                                  <a
                                    href="#edit-section"
                                    onClick={(e) => { e.preventDefault(); openSectionEditModal(section); }}
                                    title="Edit Section"
                                    style={{ fontSize: "0.85rem" }}
                                  >
                                    &#9998;
                                  </a>
                                </div>
                                {(section.testSectionDate || section.examStartTime || section.examEndTime) && (
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "10px",
                                      marginTop: "2px",
                                      fontSize: "0.72rem",
                                      color: "var(--text-muted)",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {section.testSectionDate && (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <Calendar size={11} strokeWidth={2} />
                                        {formatDate(section.testSectionDate)}
                                      </span>
                                    )}
                                    {(section.examStartTime || section.examEndTime) && (
                                      <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                                        <Clock size={11} strokeWidth={2} />
                                        {String(section.examStartTime || "").replace(/:00$/, "") || "—"}
                                        {section.examEndTime && (
                                          <> – {String(section.examEndTime).replace(/:00$/, "")}</>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="text-center">{section.totalMarks}</td>
                              <td className="text-center">{section.passMark ?? 0}</td>
                              <td className="text-center">
                                <select className="form-select form-select-sm"
                                  key={`sec-kw-${section.sectionId}-${section.keywordId ?? ''}-${lookups?.keywords?.length ?? 0}`}
                                  defaultValue={section.keywordId || ""}
                                  style={{ fontSize: "0.78rem", padding: "2px 4px", border: "1px solid transparent", background: "transparent", cursor: "pointer", textAlign: "center" }}
                                  onFocus={(e) => { e.target.style.border = "1px solid var(--brand-primary)"; e.target.style.background = "var(--surface-card)"; }}
                                  onBlur={(e) => { e.target.style.border = "1px solid transparent"; e.target.style.background = "transparent"; }}
                                  onChange={async (e) => {
                                    try {
                                      await QuickQuizService.updateSectionInfo(section.sectionId, { keywordId: Number(e.target.value) || null });
                                      loadQuiz();
                                    } catch { /* ignore */ }
                                  }}>
                                  <option value="">--</option>
                                  {lookups?.keywords.map((k) => (
                                    <option key={k.id} value={k.id}>{k.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="text-center">{section.isRankComputation ? "Yes" : ""}</td>
                              <td className="text-center"></td>
                              <td className="text-center">{section.sectionalTestTime || ""}</td>
                              <td className="text-center" style={{ whiteSpace: "nowrap" }}>
                                <button className="btn btn-sm" title={expandedSections.has(section.sectionId) ? "Hide Questions" : "Show Questions"}
                                  style={{ fontSize: "0.8rem", padding: "2px 6px", backgroundColor: expandedSections.has(section.sectionId) ? "#007bff" : "#fff", color: expandedSections.has(section.sectionId) ? "#fff" : "#007bff", border: "1px solid var(--brand-primary)", margin: "2px", borderRadius: "3px" }}
                                  onClick={() => toggleSection(section.sectionId)}>
                                  {expandedSections.has(section.sectionId) ? "▲" : "▼"}
                                </button>
                                <button className="btn btn-sm" title="Manage Questions"
                                  style={{ fontSize: "0.8rem", padding: "2px 6px", margin: "2px", backgroundColor: "var(--status-info)", color: "var(--text-on-brand)", border: "none", borderRadius: "3px" }}
                                  onClick={() => openQuestionManagement(section.sectionId)}>
                                  &#9881;
                                </button>
                              </td>
                            </tr>
                            {/* Inline questions when expanded */}
                            {expandedSections.has(section.sectionId) && section.questions.length > 0 && (
                              <tr>
                                <td colSpan={8} style={{ padding: 0 }}>
                                  <table className="table table-sm mb-0" style={{ fontSize: "0.78rem", backgroundColor: "var(--surface-card)" }}>
                                    <thead className="table-light">
                                      <tr>
                                        <th style={{ width: "50px", paddingLeft: "30px" }}>#</th>
                                        <th>Question</th>
                                        <th className="text-center" style={{ width: "70px" }}>Marks</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {section.questions.map((q) => (
                                        <tr key={q.mappingId}>
                                          <td style={{ paddingLeft: "30px" }}>Q{q.testQuestionNumber || q.questionNumber}</td>
                                          <td>
                                            <div dangerouslySetInnerHTML={{ __html: q.questionName || q.stemStatement || "-" }} />
                                          </td>
                                          <td className="text-center">{q.marks}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                            {expandedSections.has(section.sectionId) && section.questions.length === 0 && (
                              <tr>
                                <td colSpan={8} className="text-center text-muted" style={{ fontSize: "0.78rem", padding: "8px" }}>
                                  No questions in this section
                                </td>
                              </tr>
                            )}
                            {section.subSections.map((sub) => (
                              <tr key={sub.sectionId}>
                                <td style={{ paddingLeft: "40px" }}>{sub.name}</td>
                                <td className="text-center">{sub.totalMarks}</td>
                                <td className="text-center">0</td>
                                <td className="text-center">
                                  <select className="form-select form-select-sm"
                                    key={`sub-kw-${sub.sectionId}-${sub.keywordId ?? ''}-${lookups?.keywords?.length ?? 0}`}
                                    defaultValue={sub.keywordId || ""}
                                    style={{ fontSize: "0.78rem", padding: "2px 4px", border: "1px solid transparent", background: "transparent", cursor: "pointer", textAlign: "center" }}
                                    onFocus={(e) => { e.target.style.border = "1px solid var(--brand-primary)"; e.target.style.background = "var(--surface-card)"; }}
                                    onBlur={(e) => { e.target.style.border = "1px solid transparent"; e.target.style.background = "transparent"; }}
                                    onChange={async (e) => {
                                      try {
                                        await QuickQuizService.updateSectionInfo(sub.sectionId, { keywordId: Number(e.target.value) || null });
                                        loadQuiz();
                                      } catch { /* ignore */ }
                                    }}>
                                    <option value="">--</option>
                                    {lookups?.keywords.map((k) => (
                                      <option key={k.id} value={k.id}>{k.name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="text-center"></td>
                                <td className="text-center"></td>
                                <td className="text-center"></td>
                                <td className="text-center"></td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>

          {quiz.sections.length > 0 && (
            <div
              className="d-flex justify-content-between px-3 py-2 mt-1"
              style={{
                backgroundColor: "var(--surface-sunken)",
                borderRadius: "4px",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              <span>Total</span>
              <span>Marks: {totalMarks} | Questions: {totalQuestions}</span>
            </div>
          )}
        </div>
      </div>

      {/* Instructions & Messages (rich text) — full width, 2-column grid */}
      <Card className="mb-3">
        <Card.Body style={{ padding: "14px 16px" }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "var(--brand-primary)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
            Instructions &amp; Messages
          </div>
          <div className="row g-4">
            <div className="col-md-6">
              <RichInfoField label="Instructions" html={quiz.instructions} />
            </div>
            <div className="col-md-6">
              <RichInfoField label="Mobile Instructions" html={quiz.mobileInstructions} />
            </div>
            <div className="col-md-6">
              <RichInfoField label="Test Results Declaration Message" html={quiz.testDeclarationRemarks} />
            </div>
            <div className="col-md-6">
              <RichInfoField label="Student Announcement Remarks" html={quiz.studentAnnouncementRemarks} />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Publish Mappings — Two tables side by side like Java */}
      <div className="row mb-3">
        {/* Left: Center + Date */}
        <div className="col-md-5">
          <Card>
            <Card.Body style={{ padding: "8px" }}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>Center / Date</span>
                <Button size="sm" variant="outline-primary" onClick={() => { loadPublishLookups(); setSchoolForm({ schoolId: "", tagId: "", parentTagId: "", issueType: "school", testDate: "" }); setPublishMsg(null); setShowSchoolModal(true); }}
                  style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
                  + Add Centre
                </Button>
              </div>
              <div style={{ fontSize: "0.8rem", overflowX: "auto" }}>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr><th>Center</th><th>Date</th><th style={{ width: "40px" }}></th></tr>
                  </thead>
                  <tbody>
                    {quiz.schoolMappings && quiz.schoolMappings.length > 0 ? (
                      quiz.schoolMappings.map((sm, idx) => (
                        <tr key={sm.mappingId || idx}>
                          <td>{sm.schoolName || sm.tagName || "-"}{sm.tagId && !sm.schoolId ? " (Tag)" : ""}</td>
                          <td>
                            <input type="date" className="form-control form-control-sm"
                              defaultValue={sm.testDate ? new Date(sm.testDate).toISOString().split("T")[0] : ""}
                              style={{ fontSize: "0.78rem", padding: "2px 4px", border: "1px solid transparent", background: "transparent", cursor: "pointer" }}
                              onFocus={(e) => { e.target.style.border = "1px solid var(--brand-primary)"; e.target.style.background = "var(--surface-card)"; }}
                              onBlur={async (e) => {
                                e.target.style.border = "1px solid transparent"; e.target.style.background = "transparent";
                                const newDate = e.target.value;
                                if (newDate && newDate !== (sm.testDate ? new Date(sm.testDate).toISOString().split("T")[0] : "")) {
                                  try {
                                    await QuickQuizService.updateSchoolMappingDate(testId, sm.mappingId, newDate);
                                    loadQuiz();
                                  } catch { /* ignore */ }
                                }
                              }} />
                          </td>
                          <td className="text-center">
                            <a href="#del" onClick={(e) => { e.preventDefault(); handleRemoveSchool(sm.mappingId); }} title="Remove" style={{ color: "var(--status-danger)", fontSize: "0.85rem" }}>&#128465;</a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={3} className="text-center text-muted">No centers mapped</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Right: Course, Batch, Publish Status */}
        <div className="col-md-7">
          <Card>
            <Card.Body style={{ padding: "8px" }}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: "0.8rem", fontWeight: 600 }}>Course / Batch / Publish Status</span>
                <div className="d-flex gap-1">
                  <Button size="sm" variant="outline-primary" onClick={() => { loadPublishLookups(); setCourseForm({ courseId: "", courseIds: [], publishStatusId: "1", batchId: "", programId: "", termId: "", isMultiple: false }); setPublishMsg(null); setShowCourseModal(true); }}
                    style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
                    + Course
                  </Button>
                  <Button size="sm" variant="outline-primary" onClick={() => { loadPublishLookups(); setCourseForm({ courseId: "", courseIds: [], publishStatusId: "1", batchId: "", programId: "", termId: "", isMultiple: true }); setPublishMsg(null); setShowCourseModal(true); }}
                    style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
                    + Multi Course
                  </Button>
                  <Button size="sm" variant="outline-success" onClick={() => {
                    setStudentForm({ type: "enquiryId", ids: "" }); setPublishMsg(null); setShowStudentModal(true);
                    loadPublishLookups();
                    QuickQuizService.getTestStudents(testId).then((d) => setExistingStudents(d.students || [])).catch(() => {});
                  }}
                    style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
                    + Students
                  </Button>
                </div>
              </div>
              <div style={{ fontSize: "0.8rem", overflowX: "auto" }}>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      {quiz.courseMappings?.some((cm) => cm.programId || cm.termId) && <th>Program</th>}
                      {quiz.courseMappings?.some((cm) => cm.programId || cm.termId) && <th>Term</th>}
                      <th>Course</th>
                      <th>Batch</th>
                      <th>Publish Status</th>
                      <th style={{ width: "40px" }}></th>
                      <th style={{ width: "40px" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {quiz.courseMappings && quiz.courseMappings.length > 0 ? (
                      quiz.courseMappings.map((cm, idx) => (
                        <tr key={cm.mappingId || idx}>
                          {quiz.courseMappings?.some((c) => c.programId || c.termId) && (
                            <td>{cm.programName || <span style={{ color: "var(--text-faint)" }}>—</span>}</td>
                          )}
                          {quiz.courseMappings?.some((c) => c.programId || c.termId) && (
                            <td>{cm.termDisplayName || cm.termName || <span style={{ color: "var(--text-faint)" }}>—</span>}</td>
                          )}
                          <td>{cm.courseName || "-"}</td>
                          <td>{cm.batchName || "All"}</td>
                          <td>
                            {/* Controlled (`value`, not `defaultValue`): the option list
                                arrives from a separate, slower getPublishLookups call, so an
                                uncontrolled select mounts with zero options, loses its value,
                                and the browser then auto-selects the first option — making
                                every row read "Do Not Publish" regardless of its real status. */}
                            <select className="form-select form-select-sm"
                              value={cm.publishStatusId || 1}
                              disabled={!publishStatuses?.length}
                              style={{ fontSize: "0.78rem", padding: "2px 4px", border: "1px solid transparent", background: "transparent", cursor: "pointer", minWidth: "160px" }}
                              onFocus={(e) => { e.target.style.border = "1px solid var(--brand-primary)"; e.target.style.background = "var(--surface-card)"; }}
                              onBlur={(e) => { e.target.style.border = "1px solid transparent"; e.target.style.background = "transparent"; }}
                              onChange={async (e) => {
                                const newVal = Number(e.target.value);
                                const prevVal = cm.publishStatusId;
                                const setRowStatus = (val: number) => setQuiz((prev: any) => prev ? {
                                  ...prev,
                                  courseMappings: (prev.courseMappings || []).map((c: any) => (
                                    c.mappingId === cm.mappingId ? { ...c, publishStatusId: val } : c
                                  )),
                                } : prev);
                                setRowStatus(newVal); // optimistic — controlled select needs state to move
                                try {
                                  await QuickQuizService.updateCoursePublishStatus(testId, cm.mappingId, newVal);
                                  loadQuiz();
                                } catch {
                                  setRowStatus(prevVal); // save failed — put the row back
                                }
                              }}>
                              {publishStatuses?.map((ps: any) => (
                                <option key={ps.id} value={ps.id}>
                                  {ps.displayLabel || ps.name}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="text-center">
                            <a href="#del" onClick={(e) => { e.preventDefault(); handleRemoveCourse(cm.mappingId); }} title="Remove" style={{ color: "var(--status-danger)", fontSize: "0.85rem" }}>&#128465;</a>
                          </td>
                          <td className="text-center">
                            {cm.batchId ? (
                              <a href={`/testAnalysis/marks/test/${testId}/batch/${cm.batchId}`} target="_blank" rel="noopener noreferrer" title="Enter Marks" style={{ color: "var(--brand-primary)", fontSize: "0.85rem" }}>&#9998;</a>
                            ) : cm.courseId ? (
                              <a href="#pick-batch" onClick={(e) => { e.preventDefault(); loadPublishLookups(); setBatchPickerCourseId(cm.courseId!); setBatchPickerSelected(""); setShowBatchPickerModal(true); }} title="Enter Marks — pick batch" style={{ color: "var(--brand-primary)", fontSize: "0.85rem" }}>&#9998;</a>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan={7} className="text-center text-muted">No courses mapped</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Test Tag Mapping — restyled to match the section-card language used
          by the Edit Test Info modal: gradient bar in the header, soft card
          surfaces, polished select + button, indigo chips for mapped tags
          (Bootstrap Badge with bg="info" rendered as a flat teal pill that
          clashed with the indigo theme everywhere else). */}
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-default)",
          borderRadius: 12,
          marginBottom: 14,
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            borderBottom: "1px solid var(--brand-primary-subtle)",
            background: "linear-gradient(135deg, var(--surface-page) 0%, #faf5ff 100%)",
          }}
        >
          <span
            style={{
              width: 4,
              height: 16,
              borderRadius: 2,
              background: "var(--gradient-brand)",
              display: "inline-block",
            }}
          />
          <div
            style={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "#312e81",
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            Test Tag Mapping
          </div>
        </div>
        <div style={{ padding: "14px 16px" }}>
          {tagLookups ? (
            <>
              {tagLookups.tagTypes.map((tt) => {
                const typeTags = tagLookups.tags.filter((t) => t.typeId === tt.id);
                const mapped = tagLookups.mappedTags.filter((mt) => mt.typeId === tt.id);
                const canAdd = !!tagSelections[tt.id] && !tagSaving;
                return (
                  <div key={tt.id} style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <label
                        style={{
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          color: "var(--text-body)",
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          margin: 0,
                          width: 130,
                          flexShrink: 0,
                        }}
                      >
                        {tt.name}
                      </label>
                      <Form.Select
                        // Cap the select at a sensible width so it doesn't
                        // stretch across the entire card. 360px comfortably
                        // shows the longest tag names ("Mathematics", batch
                        // tag values, etc.) without dominating the row.
                        style={{ ...editSelectStyle, width: 360, maxWidth: "100%", flex: "0 1 360px" }}
                        value={tagSelections[tt.id] || ""}
                        onChange={(e) =>
                          setTagSelections((prev) => ({ ...prev, [tt.id]: e.target.value }))
                        }
                      >
                        <option value="">— None —</option>
                        {typeTags.map((tag) => (
                          <option key={tag.id} value={tag.id}>{tag.name}</option>
                        ))}
                      </Form.Select>
                      <button
                        type="button"
                        disabled={!canAdd}
                        onClick={() => handleTestTagAdd(tt.id)}
                        style={{
                          padding: "8px 14px",
                          border: "none",
                          background: canAdd
                            ? "var(--gradient-brand)"
                            : "#cbd5e1",
                          color: "var(--text-on-brand)",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          borderRadius: 8,
                          cursor: canAdd ? "pointer" : "not-allowed",
                          whiteSpace: "nowrap",
                          height: 38,
                          boxShadow: canAdd ? "0 2px 6px rgba(102, 126, 234, 0.25)" : "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        + Add Tag
                      </button>
                    </div>
                    {mapped.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 6,
                          marginTop: 8,
                          // Align with the start of the select column
                          // (label width 130px + 10px gap).
                          marginLeft: 140,
                        }}
                      >
                        {mapped.map((mt) => (
                          <span
                            key={mt.mappingId}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "3px 4px 3px 10px",
                              background: "linear-gradient(135deg, var(--brand-primary-subtle) 0%, var(--brand-primary-subtle) 100%)",
                              color: "var(--brand-primary-hover)",
                              border: "1px solid var(--brand-primary-subtle)",
                              borderRadius: 999,
                              fontSize: "0.74rem",
                              fontWeight: 600,
                            }}
                          >
                            {mt.tagName}
                            <button
                              type="button"
                              onClick={() => handleTestTagRemove(mt.mappingId)}
                              title="Remove"
                              style={{
                                width: 18,
                                height: 18,
                                borderRadius: "50%",
                                border: "none",
                                background: "transparent",
                                color: "var(--brand-primary-hover)",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: 0,
                                fontSize: 11,
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.background = "var(--brand-primary-subtle)";
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.background = "transparent";
                              }}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {tagLookups.tagTypes.length === 0 && (
                <div style={{ fontSize: "0.82rem", color: "var(--text-faint)", padding: 4 }}>
                  No tag types configured for tests.
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: 8 }}>
              <Spinner role="status" animation="border" size="sm" />
            </div>
          )}
        </div>
      </div>

      {/* Audit & Activity — consolidated card (Bundle Audit, Alerts Audit, Audit Trail) */}
      {(() => {
        let bundles: any[] = [];
        let alerts: any[] = [];
        try {
          const auditJson = quiz.auditJson ? JSON.parse(quiz.auditJson) : null;
          bundles = auditJson?.bundle || auditJson?.bundles || [];
          alerts = auditJson?.alerts || [];
        } catch { /* keep defaults */ }

        const trailCount = quiz.auditTrail?.length || 0;
        const totalEvents = bundles.length + alerts.length + trailCount;
        const latestBundle = bundles.length > 0 ? bundles[bundles.length - 1] : null;
        const latestAlert = alerts.length > 0 ? alerts[alerts.length - 1] : null;

        const actionClass = (action?: string) => {
          if (action === "SAVE") return "qva-trail-action is-save";
          if (action === "UPDATE") return "qva-trail-action is-update";
          if (action === "DELETE") return "qva-trail-action is-delete";
          return "qva-trail-action is-other";
        };

        return (
          <div className="qva-card">
            <div className="qva-header">
              <div className="qva-header-left">
                <span className="qva-header-icon">
                  <Activity size={16} strokeWidth={2.25} />
                </span>
                <div>
                  <span className="qva-header-eyebrow">Test #{quiz.testId}</span>
                  <div className="qva-header-title">Audit &amp; Activity</div>
                </div>
              </div>
              <span className="qva-header-count">
                {totalEvents} {totalEvents === 1 ? "event" : "events"}
              </span>
            </div>

            {/* Summary tiles */}
            <div className="qva-tiles">
              {/* Bundle Audit */}
              <div className="qva-tile">
                <span className="qva-tile-icon is-bundle">
                  <Package size={18} strokeWidth={2} />
                </span>
                <div className="qva-tile-body">
                  <div className="qva-tile-headrow">
                    <span className="qva-tile-title">Bundle Audit</span>
                    {bundles.length > 0 ? (
                      <span className="qva-tile-count">{bundles.length}</span>
                    ) : (
                      <span className="qva-tile-count is-empty">—</span>
                    )}
                  </div>
                  {latestBundle ? (
                    <div className="qva-tile-latest">
                      <span className="qva-tile-latest-by">
                        {latestBundle.createdBy || latestBundle.generatedBy || "-"}
                      </span>{" "}
                      <span className="qva-tile-latest-when">
                        · {latestBundle.createdOn || latestBundle.generatedOn || "-"}
                      </span>
                    </div>
                  ) : (
                    <div className="qva-tile-empty">No bundles generated yet</div>
                  )}
                  {bundles.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="qva-tile-more"
                        onClick={() => setShowBundleAudit(!showBundleAudit)}
                      >
                        {showBundleAudit ? (
                          <>Show less <ChevronUp size={12} /></>
                        ) : (
                          <>+{bundles.length - 1} more {bundles.length - 1 === 1 ? "entry" : "entries"} <ChevronDown size={12} /></>
                        )}
                      </button>
                      {showBundleAudit && (
                        <div className="qva-tile-history">
                          {bundles.slice(0, -1).reverse().map((b: any, i: number) => (
                            <div key={i} className="qva-tile-history-row">
                              <span className="qva-tile-history-by">
                                {b.createdBy || b.generatedBy || "-"}
                              </span>
                              <span>{b.createdOn || b.generatedOn || "-"}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Alerts Audit */}
              <div className="qva-tile">
                <span className="qva-tile-icon is-alerts">
                  <Bell size={18} strokeWidth={2} />
                </span>
                <div className="qva-tile-body">
                  <div className="qva-tile-headrow">
                    <span className="qva-tile-title">Alerts Audit</span>
                    {alerts.length > 0 ? (
                      <span className="qva-tile-count">{alerts.length}</span>
                    ) : (
                      <span className="qva-tile-count is-empty">—</span>
                    )}
                  </div>
                  {latestAlert ? (
                    <div className="qva-tile-latest">
                      <span className="qva-tile-latest-by">{latestAlert.sentBy || "-"}</span>{" "}
                      <span className="qva-tile-latest-when">· {latestAlert.sentOn || "-"}</span>
                      {latestAlert.templateName && (
                        <div style={{ color: "var(--text-muted)", fontSize: 11.5, marginTop: 2 }}>
                          {latestAlert.templateName}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="qva-tile-empty">No alerts sent yet</div>
                  )}
                  {alerts.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="qva-tile-more"
                        onClick={() => setShowAlertsAudit(!showAlertsAudit)}
                      >
                        {showAlertsAudit ? (
                          <>Show less <ChevronUp size={12} /></>
                        ) : (
                          <>+{alerts.length - 1} more {alerts.length - 1 === 1 ? "entry" : "entries"} <ChevronDown size={12} /></>
                        )}
                      </button>
                      {showAlertsAudit && (
                        <div className="qva-tile-history">
                          {alerts.slice(0, -1).reverse().map((a: any, i: number) => (
                            <div key={i} className="qva-tile-history-row">
                              <span className="qva-tile-history-by">{a.sentBy || "-"}</span>
                              <span>
                                {a.sentOn || "-"}
                                {a.templateName ? ` · ${a.templateName}` : ""}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Audit Trail timeline */}
            <div className="qva-trail">
              <div className="qva-trail-header">
                <span className="qva-trail-headline">
                  <span className="qva-trail-headline-dot" />
                  <History size={14} strokeWidth={2.25} />
                  Audit Trail
                </span>
                <span className="qva-trail-count">
                  {trailCount} {trailCount === 1 ? "entry" : "entries"}
                </span>
              </div>

              {trailCount > 0 ? (
                <>
                  <div className="qva-trail-list">
                    {(showAuditTrail ? quiz.auditTrail! : quiz.auditTrail!.slice(0, 5)).map((a) => (
                      <div key={a.id} className="qva-trail-row">
                        <span className="qva-trail-dot" />
                        <div className="qva-trail-main">
                          <span className={actionClass(a.action)}>{a.action || "EVENT"}</span>
                          <span className="qva-trail-label">{a.itemTypeName || "Test Event"}</span>
                          {a.remarks && (
                            <span className="qva-trail-remarks">— {a.remarks}</span>
                          )}
                        </div>
                        <div className="qva-trail-meta">
                          <div className="qva-trail-meta-by">
                            {a.userName || (a.userId ? `User #${a.userId}` : "System")}
                          </div>
                          <div className="qva-trail-meta-when">{formatDateTime(a.actedOn)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {trailCount > 5 && (
                    <div className="qva-trail-toggle">
                      <button
                        type="button"
                        onClick={() => setShowAuditTrail(!showAuditTrail)}
                      >
                        {showAuditTrail ? (
                          <>Show less <ChevronUp size={12} style={{ verticalAlign: "-2px" }} /></>
                        ) : (
                          <>Show all {trailCount} entries <ChevronDown size={12} style={{ verticalAlign: "-2px" }} /></>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="qva-empty">
                  No audit trail yet. Create, update or publish actions will appear here.
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ==================== MODALS ==================== */}

      {/* Edit Test Info Modal — grouped into labelled sections so the form
          reads as a sequence of decisions (basics → schedule → scoring →
          flags → years → remarks) rather than a dense 2-column grid of 18
          loose fields. All inputs share the same inline styling so they
          look uniform regardless of underlying type (text, select,
          datetime-local, etc.). */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
        <Modal.Header
          closeButton
          style={{
            background: "var(--gradient-brand)",
            color: "var(--text-on-brand)",
            borderBottom: "none",
            padding: "14px 20px",
          }}
          closeVariant="white"
        >
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: -0.2 }}>
            Edit Test Info
            <span style={{ opacity: 0.8, fontWeight: 500, marginLeft: 8 }}>· #{quiz.testId}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", background: "var(--surface-page)" }}>
          {editError && (
            <div style={{
              background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)",
              color: "var(--status-danger)", padding: "10px 14px", borderRadius: 10,
              fontSize: "0.85rem", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <span style={{ flexShrink: 0, marginTop: 1 }}>⚠</span>
              <span>{editError}</span>
            </div>
          )}

          {/* ── Basics ── */}
          <EditSection title="Basics">
            <EditField label="Test Name">
              <Form.Control
                style={editInputStyle}
                value={editForm.name || ""}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </EditField>
            <EditField label="Test Display Name">
              <Form.Control
                style={editInputStyle}
                value={editForm.displayName || ""}
                onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
              />
            </EditField>
            <EditField label="Test Date">
              <Form.Control
                type="date"
                style={editInputStyle}
                value={editForm.testDate || ""}
                onChange={(e) => setEditForm({ ...editForm, testDate: e.target.value })}
              />
            </EditField>
            <EditField label="Category">
              <Form.Select
                style={editSelectStyle}
                value={editForm.categoryId || ""}
                onChange={(e) => setEditForm({ ...editForm, categoryId: e.target.value })}
              >
                <option value="">— Select —</option>
                {lookups?.categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            </EditField>
            <EditField label="Keyword">
              <Form.Select
                style={editSelectStyle}
                value={editForm.keywordId || ""}
                onChange={(e) => setEditForm({ ...editForm, keywordId: e.target.value })}
              >
                <option value="">None</option>
                {lookups?.keywords.map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </Form.Select>
            </EditField>
            <EditField label="Test Type">
              <Form.Select
                style={editSelectStyle}
                value={editForm.testTypeId || ""}
                onChange={(e) => setEditForm({ ...editForm, testTypeId: e.target.value })}
              >
                <option value="">— Select —</option>
                {lookups?.testTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Form.Select>
            </EditField>
            <EditField label="Player">
              <Form.Select
                style={editSelectStyle}
                value={editForm.playerId || ""}
                onChange={(e) => setEditForm({ ...editForm, playerId: e.target.value })}
              >
                <option value="">— Select —</option>
                {lookups?.players.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </Form.Select>
            </EditField>
          </EditSection>

          {/* ── Schedule ── */}
          <EditSection title="Schedule">
            <EditField label="Start Date & Time">
              <Form.Control
                type="datetime-local"
                style={editInputStyle}
                value={editForm.startDate || ""}
                onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
              />
            </EditField>
            <EditField label="End Date & Time">
              <Form.Control
                type="datetime-local"
                style={editInputStyle}
                value={editForm.endDate || ""}
                onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
              />
            </EditField>
            <EditField label="Test CutOff Time">
              <Form.Control
                type="datetime-local"
                style={editInputStyle}
                value={editForm.testCutOffTime || ""}
                onChange={(e) => setEditForm({ ...editForm, testCutOffTime: e.target.value })}
              />
            </EditField>
            <EditField label="Test Time (mins)">
              <Form.Control
                type="number"
                style={editInputStyle}
                value={editForm.testTime || ""}
                onChange={(e) => setEditForm({ ...editForm, testTime: e.target.value })}
              />
            </EditField>
          </EditSection>

          {/* ── Scoring ── */}
          <EditSection title="Scoring">
            <EditField
              label="Total Marks"
              hint={`Must be ≥ section total (${sectionTotalSum}). Increase here to reserve buffer marks; decreasing below the section total is rejected.`}
            >
              {(() => {
                // Floor is the actual SUM of immediate child section marks
                // — the invariant the backend enforces. `totalMarks` (the
                // const at the top of the render) is the *displayed* test
                // total which may already include buffer above the sum, so
                // we use `sectionTotalSum` here.
                const sectionSum = sectionTotalSum;
                const entered = Number(editForm.totalMarks);
                const isInvalid =
                  editForm.totalMarks !== "" &&
                  editForm.totalMarks !== undefined &&
                  Number.isFinite(entered) &&
                  entered < sectionSum;
                return (
                  <>
                    <Form.Control
                      type="number"
                      style={{
                        ...editInputStyle,
                        ...(isInvalid
                          ? { borderColor: "var(--status-danger-border)", background: "var(--status-danger-bg)" }
                          : {}),
                      }}
                      min={sectionSum}
                      step={1}
                      value={editForm.totalMarks ?? ""}
                      onChange={(e) =>
                        setEditForm({ ...editForm, totalMarks: e.target.value })
                      }
                    />
                    {isInvalid && (
                      <div style={{ fontSize: "0.72rem", color: "var(--status-danger)", marginTop: 4 }}>
                        Total Marks ({entered}) is less than the sum of section
                        marks ({sectionSum}). Increase the total or reduce
                        per-section marks first.
                      </div>
                    )}
                  </>
                );
              })()}
            </EditField>
            <EditField label="Test Result Type">
              {/* Hard-coded enum, mirroring the create-quiz form
                  (QuizFormPanel.tsx). The column is a FK on test_master but
                  the app only recognises two values today: NULL (default) and
                  1 (Instant Result). If/when more result-type rows are
                  added, swap this for a lookup-driven Select. */}
              <Form.Select
                style={editSelectStyle}
                value={editForm.testResultTypeId || ""}
                onChange={(e) =>
                  setEditForm({ ...editForm, testResultTypeId: e.target.value })
                }
              >
                <option value="">— Default —</option>
                <option value="1">Instant Result</option>
              </Form.Select>
            </EditField>
          </EditSection>

          {/* ── Configuration ── */}
          <EditSection title="Configuration">
            <EditField label="Choice Sequence">
              <Form.Control
                style={editInputStyle}
                value={editForm.choiceSequence || ""}
                onChange={(e) => setEditForm({ ...editForm, choiceSequence: e.target.value })}
              />
            </EditField>
            <EditField label="Test Master Password">
              <Form.Control
                style={editInputStyle}
                value={editForm.password || ""}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
              />
            </EditField>
            <EditField label="Sort Order">
              <Form.Control
                type="number"
                style={editInputStyle}
                value={editForm.sortOrder || ""}
                onChange={(e) => setEditForm({ ...editForm, sortOrder: e.target.value })}
              />
            </EditField>
            {lookups?.supplementaryIndices && lookups.supplementaryIndices.length > 0 && (
              <EditField label="Supplementary Index">
                <Form.Select
                  style={editSelectStyle}
                  value={(editForm as any).supplementaryIndex || ""}
                  onChange={(e) => setEditForm({ ...editForm, supplementaryIndex: e.target.value ? Number(e.target.value) : null } as any)}
                >
                  <option value="">None</option>
                  {lookups.supplementaryIndices.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </Form.Select>
              </EditField>
            )}
          </EditSection>

          {/* ── Flags ── */}
          <EditSection title="Flags">
            <div className="col-12" style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <PillToggle
                checked={editForm.rankDefinition || false}
                onChange={(v) => setEditForm({ ...editForm, rankDefinition: v })}
                label="Rank Definition Required"
              />
              <PillToggle
                checked={!!(editForm as any).isBacklog}
                onChange={(v) => setEditForm({ ...editForm, isBacklog: v } as any)}
                label="Is Backlog Test"
              />
            </div>
          </EditSection>

          {/* ── Year(s) ── */}
          {lookups?.years && lookups.years.length > 0 && (
            <EditSection title="Year(s)">
              <div className="col-12">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {lookups.years.map((y) => {
                    const active = editYearIds.includes(y.id);
                    return (
                      <button
                        key={y.id}
                        type="button"
                        onClick={() => {
                          if (active) setEditYearIds(editYearIds.filter((id) => id !== y.id));
                          else setEditYearIds([...editYearIds, y.id]);
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "5px 12px",
                          borderRadius: 999,
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          border: active ? "1.5px solid #667eea" : "1px solid #e2e8f0",
                          background: active ? "linear-gradient(135deg, #eef2ff 0%, #ede9fe 100%)" : "#fff",
                          color: active ? "#4338ca" : "#475569",
                          cursor: "pointer",
                          transition: "all 120ms",
                          boxShadow: active ? "0 1px 3px rgba(99, 102, 241, 0.18)" : "none",
                        }}
                      >
                        {active && <span style={{ color: "var(--brand-primary)", lineHeight: 1 }}>✓</span>}
                        {y.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </EditSection>
          )}

          {/* ── Remarks ── */}
          <EditSection title="Remarks">
            <div className="col-12">
              <Form.Control
                as="textarea"
                rows={3}
                style={{
                  ...editInputStyle,
                  height: "auto",
                  minHeight: 72,
                  resize: "vertical",
                }}
                value={editForm.remarks || ""}
                onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
              />
            </div>
          </EditSection>

          {/* ── Instructions & Messages (rich text) ── */}
          <EditSection title="Instructions & Messages">
            <div className="col-md-6">
              <div style={editFieldLabelStyle}>Instructions</div>
              <CKEditor
                editor={ClassicEditor}
                data={editForm.instructions || ""}
                config={{ placeholder: "Enter web instructions shown to students..." }}
                onChange={(_e: any, editor: any) => setEditForm({ ...editForm, instructions: editor.getData() })}
              />
            </div>
            <div className="col-md-6">
              <div style={editFieldLabelStyle}>Mobile Instructions</div>
              <CKEditor
                editor={ClassicEditor}
                data={editForm.mobileInstructions || ""}
                config={{ placeholder: "Enter instructions shown on the mobile app..." }}
                onChange={(_e: any, editor: any) => setEditForm({ ...editForm, mobileInstructions: editor.getData() })}
              />
            </div>
            <div className="col-md-6">
              <div style={editFieldLabelStyle}>Test Results Declaration Message</div>
              <CKEditor
                editor={ClassicEditor}
                data={editForm.testDeclarationRemarks || ""}
                config={{ placeholder: "Message shown when results are declared..." }}
                onChange={(_e: any, editor: any) => setEditForm({ ...editForm, testDeclarationRemarks: editor.getData() })}
              />
            </div>
            <div className="col-md-6">
              <div style={editFieldLabelStyle}>Student Announcement Remarks</div>
              <CKEditor
                editor={ClassicEditor}
                data={editForm.studentAnnouncementRemarks || ""}
                config={{ placeholder: "Announcement remarks shown to students..." }}
                onChange={(_e: any, editor: any) => setEditForm({ ...editForm, studentAnnouncementRemarks: editor.getData() })}
              />
            </div>
          </EditSection>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid var(--border-default)", padding: "12px 20px", background: "var(--surface-card)" }}>
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowEditModal(false)}
            disabled={editSaving}
            style={{
              padding: "8px 18px",
              border: "1px solid var(--border-default)",
              background: "var(--surface-card)",
              color: "var(--text-body)",
              fontWeight: 600,
              borderRadius: 8,
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleEditSave}
            disabled={editSaving}
            style={{
              padding: "8px 22px",
              border: "none",
              background: editSaving
                ? "#cbd5e1"
                : "var(--gradient-brand)",
              color: "var(--text-on-brand)",
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: editSaving ? "none" : "0 2px 8px rgba(102, 126, 234, 0.3)",
            }}
          >
            {editSaving ? (
              <>
                <Spinner role="status" animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Finalize & Bundle Modal */}
      <Modal
        show={showBundleModal}
        onHide={closeBundleModal}
        centered
        backdrop={bundling ? "static" : true}
        keyboard={!bundling}
      >
        <Modal.Header closeButton={!bundling}>
          <Modal.Title style={{ fontSize: "1.1rem" }}>
            Finalize & Bundle — {quiz.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!bundleProgress && !bundleResult?.success && (
            <Form.Group className="mb-3">
              <Form.Label>Allow Test Download Before</Form.Label>
              <Form.Select
                value={downloadBefore}
                onChange={(e) => setDownloadBefore(Number(e.target.value))}
                disabled={bundling}
              >
                {DOWNLOAD_BEFORE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          {bundling && bundleProgress && (
            <div className="mb-3">
              <ProgressBar
                now={bundleProgress.progressPercentage}
                label={`${bundleProgress.progressPercentage}%`}
                animated
                striped
              />
              <small className="text-muted d-block mt-1">
                Generating PDFs... {bundleProgress.papersCompleted} of{" "}
                {bundleProgress.totalPapers} papers complete
              </small>
            </div>
          )}
          {bundling && !bundleProgress && (
            <div className="text-center py-3">
              <Spinner role="status" animation="border" size="sm" className="me-2" />
              <span>Processing... please wait</span>
            </div>
          )}
          {bundleResult && !bundling && (
            <div style={{ color: bundleResult.success ? "#28a745" : "#dc3545", fontWeight: 500 }}>
              {bundleResult.success ? "Bundle complete!" : `Error: ${bundleResult.message}`}
            </div>
          )}
          {bundleResult?.success && bundling && (
            <small className="text-muted d-block mt-1">{bundleResult.message}</small>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!bundling && !bundleResult?.success ? (
            <>
              <Button variant="secondary" onClick={closeBundleModal} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Cancel</Button>
              <Button variant="primary" onClick={handleFinalizeBundle} disabled={bundling} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
                Finalize & Bundle
              </Button>
            </>
          ) : !bundling && bundleResult?.success ? (
            <Button variant="primary" onClick={closeBundleModal} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>Close</Button>
          ) : null}
        </Modal.Footer>
      </Modal>

      {/* Edit Section Modal — same visual language as Edit Test Info above:
          gradient header, sectioned white cards on a soft-grey body,
          consistent input styling, pill toggle for the boolean flag,
          gradient primary action in the footer. */}
      <Modal
        show={showSectionEditModal}
        onHide={() => setShowSectionEditModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            background: "var(--gradient-brand)",
            color: "var(--text-on-brand)",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: -0.2 }}>
            Edit Section
            {sectionEditForm.name && (
              <span style={{ opacity: 0.8, fontWeight: 500, marginLeft: 8 }}>
                · {sectionEditForm.name}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", background: "var(--surface-page)" }}>
          {sectionEditError && (
            <div style={{
              background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)",
              color: "var(--status-danger)", padding: "10px 14px", borderRadius: 10,
              fontSize: "0.85rem", marginBottom: 14, display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <span style={{ flexShrink: 0, marginTop: 1 }}>⚠</span>
              <span>{sectionEditError}</span>
            </div>
          )}

          {/* ── Basics ── */}
          <EditSection title="Basics">
            <EditField label="Section Name">
              <Form.Control
                style={editInputStyle}
                value={sectionEditForm.name || ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, name: e.target.value })}
              />
            </EditField>
            <EditField label="Keyword">
              <Form.Select
                style={editSelectStyle}
                value={sectionEditForm.keywordId || ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, keywordId: e.target.value })}
              >
                <option value="">— Select —</option>
                {lookups?.keywords.map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </Form.Select>
            </EditField>
          </EditSection>

          {/* ── Scoring ── */}
          <EditSection title="Scoring">
            <EditField label="Section Marks">
              <Form.Control
                type="number"
                style={editInputStyle}
                value={sectionEditForm.totalMarks ?? ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, totalMarks: e.target.value })}
              />
            </EditField>
            <EditField label="Pass Mark (Threshold)">
              <Form.Control
                type="number"
                style={editInputStyle}
                value={sectionEditForm.passMark ?? ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, passMark: e.target.value })}
              />
            </EditField>
            <EditField label="Negative Marks">
              <Form.Control
                type="number"
                style={editInputStyle}
                value={sectionEditForm.negativeMarks || ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, negativeMarks: e.target.value })}
              />
            </EditField>
          </EditSection>

          {/* ── Schedule ── */}
          <EditSection title="Schedule">
            <EditField label="Exam Date">
              <Form.Control
                type="date"
                style={editInputStyle}
                value={sectionEditForm.testSectionDate || ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, testSectionDate: e.target.value })}
              />
            </EditField>
            <EditField label="Sectional Test Time (mins)">
              <Form.Control
                type="number"
                style={editInputStyle}
                value={sectionEditForm.sectionalTestTime || ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, sectionalTestTime: e.target.value })}
              />
            </EditField>
            <EditField label="Exam Start Time">
              <Form.Control
                type="time"
                style={editInputStyle}
                value={sectionEditForm.examStartTime || ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, examStartTime: e.target.value })}
              />
            </EditField>
            <EditField label="Exam End Time">
              <Form.Control
                type="time"
                style={editInputStyle}
                value={sectionEditForm.examEndTime || ""}
                onChange={(e) => setSectionEditForm({ ...sectionEditForm, examEndTime: e.target.value })}
              />
            </EditField>

            {/* ── Subject-wise schedule (optional) ──
                For language / elective sections where each subject sits on a
                different date (e.g. Hindi on 24-Jul, French on 25-Jul). Saved
                into SectionConfig.schedules. When empty, the single Exam
                Date / Start / End above applies to the whole section. */}
            <div className="col-12">
              <div style={{ borderTop: "1px dashed var(--border-default)", margin: "4px 0 12px" }} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div>
                  <div style={editFieldLabelStyle}>Subject-wise Exam Schedule (optional)</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--text-faint)", marginTop: 2, lineHeight: 1.4 }}>
                    Add a row per subject when languages/electives in this section are examined on different dates.
                    Leave empty to use the single date &amp; time above.
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() =>
                    setSectionEditForm({
                      ...sectionEditForm,
                      schedules: [
                        ...(sectionEditForm.schedules || []),
                        { subject: "", date: sectionEditForm.testSectionDate || "", startTime: sectionEditForm.examStartTime || "", endTime: sectionEditForm.examEndTime || "" },
                      ],
                    })
                  }
                  style={{
                    flexShrink: 0,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 14px",
                    border: "none",
                    background: "var(--gradient-brand)",
                    color: "var(--text-on-brand)",
                    fontWeight: 600,
                    borderRadius: 8,
                  }}
                >
                  <Plus size={15} /> Add Subject
                </Button>
              </div>

              {(sectionEditForm.schedules || []).length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {/* Column headers */}
                  <div style={{ display: "flex", gap: 8, padding: "0 2px", ...editFieldLabelStyle, marginBottom: 0 }}>
                    <span style={{ flex: "2 1 0" }}>Subject</span>
                    <span style={{ flex: "1.4 1 0" }}>Exam Date</span>
                    <span style={{ flex: "1.1 1 0" }}>Start</span>
                    <span style={{ flex: "1.1 1 0" }}>End</span>
                    <span style={{ width: 34 }} />
                  </div>
                  {(sectionEditForm.schedules || []).map((row: any, idx: number) => {
                    const updateRow = (patch: Record<string, string>) => {
                      const next = [...(sectionEditForm.schedules || [])];
                      next[idx] = { ...next[idx], ...patch };
                      setSectionEditForm({ ...sectionEditForm, schedules: next });
                    };
                    return (
                      <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <Form.Control
                          placeholder="e.g. Hindi"
                          style={{ ...editInputStyle, flex: "2 1 0" }}
                          value={row.subject || ""}
                          onChange={(e) => updateRow({ subject: e.target.value })}
                        />
                        <Form.Control
                          type="date"
                          style={{ ...editInputStyle, flex: "1.4 1 0" }}
                          value={row.date || ""}
                          onChange={(e) => updateRow({ date: e.target.value })}
                        />
                        <Form.Control
                          type="time"
                          style={{ ...editInputStyle, flex: "1.1 1 0" }}
                          value={row.startTime || ""}
                          onChange={(e) => updateRow({ startTime: e.target.value })}
                        />
                        <Form.Control
                          type="time"
                          style={{ ...editInputStyle, flex: "1.1 1 0" }}
                          value={row.endTime || ""}
                          onChange={(e) => updateRow({ endTime: e.target.value })}
                        />
                        <Button
                          size="sm"
                          variant="light"
                          title="Remove row"
                          onClick={() => {
                            const next = (sectionEditForm.schedules || []).filter((_: any, i: number) => i !== idx);
                            setSectionEditForm({ ...sectionEditForm, schedules: next });
                          }}
                          style={{
                            flexShrink: 0,
                            width: 34,
                            height: 38,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            border: "1px solid var(--status-danger-border)",
                            background: "var(--status-danger-bg)",
                            color: "var(--status-danger)",
                            borderRadius: 8,
                          }}
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </EditSection>

          {/* ── Flags ── */}
          <EditSection title="Flags">
            <div className="col-12" style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <PillToggle
                checked={sectionEditForm.isRankComputation || false}
                onChange={(v) =>
                  setSectionEditForm({ ...sectionEditForm, isRankComputation: v })
                }
                label="Rank Computation Required"
              />
            </div>
          </EditSection>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid var(--border-default)", padding: "12px 20px", background: "var(--surface-card)" }}>
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowSectionEditModal(false)}
            disabled={sectionEditSaving}
            style={{
              padding: "8px 18px",
              border: "1px solid var(--border-default)",
              background: "var(--surface-card)",
              color: "var(--text-body)",
              fontWeight: 600,
              borderRadius: 8,
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSectionEditSave}
            disabled={sectionEditSaving}
            style={{
              padding: "8px 22px",
              border: "none",
              background: sectionEditSaving
                ? "#cbd5e1"
                : "var(--gradient-brand)",
              color: "var(--text-on-brand)",
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: sectionEditSaving ? "none" : "0 2px 8px rgba(102, 126, 234, 0.3)",
            }}
          >
            {sectionEditSaving ? (
              <>
                <Spinner role="status" animation="border" size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send Alert Modal */}
      <Modal show={showAlertModal} onHide={() => !alertSending && setShowAlertModal(false)} centered>
        <Modal.Header closeButton={!alertSending} style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "12px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>Send Alert</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px" }}>
          {alertError && (
            <div style={{ background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", color: "var(--status-danger)", padding: "10px 14px", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "16px" }}>
              {alertError}
            </div>
          )}
          {alertSuccess && (
            <div style={{ background: "var(--status-success-bg)", border: "1px solid var(--status-success-border)", color: "var(--status-success)", padding: "10px 14px", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "16px" }}>
              {alertSuccess}
            </div>
          )}

          {/* Template */}
          <Form.Group className="mb-4">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-strong)", marginBottom: "6px" }}>Alert Template</Form.Label>
            <Form.Select value={alertForm.templateId} onChange={(e) => setAlertForm({ ...alertForm, templateId: e.target.value })} disabled={alertSending}>
              <option value="">--Select template--</option>
              {alertTemplates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {/* Alert Via */}
          <div style={{ background: "var(--surface-page)", borderRadius: "8px", padding: "14px 16px", marginBottom: "16px" }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--text-strong)", marginBottom: "10px" }}>Alert Via</div>
            <div className="d-flex" style={{ gap: "20px" }}>
              {[
                { key: "sms", label: "SMS" },
                { key: "email", label: "Email" },
                { key: "whatsapp", label: "WhatsApp" },
              ].map((item) => (
                <Form.Check key={item.key} type="checkbox" id={`alert-via-${item.key}`}
                  label={<span style={{ fontSize: "0.85rem" }}>{item.label}</span>}
                  checked={(alertForm as any)[item.key]}
                  onChange={(e) => setAlertForm({ ...alertForm, [item.key]: e.target.checked })}
                  disabled={alertSending}
                />
              ))}
            </div>
          </div>

          {/* Send To */}
          <div style={{ background: "var(--surface-page)", borderRadius: "8px", padding: "14px 16px", marginBottom: "16px" }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--text-strong)", marginBottom: "10px" }}>Send To</div>
            <div className="d-flex" style={{ gap: "20px" }}>
              {[
                { key: "student", label: "Student" },
                { key: "father", label: "Father" },
                { key: "mother", label: "Mother" },
                { key: "guardian", label: "Guardian" },
              ].map((item) => (
                <Form.Check key={item.key} type="checkbox" id={`alert-to-${item.key}`}
                  label={<span style={{ fontSize: "0.85rem" }}>{item.label}</span>}
                  checked={(alertForm as any)[item.key]}
                  onChange={(e) => setAlertForm({ ...alertForm, [item.key]: e.target.checked })}
                  disabled={alertSending}
                />
              ))}
            </div>
          </div>

          {/* Filter By Attendance */}
          <div style={{ background: "var(--surface-page)", borderRadius: "8px", padding: "14px 16px" }}>
            <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--text-strong)", marginBottom: "10px" }}>Filter By Attendance</div>
            <div className="d-flex" style={{ gap: "20px" }}>
              {[
                { key: "present", label: "Present" },
                { key: "absent", label: "Absent" },
              ].map((item) => (
                <Form.Check key={item.key} type="checkbox" id={`alert-att-${item.key}`}
                  label={<span style={{ fontSize: "0.85rem" }}>{item.label}</span>}
                  checked={(alertForm as any)[item.key]}
                  onChange={(e) => setAlertForm({ ...alertForm, [item.key]: e.target.checked })}
                  disabled={alertSending}
                />
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ padding: "12px 24px", borderTop: "1px solid var(--border-default)" }}>
          <button className="btn btn-sm" onClick={() => setShowAlertModal(false)} disabled={alertSending}
            style={{ fontSize: "0.8rem", padding: "6px 20px", backgroundColor: "var(--surface-card)", color: "var(--text-muted)", border: "1px solid #6c757d", borderRadius: "4px", margin: "2px" }}>
            Cancel
          </button>
          <button className="btn btn-sm" onClick={handleSendAlert} disabled={alertSending}
            style={{ fontSize: "0.8rem", padding: "6px 20px", backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", borderRadius: "4px", margin: "2px" }}>
            {alertSending ? "Sending..." : "Send Alert"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Add Centre Modal */}
      <Modal show={showSchoolModal} onHide={() => !publishSaving && setShowSchoolModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1.1rem" }}>Add Centre</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {publishMsg && (
            <div style={{ background: publishMsg.type === "error" ? "#fef2f2" : "#f0fdf4", border: `1px solid ${publishMsg.type === "error" ? "#fecaca" : "#bbf7d0"}`, color: publishMsg.type === "error" ? "#dc2626" : "#16a34a", padding: "8px 12px", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "12px" }}>
              {publishMsg.text}
            </div>
          )}
          <Form.Group className="mb-3">
            <div className="d-flex gap-3 mb-2">
              <Form.Check type="radio" label="Centre" name="issueType" checked={schoolForm.issueType === "school"} onChange={() => setSchoolForm({ ...schoolForm, issueType: "school", tagId: "" })} style={{ fontSize: "0.85rem" }} />
              <Form.Check type="radio" label="Centre Tag" name="issueType" checked={schoolForm.issueType === "tag"} onChange={() => setSchoolForm({ ...schoolForm, issueType: "tag", schoolId: "" })} style={{ fontSize: "0.85rem" }} />
            </div>
            {schoolForm.issueType === "school" ? (
              <>
                <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Centre</Form.Label>
                <Form.Select size="sm" value={schoolForm.schoolId} onChange={(e) => setSchoolForm({ ...schoolForm, schoolId: e.target.value })} disabled={publishSaving}>
                  <option value="">--Select Centre--</option>
                  {publishLookups?.schools?.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.name}{s.city ? ` (${s.city})` : ""}</option>
                  ))}
                </Form.Select>
              </>
            ) : (
              <>
                {/* Parent tag type dropdown (e.g. Country) */}
                {(() => {
                  const parentTypes = (publishLookups?.schoolTagTypes || []).filter((t: any) => !t.parentId);
                  const childTypes = (publishLookups?.schoolTagTypes || []).filter((t: any) => t.parentId);
                  const parentTags = (publishLookups?.schoolTags || []).filter((t: any) =>
                    parentTypes.some((pt: any) => pt.id === t.typeId) && !t.parentId
                  );
                  const selectedParentTag = schoolForm.parentTagId ? Number(schoolForm.parentTagId) : 0;
                  const childTags = selectedParentTag
                    ? (publishLookups?.schoolTags || []).filter((t: any) =>
                        childTypes.some((ct: any) => ct.id === t.typeId) && t.parentId === selectedParentTag
                      )
                    : [];

                  return (
                    <>
                      {parentTypes.length > 0 && (
                        <Form.Group className="mb-2">
                          <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>{parentTypes[0]?.name || "Parent Tag"}</Form.Label>
                          <Form.Select size="sm" value={schoolForm.parentTagId} onChange={(e) => setSchoolForm({ ...schoolForm, parentTagId: e.target.value, tagId: "" })} disabled={publishSaving}>
                            <option value="">--Select {parentTypes[0]?.name || "Tag"}--</option>
                            {parentTags.map((t: any) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}
                      {childTypes.length > 0 && selectedParentTag > 0 && (
                        <Form.Group className="mb-2">
                          <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>{childTypes[0]?.name || "Child Tag"}</Form.Label>
                          <Form.Select size="sm" value={schoolForm.tagId} onChange={(e) => setSchoolForm({ ...schoolForm, tagId: e.target.value })} disabled={publishSaving}>
                            <option value="">--Select {childTypes[0]?.name || "Tag"}--</option>
                            {childTags.map((t: any) => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      )}
                      {childTypes.length === 0 && parentTypes.length > 0 && (
                        <input type="hidden" />
                      )}
                    </>
                  );
                })()}
              </>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Test Date</Form.Label>
            <Form.Control type="date" size="sm" value={schoolForm.testDate} onChange={(e) => setSchoolForm({ ...schoolForm, testDate: e.target.value })} disabled={publishSaving} style={{ margin: "2px" }} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowSchoolModal(false)} disabled={publishSaving} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleIssueSchool} disabled={publishSaving || (schoolForm.issueType === "school" ? !schoolForm.schoolId : (!schoolForm.tagId && !schoolForm.parentTagId))} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
            {publishSaving ? <><Spinner role="status" animation="border" size="sm" className="me-1" />Adding...</> : "Add"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Course Modal — same visual language as the Edit Test Info
          modal: gradient header, sectioned white cards, soft-grey body,
          consistent input/select sizing via editInputStyle / editSelectStyle,
          gradient primary action in the footer. The cascade still works the
          same: Program → Term → Course → Batch → Publish Status. */}
      <Modal
        show={showCourseModal}
        onHide={() => !publishSaving && setShowCourseModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            background: "var(--gradient-brand)",
            color: "var(--text-on-brand)",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: -0.2 }}>
            {courseForm.isMultiple ? "Assign Multiple Courses" : "Assign Course"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", background: "var(--surface-page)" }}>
          {publishMsg && (
            <div
              style={{
                background: publishMsg.type === "error" ? "#fef2f2" : "#f0fdf4",
                border: `1px solid ${publishMsg.type === "error" ? "#fecaca" : "#bbf7d0"}`,
                color: publishMsg.type === "error" ? "#991b1b" : "#166534",
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: "0.85rem",
                marginBottom: 14,
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1 }}>
                {publishMsg.type === "error" ? "⚠" : "✓"}
              </span>
              <span>{publishMsg.text}</span>
            </div>
          )}

          {/* ── Program & Term ── (only when programs are configured) */}
          {publishLookups?.programs?.length > 0 && (
            <EditSection title="Program & Term">
              <EditField label="Program">
                <Form.Select
                  style={editSelectStyle}
                  value={courseForm.programId}
                  onChange={(e) =>
                    setCourseForm({
                      ...courseForm,
                      programId: e.target.value,
                      termId: "",
                      courseId: "",
                      courseIds: [],
                      batchId: "",
                    })
                  }
                  disabled={publishSaving}
                >
                  <option value="">— Select —</option>
                  {publishLookups?.programs?.map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Form.Select>
              </EditField>
              <EditField
                label={`Term${courseForm.programId ? " *" : ""}`}
                hint={
                  !courseForm.programId
                    ? "Pick a program first."
                    : !courseForm.termId
                      ? "Term is required when a program is selected."
                      : undefined
                }
              >
                <Form.Select
                  style={{
                    ...editSelectStyle,
                    ...(!!courseForm.programId && !courseForm.termId
                      ? { borderColor: "var(--status-danger-border)", background: "var(--status-danger-bg)" }
                      : {}),
                  }}
                  value={courseForm.termId}
                  onChange={(e) =>
                    setCourseForm({
                      ...courseForm,
                      termId: e.target.value,
                      courseId: "",
                      courseIds: [],
                      batchId: "",
                    })
                  }
                  disabled={publishSaving || !courseForm.programId}
                >
                  <option value="">— Select —</option>
                  {(publishLookups?.terms || [])
                    .filter(
                      (t: any) =>
                        !courseForm.programId ||
                        Number(t.programId) === Number(courseForm.programId),
                    )
                    .map((t: any) => (
                      <option key={t.id} value={t.id}>
                        {t.displayName || t.name}
                      </option>
                    ))}
                </Form.Select>
              </EditField>
            </EditSection>
          )}

          {/* ── Course ── */}
          <EditSection title="Course">
            <div className="col-12">
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "var(--text-body)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {courseForm.isMultiple ? "Courses (multi-select)" : "Course"}
                {courseForm.termId && (
                  <span
                    style={{
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      color: "var(--brand-primary)",
                      background: "var(--brand-primary-subtle)",
                      padding: "1px 8px",
                      borderRadius: 999,
                      letterSpacing: 0,
                      textTransform: "none",
                    }}
                  >
                    filtered by term
                  </span>
                )}
              </div>
              {(() => {
                // Build courseIds allowed for the current term (course_year_properties.TermId = termId)
                const allowedCourseIds = courseForm.termId
                  ? new Set<number>(
                      (publishLookups?.courseTermLinks || [])
                        .filter(
                          (l: any) => Number(l.termId) === Number(courseForm.termId),
                        )
                        .map((l: any) => Number(l.courseId)),
                    )
                  : null;
                const filteredCourses = allowedCourseIds
                  ? (publishLookups?.courses || []).filter((c: any) =>
                      allowedCourseIds.has(Number(c.id)),
                    )
                  : publishLookups?.courses || [];
                if (courseForm.isMultiple) {
                  return (
                    <Form.Select
                      multiple
                      style={{ ...editSelectStyle, height: 140, paddingRight: 12 }}
                      value={courseForm.courseIds}
                      onChange={(e) =>
                        setCourseForm({
                          ...courseForm,
                          courseIds: Array.from(
                            e.target.selectedOptions,
                            (o) => o.value,
                          ),
                        })
                      }
                      disabled={publishSaving}
                    >
                      {filteredCourses.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </Form.Select>
                  );
                }
                return (
                  <Form.Select
                    style={editSelectStyle}
                    value={courseForm.courseId}
                    onChange={(e) =>
                      setCourseForm({
                        ...courseForm,
                        courseId: e.target.value,
                        batchId: "",
                      })
                    }
                    disabled={publishSaving}
                  >
                    <option value="">— Select —</option>
                    {filteredCourses.map((c: any) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Form.Select>
                );
              })()}
              {courseForm.termId &&
                (publishLookups?.courseTermLinks || []).filter(
                  (l: any) => Number(l.termId) === Number(courseForm.termId),
                ).length === 0 && (
                  <div
                    style={{
                      fontSize: "0.72rem",
                      color: "var(--status-warning-text)",
                      background: "var(--status-warning-bg)",
                      border: "1px solid var(--status-warning-border)",
                      borderRadius: 6,
                      padding: "6px 10px",
                      marginTop: 8,
                    }}
                  >
                    No courses are mapped to this term.
                  </div>
                )}
              {courseForm.isMultiple && (
                <div style={{ fontSize: "0.72rem", color: "var(--text-faint)", marginTop: 4 }}>
                  Hold Ctrl / ⌘ to select multiple courses.
                </div>
              )}
            </div>
          </EditSection>

          {/* ── Batch & Publish ── */}
          <EditSection title="Batch & Publish">
            <EditField
              label="Batch"
              hint={
                courseForm.programId
                  ? `Filtered by program${
                      (publishLookups?.testYearIds?.length || 0) > 0
                        ? " & test year"
                        : ""
                    }${courseForm.isMultiple ? "; applied to every selected course" : ""}.`
                  : courseForm.isMultiple
                    ? "Applied to every selected course."
                    : undefined
              }
            >
              <Form.Select
                style={editSelectStyle}
                value={courseForm.batchId}
                onChange={(e) =>
                  setCourseForm({ ...courseForm, batchId: e.target.value })
                }
                disabled={publishSaving}
              >
                <option value="">All</option>
                {(() => {
                  // Java reference: batch_master WHERE programId=? AND yearId IN (test_year_mapping years)
                  const testYearIds: number[] =
                    publishLookups?.testYearIds || [];
                  const yearSet =
                    testYearIds.length > 0
                      ? new Set(testYearIds.map(Number))
                      : null;
                  const programId = courseForm.programId
                    ? Number(courseForm.programId)
                    : null;
                  // In multi mode there is no single courseId; fall back to union of selected courseIds
                  const selectedCourseIds: number[] = courseForm.isMultiple
                    ? (courseForm.courseIds || [])
                        .map(Number)
                        .filter((n) => !isNaN(n))
                    : courseForm.courseId
                      ? [Number(courseForm.courseId)]
                      : [];

                  // Build set of batchIds for the chosen course(s) (legacy course-based filter)
                  const courseBatchSet =
                    selectedCourseIds.length > 0
                      ? new Set<number>(
                          (publishLookups?.batchCourseLinks || [])
                            .filter((l: any) =>
                              selectedCourseIds.includes(Number(l.courseId)),
                            )
                            .map((l: any) => Number(l.batchId)),
                        )
                      : null;

                  return (publishLookups?.batches || [])
                    .filter((b: any) => {
                      if (programId) {
                        // Strict: batch must belong to the selected program
                        if (Number(b.programId) !== programId) return false;
                        // Strict: batch must belong to one of the test years (if test has year mappings)
                        if (yearSet && !yearSet.has(Number(b.yearId)))
                          return false;
                      } else if (courseBatchSet) {
                        // Legacy: filter by course mapping when program isn't chosen
                        if (!courseBatchSet.has(Number(b.id))) return false;
                      }
                      return true;
                    })
                    .map((b: any) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ));
                })()}
              </Form.Select>
            </EditField>
            <EditField label="Publish Status">
              <Form.Select
                style={editSelectStyle}
                value={courseForm.publishStatusId}
                onChange={(e) =>
                  setCourseForm({
                    ...courseForm,
                    publishStatusId: e.target.value,
                  })
                }
                disabled={publishSaving}
              >
                {publishLookups?.publishStatuses?.map((ps: any) => (
                  <option key={ps.id} value={ps.id}>
                    {ps.displayLabel || ps.name}
                  </option>
                ))}
              </Form.Select>
            </EditField>
          </EditSection>
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: "1px solid var(--border-default)",
            padding: "12px 20px",
            background: "var(--surface-card)",
          }}
        >
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowCourseModal(false)}
            disabled={publishSaving}
            style={{
              padding: "8px 18px",
              border: "1px solid var(--border-default)",
              background: "var(--surface-card)",
              color: "var(--text-body)",
              fontWeight: 600,
              borderRadius: 8,
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleIssueCourse}
            disabled={
              publishSaving ||
              (!courseForm.courseId && courseForm.courseIds.length === 0) ||
              (!!courseForm.programId && !courseForm.termId)
            }
            style={{
              padding: "8px 22px",
              border: "none",
              background:
                publishSaving ||
                (!courseForm.courseId && courseForm.courseIds.length === 0) ||
                (!!courseForm.programId && !courseForm.termId)
                  ? "#cbd5e1"
                  : "var(--gradient-brand)",
              color: "var(--text-on-brand)",
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: publishSaving
                ? "none"
                : "0 2px 8px rgba(102, 126, 234, 0.3)",
            }}
          >
            {publishSaving ? (
              <>
                <Spinner role="status" animation="border" size="sm" className="me-2" />
                Adding…
              </>
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Batch Picker Modal for Enter Marks */}
      <Modal show={showBatchPickerModal} onHide={() => setShowBatchPickerModal(false)} centered size="sm">
        <Modal.Header closeButton style={{ backgroundColor: "#333", color: "var(--text-on-brand)", padding: "8px 12px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Select Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Batch</Form.Label>
            <Form.Select size="sm" value={batchPickerSelected} onChange={(e) => setBatchPickerSelected(e.target.value)} style={{ margin: "2px" }}>
              <option value="">--Select--</option>
              {publishLookups?.batches?.filter((b: any) => b.courseId === batchPickerCourseId).map((b: any) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowBatchPickerModal(false)} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Cancel</Button>
          <Button variant="primary" size="sm" disabled={!batchPickerSelected}
            onClick={() => { setShowBatchPickerModal(false); window.open(`/testAnalysis/marks/test/${testId}/batch/${batchPickerSelected}`, "_blank"); }}
            style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
            Go
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mark Absentees Modal — pick one or more batches, then auto-mark every
          student in them who hasn't attempted the test as Absent. Mirrors the
          legacy Java testCreation "Mark Absentees" flow. */}
      <Modal show={showMarkAbsentModal} onHide={() => !markAbsentLoading && setShowMarkAbsentModal(false)} centered>
        <Modal.Header closeButton closeVariant="white" style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", padding: "12px 16px" }}>
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700 }}>Mark Absentees</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "16px 20px" }}>
          <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 8 }}>Select Batch</Form.Label>

          {markAbsentBatchesLoading ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-muted)", fontSize: "0.85rem", padding: "12px 0" }}>
              <Spinner animation="border" size="sm" /> Loading batches…
            </div>
          ) : markAbsentBatches.length > 0 ? (
            <>
              <div
                style={{
                  border: "1px solid var(--border-default)",
                  borderRadius: 8,
                  maxHeight: 280,
                  overflowY: "auto",
                  padding: "4px 0",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    borderBottom: "1px solid var(--border-default)",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    margin: 0,
                  }}
                >
                  <input type="checkbox" checked={allMarkAbsentSelected} onChange={toggleMarkAbsentSelectAll} />
                  Select All ({markAbsentBatches.length})
                </label>
                {markAbsentBatches.map((b) => (
                  <label
                    key={b.batchId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "7px 12px",
                      fontSize: "0.85rem",
                      cursor: "pointer",
                      margin: 0,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={markAbsentSelectedIds.includes(b.batchId)}
                      onChange={() => toggleMarkAbsentBatch(b.batchId)}
                    />
                    <span style={{ flex: 1 }}>{b.batchName}</span>
                    <span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>
                      {b.studentCount} student{b.studentCount === 1 ? "" : "s"}
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: 8 }}>
                Students who already attempted (or were already marked) are skipped automatically.
              </div>
            </>
          ) : (
            !markAbsentResult && (
              <div style={{ color: "var(--text-muted)", fontSize: "0.85rem", padding: "12px 0" }}>
                No batches are mapped to this test.
              </div>
            )
          )}

          {markAbsentResult && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 12px",
                borderRadius: 8,
                fontSize: "0.83rem",
                background: markAbsentResult.type === "error" ? "#fef2f2" : "#f0fdf4",
                border: `1px solid ${markAbsentResult.type === "error" ? "#fecaca" : "#bbf7d0"}`,
                color: markAbsentResult.type === "error" ? "#991b1b" : "#166534",
              }}
            >
              {markAbsentResult.text}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            disabled={markAbsentLoading}
            onClick={() => setShowMarkAbsentModal(false)}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}
          >
            Close
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={markAbsentLoading || markAbsentSelectedIds.length === 0}
            onClick={confirmMarkAbsentees}
            style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}
          >
            {markAbsentLoading ? "Marking…" : "Mark Absentees"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Students Modal — same gradient header / sectioned body
          treatment as Assign Course. The two ID modes (Enquiry Id vs
          Student Code) become pill toggles for consistency with the rest
          of the redesigned forms. */}
      <Modal
        show={showStudentModal}
        onHide={() => !publishSaving && setShowStudentModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            background: "var(--gradient-brand)",
            color: "var(--text-on-brand)",
            borderBottom: "none",
            padding: "14px 20px",
          }}
        >
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 700, letterSpacing: -0.2 }}>
            Add Students
            {quiz?.name && (
              <span style={{ opacity: 0.8, fontWeight: 500, marginLeft: 8 }}>
                · {quiz.name}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "20px 24px", background: "var(--surface-page)" }}>
          {publishMsg && (
            <div
              style={{
                background: publishMsg.type === "error" ? "#fef2f2" : "#f0fdf4",
                border: `1px solid ${publishMsg.type === "error" ? "#fecaca" : "#bbf7d0"}`,
                color: publishMsg.type === "error" ? "#991b1b" : "#166534",
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: "0.85rem",
                marginBottom: 14,
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 1 }}>
                {publishMsg.type === "error" ? "⚠" : "✓"}
              </span>
              <span>{publishMsg.text}</span>
            </div>
          )}

          {/* ── Publish status (test-level) ──
              Mirrors the legacy Java Add Students modal. Persists to
              test_master.PublishStatusId and fires an incremental S3
              re-bundle on every change. */}
          <EditSection title="Publish Status">
            <EditField label="Publish Status">
              <Form.Select
                style={editSelectStyle}
                value={quiz?.publishStatusId ?? ""}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  if (!Number.isNaN(v) && v !== quiz?.publishStatusId) {
                    handleUpdateTestPublishStatus(v);
                  }
                }}
                disabled={publishSaving || !publishLookups?.publishStatuses?.length}
              >
                {publishLookups?.publishStatuses?.map((ps: any) => (
                  <option key={ps.id} value={ps.id}>
                    {ps.displayLabel || ps.name}
                  </option>
                ))}
              </Form.Select>
            </EditField>
          </EditSection>

          {/* ── Add by ── */}
          <EditSection title="Add by">
            <div className="col-12" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <PillToggle
                checked={studentForm.type === "enquiryId"}
                onChange={() => setStudentForm({ ...studentForm, type: "enquiryId" })}
                label="Enquiry Id"
              />
              <PillToggle
                checked={studentForm.type === "studentCode"}
                onChange={() => setStudentForm({ ...studentForm, type: "studentCode" })}
                label="Student Code"
              />
            </div>
            <div className="col-12" style={{ marginTop: 10 }}>
              <div
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: "var(--text-body)",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  marginBottom: 4,
                }}
              >
                {studentForm.type === "enquiryId" ? "Enquiry Ids" : "Student Codes"}
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                value={studentForm.ids}
                onChange={(e) => setStudentForm({ ...studentForm, ids: e.target.value })}
                disabled={publishSaving}
                placeholder={
                  studentForm.type === "enquiryId"
                    ? "e.g. 1234, 1235, 1236"
                    : "e.g. STU001, STU002, STU003"
                }
                style={{
                  ...editInputStyle,
                  height: "auto",
                  minHeight: 96,
                  resize: "vertical",
                  fontFamily: "ui-monospace, monospace",
                }}
              />
              <div style={{ fontSize: "0.72rem", color: "var(--text-faint)", marginTop: 4 }}>
                Comma- or newline-separated{" "}
                {studentForm.type === "enquiryId" ? "enquiry ids" : "student codes"}.
              </div>
            </div>
            <div className="col-12" style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
              <button
                type="button"
                onClick={handleAddStudents}
                disabled={publishSaving || !studentForm.ids.trim()}
                style={{
                  padding: "8px 22px",
                  border: "none",
                  background:
                    publishSaving || !studentForm.ids.trim()
                      ? "#cbd5e1"
                      : "var(--gradient-brand)",
                  color: "var(--text-on-brand)",
                  fontWeight: 600,
                  borderRadius: 8,
                  cursor:
                    publishSaving || !studentForm.ids.trim() ? "not-allowed" : "pointer",
                  height: 38,
                  boxShadow:
                    publishSaving || !studentForm.ids.trim()
                      ? "none"
                      : "0 2px 8px rgba(102, 126, 234, 0.3)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {publishSaving ? (
                  <>
                    <Spinner role="status" animation="border" size="sm" />
                    Adding…
                  </>
                ) : (
                  "+ Add"
                )}
              </button>
            </div>
          </EditSection>

          {/* ── Already mapped ── */}
          {existingStudents.length > 0 && (
            <EditSection title={`Already mapped · ${existingStudents.length}`}>
              <div className="col-12">
                <div
                  style={{
                    maxHeight: 280,
                    overflowY: "auto",
                    border: "1px solid var(--border-default)",
                    borderRadius: 8,
                  }}
                >
                  <table
                    className="table table-sm mb-0"
                    style={{ fontSize: "0.8rem" }}
                  >
                    <thead
                      style={{
                        background: "var(--surface-page)",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      <tr>
                        <th style={{ width: 40, padding: "8px 12px", fontWeight: 700, color: "var(--text-body)" }}>#</th>
                        <th style={{ padding: "8px 12px", fontWeight: 700, color: "var(--text-body)" }}>Enquiry ID</th>
                        <th style={{ padding: "8px 12px", fontWeight: 700, color: "var(--text-body)" }}>Student Code</th>
                        <th style={{ padding: "8px 12px", fontWeight: 700, color: "var(--text-body)" }}>Name</th>
                        <th style={{ width: 44 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {existingStudents.map((s, idx) => (
                        <tr key={s.mappingId}>
                          <td style={{ padding: "8px 12px", color: "var(--text-faint)" }}>{idx + 1}</td>
                          <td style={{ padding: "8px 12px", fontFamily: "ui-monospace, monospace" }}>{s.enquiryId}</td>
                          <td style={{ padding: "8px 12px", fontFamily: "ui-monospace, monospace" }}>{s.studentCode || "—"}</td>
                          <td style={{ padding: "8px 12px", color: "var(--text-strong)", fontWeight: 500 }}>
                            {[s.firstName, s.lastName].filter(Boolean).join(" ") || "—"}
                          </td>
                          <td style={{ padding: "4px 8px", textAlign: "center" }}>
                            <button
                              type="button"
                              title="Remove"
                              onClick={() => {
                                QuickQuizService.removeStudent(testId, s.mappingId)
                                  .then(() => {
                                    setExistingStudents((prev) =>
                                      prev.filter((x) => x.mappingId !== s.mappingId),
                                    );
                                  })
                                  .catch(() => { });
                              }}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                border: "1px solid var(--status-danger-border)",
                                background: "var(--surface-card)",
                                color: "var(--status-danger)",
                                cursor: "pointer",
                                fontSize: 14,
                                lineHeight: 1,
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.background = "var(--status-danger-bg)";
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLButtonElement).style.background = "var(--surface-card)";
                              }}
                            >
                              🗑
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </EditSection>
          )}
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: "1px solid var(--border-default)",
            padding: "12px 20px",
            background: "var(--surface-card)",
          }}
        >
          <Button
            variant="light"
            size="sm"
            onClick={() => setShowStudentModal(false)}
            disabled={publishSaving}
            style={{
              padding: "8px 18px",
              border: "1px solid var(--border-default)",
              background: "var(--surface-card)",
              color: "var(--text-body)",
              fontWeight: 600,
              borderRadius: 8,
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Test Duration Modal */}
      <Modal show={showDurationModal} onHide={() => !durationSaving && setShowDurationModal(false)} centered>
        <Modal.Header closeButton={!durationSaving} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1.1rem" }}>Edit Test Duration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {durationError && (
            <div style={{ background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", color: "var(--status-danger)", padding: "8px 12px", borderRadius: "6px", fontSize: "0.85rem", marginBottom: "12px" }}>
              {durationError}
            </div>
          )}
          <div className="mb-3 d-flex align-items-center gap-3">
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 0, minWidth: "140px" }}>{quiz?.name}</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              value={durationForm.testTime}
              onChange={(e) => setDurationForm({ ...durationForm, testTime: e.target.value })}
              disabled={durationSaving}
              style={{ maxWidth: "120px", margin: "2px" }}
              placeholder="mins"
            />
          </div>
          {durationForm.sections.map((sec, idx) => (
            <div key={sec.sectionId} className="mb-2 d-flex align-items-center gap-3">
              <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, marginBottom: 0, minWidth: "140px" }}>{sec.name}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                value={sec.time}
                onChange={(e) => {
                  const updated = [...durationForm.sections];
                  updated[idx] = { ...sec, time: e.target.value };
                  setDurationForm({ ...durationForm, sections: updated });
                }}
                disabled={durationSaving}
                style={{ maxWidth: "120px", margin: "2px" }}
                placeholder="mins"
              />
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDurationModal(false)} disabled={durationSaving} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={handleDurationSave} disabled={durationSaving} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
            {durationSaving ? (<><Spinner role="status" animation="border" size="sm" className="me-1" />Updating...</>) : "Update"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Question Management Modal (Java: QuestionTagMapping.jsp) */}
      {showQMModal && <style>{`.qm-fullscreen-modal .modal-dialog { max-width: 100vw !important; width: 100vw !important; height: 100vh !important; margin: 0 !important; } .qm-fullscreen-modal .modal-content { height: 100vh !important; border: none !important; border-radius: 0 !important; }`}</style>}
      <Modal show={showQMModal} onHide={() => setShowQMModal(false)} className="qm-fullscreen-modal">
        <Modal.Header closeButton style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {qmData?.section?.Name || "Section"} - Question Management
          </Modal.Title>
          <div className="ms-auto d-flex gap-2">
            <button className="btn btn-sm" onClick={handleDeleteAllQuestions} style={{ fontSize: "0.75rem", backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "none", margin: "2px", padding: "4px 12px", borderRadius: "4px" }}>
              Delete Questions
            </button>
            {(() => {
              const { full, current, cap } = sectionCapacity();
              return (
                <button className="btn btn-sm" onClick={openAddQuestionsModal} disabled={full}
                  title={full ? `Section is full (${current}/${cap} marks). Increase the section's marks to add more.` : "Add Questions"}
                  style={{ fontSize: "0.75rem", backgroundColor: full ? "#e9ecef" : "#fff", color: full ? "#adb5bd" : "#333", border: "none", margin: "2px", padding: "4px 12px", borderRadius: "4px", cursor: full ? "not-allowed" : "pointer" }}>
                  Add Questions
                </button>
              );
            })()}
          </div>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.82rem", overflowY: "auto", padding: "12px" }}>
          {/* Import Question Tag Mapping (Java: preQuestionTagMapping) */}
          <div style={{ background: "var(--surface-page)", border: "1px solid var(--border-default)", borderRadius: "8px", padding: "12px 14px", marginBottom: "14px" }}>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: "10px" }}>
              <input
                id="qm-import-file"
                type="file"
                accept=".xlsx"
                style={{ display: "none" }}
                onChange={(e) => { setQmImportFile(e.target.files?.[0] || null); setQmImportMsg(null); }}
              />
              <label htmlFor="qm-import-file"
                className="btn btn-sm mb-0"
                style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", border: "none", margin: 0, padding: "6px 16px", borderRadius: "4px", fontWeight: 600, cursor: "pointer" }}>
                &#128193; Browse...
              </label>
              <span style={{
                flex: "1 1 220px", minWidth: 0, border: "1px solid var(--border-strong)", borderRadius: "4px",
                padding: "6px 12px", background: "var(--surface-card)", color: qmImportFile ? "#2d3748" : "#94a3b8",
                fontSize: "0.8rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {qmImportFile ? qmImportFile.name : "Excel File (.xlsx)"}
              </span>
              <Button size="sm" onClick={handleImportTagMapping} disabled={!qmImportFile || qmImporting}
                style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "none", margin: 0, padding: "6px 18px", borderRadius: "4px", fontWeight: 600 }}>
                {qmImporting ? <><Spinner role="status" animation="border" size="sm" className="me-1" />Importing...</> : "Import"}
              </Button>
            </div>
            <div className="mt-2" style={{ fontSize: "0.78rem" }}>
              <span style={{ fontWeight: 600, color: "var(--text-body)", marginRight: "6px" }}>Template</span>
              <a href="#tagMappingTemplate" onClick={(e) => { e.preventDefault(); handleDownloadTagMappingTemplate(); }}
                style={{ color: "#2196F3", textDecoration: "none", fontWeight: 500 }}>
                {qmTemplateDownloading ? "Downloading..." : "Question Tag Mapping Template.xlsx"}
              </a>
            </div>
            {qmImportMsg && (
              <div className={`alert alert-${qmImportMsg.type} py-2 mb-0 mt-2`} style={{ fontSize: "0.8rem", whiteSpace: "pre-line" }}>
                {qmImportMsg.text}
              </div>
            )}
          </div>
          {qmLoading ? (
            <div className="text-center py-4"><Spinner role="status" animation="border" /></div>
          ) : qmData?.questions?.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <style>{`
                .qm-question-table tbody tr { transition: background-color 0.12s ease; }
                .qm-question-table tbody tr:hover > td { background-color: #eef4ff; }
                .qm-question-table tbody tr:hover { box-shadow: inset 3px 0 0 0 #2563eb; }
              `}</style>
              <table className="table table-sm table-bordered mb-0 qm-question-table" style={{ fontSize: "0.8rem" }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "30px" }}>#</th>
                    <th style={{ minWidth: "420px", maxWidth: "560px" }}>Question &amp; Solution</th>
                    <th style={{ width: "70px" }}>Correct Answer</th>
                    {(qmData.tagTypes || []).map((tt: any) => (
                      <th key={tt.id} style={{ minWidth: "90px", fontSize: "0.75rem" }}>{tt.name}</th>
                    ))}
                    <th style={{ minWidth: "180px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {qmData.questions.map((q: any) => (
                    <tr key={q.mappingId}>
                      <td className="text-primary fw-bold" style={{ verticalAlign: "top" }}>{q.questionNumber}</td>
                      <td style={{ verticalAlign: "top", maxWidth: "560px" }}>
                        {/* Question */}
                        <div style={{ display: "flex", gap: "6px", alignItems: "baseline" }}>
                          <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--brand-primary)", flex: "0 0 auto", marginTop: "1px" }}>Question</span>
                          <div dangerouslySetInnerHTML={{ __html: q.questionName || q.stemStatement || "" }} style={{ fontSize: "0.78rem", wordBreak: "break-word", color: "var(--text-strong)" }} />
                        </div>
                        {/* Solution */}
                        {q.solution && (
                          <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "1px dashed var(--border-strong)", display: "flex", gap: "6px", alignItems: "baseline" }}>
                            <span style={{ fontSize: "0.62rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--status-success)", flex: "0 0 auto", marginTop: "1px" }}>Solution</span>
                            <div dangerouslySetInnerHTML={{ __html: q.solution }} style={{ fontSize: "0.75rem", wordBreak: "break-word", color: "var(--text-body)" }} />
                          </div>
                        )}
                      </td>
                      <td style={{ verticalAlign: "top", textAlign: "center" }}>
                        <span style={{ display: "inline-block", minWidth: "26px", padding: "2px 8px", borderRadius: "6px", fontWeight: 700, fontSize: "0.78rem", background: "var(--status-info-bg)", color: "var(--brand-primary-active)" }}>{q.correctAnswer}</span>
                      </td>
                      {(qmData.tagTypes || []).map((tt: any) => {
                        const tags = q.tagsByType?.[tt.id] || [];
                        return (
                          <td key={tt.id} style={{ fontSize: "0.72rem", verticalAlign: "top" }}>
                            {tags.map((t: any) => t.tagName).join(", ")}
                          </td>
                        );
                      })}
                      <td style={{ whiteSpace: "nowrap", verticalAlign: "top" }}>
                        <div className="d-flex flex-column" style={{ gap: "4px" }}>
                          <button className="btn btn-sm" onClick={() => window.open(`/question-bank?mode=view&id=${q.questionId}`, "_blank", "noopener,noreferrer")}
                            style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "none", borderRadius: "3px" }}
                            title="View Question">&#128065; View Question</button>
                          <button className="btn btn-sm" onClick={() => setEditQuestionId(q.questionId)}
                            style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "#6f42c1", color: "var(--text-on-brand)", border: "none", borderRadius: "3px" }}
                            title="Edit Question">&#9998; Edit Question</button>
                          <button className="btn btn-sm" onClick={() => { setReplaceForm({ mappingId: q.mappingId, questionId: q.questionId, index: q.questionNumber, newQuestionId: "" }); setShowReplaceModal(true); }}
                            style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--status-info)", color: "var(--text-on-brand)", border: "none", borderRadius: "3px" }}
                            title="Replace Question">&#8634; Replace</button>
                          <button className="btn btn-sm" onClick={() => openFullAddTagsModal(q.questionId)}
                            style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "none", borderRadius: "3px" }}
                            title="Add Tags">&#43; Add Tags</button>
                          <button className="btn btn-sm" onClick={() => openRemoveTags(q.questionId)}
                            style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--status-warning)", color: "var(--text-strong)", border: "none", borderRadius: "3px" }}
                            title="Remove Tags">&#8722; Remove Tags</button>
                          <button className="btn btn-sm" onClick={() => handleDeleteQuestion(q.mappingId)}
                            style={{ fontSize: "0.7rem", padding: "2px 8px", backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "none", borderRadius: "3px" }}
                            title="Delete Question">&#10005; Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted">No questions in this section.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowQMModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Question overlay — embeds /question-bank?mode=edit&id=X&embed=1 in
          an iframe so the existing question editor (with all its CKEditor /
          choices / tags logic) can be reused. We use a plain fullscreen overlay
          (NOT a Bootstrap Modal) so the inner page's own form Modal isn't
          stacked inside another Modal frame. We listen for postMessage from
          the iframe (qb-edit-saved / qb-edit-close) to dismiss the overlay. */}
      {editQuestionId !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            backgroundColor: "var(--surface-card)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              backgroundColor: "#6f42c1",
              color: "var(--text-on-brand)",
              padding: "10px 16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flex: "0 0 auto",
            }}
          >
            <span style={{ fontSize: "1rem", fontWeight: 600 }}>Edit Question #{editQuestionId}</span>
            <button
              onClick={() => setEditQuestionId(null)}
              aria-label="Close"
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-on-brand)",
                fontSize: "1.4rem",
                lineHeight: 1,
                cursor: "pointer",
                padding: "0 6px",
              }}
            >
              &times;
            </button>
          </div>
          <iframe
            key={editQuestionId}
            title={`Edit question ${editQuestionId}`}
            src={`/question-bank?mode=edit&id=${editQuestionId}&embed=1`}
            style={{ flex: "1 1 auto", width: "100%", border: "none", display: "block" }}
          />
        </div>
      )}

      {/* Replace Question Modal */}
      <Modal show={showReplaceModal} onHide={() => setShowReplaceModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Replace Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>Index</Form.Label>
            <Form.Control size="sm" value={replaceForm.index} disabled style={{ backgroundColor: "var(--surface-sunken)" }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>Original Question ID</Form.Label>
            <Form.Control size="sm" value={replaceForm.questionId} disabled style={{ backgroundColor: "var(--surface-sunken)" }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600 }}>Question ID to be replaced with</Form.Label>
            <Form.Control size="sm" type="number" placeholder="Question Id" value={replaceForm.newQuestionId} onChange={(e) => setReplaceForm({ ...replaceForm, newQuestionId: e.target.value })} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowReplaceModal(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleReplaceQuestion} disabled={!replaceForm.newQuestionId}>Replace</Button>
        </Modal.Footer>
      </Modal>

      {/* Full Add Tags Modal (like Java — all tag types with cascading dropdowns) */}
      <Modal show={showFullAddTagsModal} onHide={() => setShowFullAddTagsModal(false)} centered size="lg">
        <Modal.Header closeButton style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Add Tags</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem" }}>
          <div className="mb-2"><Badge bg="warning" text="dark">Tags</Badge></div>
          {fullTagTypes.map((tt) => {
            const levels = qTagSelections[tt.classId] || [];
            return (
              <div key={tt.classId} className="d-flex align-items-start mb-3 p-2" style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                <label style={{ fontWeight: 600, minWidth: "110px", paddingTop: "4px", fontSize: "0.82rem", color: "var(--text-body)" }}>{tt.displayName || tt.name}</label>
                <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
                  {levels.map((lvl: any, idx: number) => (
                    <Form.Select key={idx} size="sm" style={{ maxWidth: "180px", margin: "2px" }}
                      value={lvl.tagId || ""}
                      onChange={(e) => handleTagDropdownChange(tt.classId, idx, Number(e.target.value))}>
                      <option value="">{idx === 0 ? `#All#` : `--Select--`}</option>
                      {lvl.options.map((o: any) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </Form.Select>
                  ))}
                  <Button size="sm" onClick={() => handleAddTagForType(tt.classId)}
                    style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", padding: "2px 10px", fontSize: "0.85rem", fontWeight: 700 }}>+</Button>
                </div>
              </div>
            );
          })}
          {addedTagIds.length > 0 && (
            <div className="mt-3 p-2" style={{ backgroundColor: "#f0f8ff", borderRadius: "4px" }}>
              <label style={{ fontWeight: 600, fontSize: "0.82rem" }}>Tags to add:</label>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {addedTagIds.map((t, i) => (
                  <Badge key={i} bg="info" style={{ fontSize: "0.78rem", padding: "5px 8px" }}>
                    {t.tagName}
                    <a href="#rm" onClick={(e) => { e.preventDefault(); setAddedTagIds((p) => p.filter((_, j) => j !== i)); }}
                      style={{ color: "var(--text-on-brand)", marginLeft: "6px", fontWeight: 700 }}>&times;</a>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShowFullAddTagsModal(false)}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Close</Button>
          <Button size="sm" onClick={handleSaveAllTags} disabled={addedTagIds.length === 0}
            style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>Update changes</Button>
        </Modal.Footer>
      </Modal>

      {/* Add Questions Modal */}
      <Modal show={showAddQuestionsModal} onHide={() => setShowAddQuestionsModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Add Questions</Modal.Title>
        </Modal.Header>
        {(() => {
          const { capDefined, cap, current, remaining, full } = sectionCapacity();
          const enteredCount = addQForm.questionIds.split(",").map((s) => s.trim()).filter(Boolean).length;
          const perQ = Number(addQForm.marks) || 0;
          const projectedAdd = perQ * enteredCount;
          const overCap = capDefined && perQ > 0 && enteredCount > 0 && (current + projectedAdd > cap);
          return (
            <>
              <Modal.Body>
                {addQMsg && <div className={`alert alert-${addQMsg.type} py-2`} style={{ fontSize: "0.85rem" }}>{addQMsg.text}</div>}
                {capDefined && !full && (
                  <div className="py-2 mb-2" style={{ fontSize: "0.82rem", color: "var(--text-body)", background: "#f1f3f5", borderRadius: "6px", padding: "6px 10px" }}>
                    Section marks: <strong>{current}</strong> / {cap} used · <strong>{remaining}</strong> remaining
                  </div>
                )}
                {overCap && (
                  <div className="alert alert-danger py-2" style={{ fontSize: "0.85rem" }}>
                    Adding {enteredCount} question(s) × {perQ} = {projectedAdd} marks would total {current + projectedAdd}, exceeding the section's total marks ({cap}). Only {remaining} mark(s) remaining.
                  </div>
                )}
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Question IDs (comma separated)</Form.Label>
                  <Form.Control as="textarea" rows={3} size="sm" value={addQForm.questionIds}
                    onChange={(e) => setAddQForm({ ...addQForm, questionIds: e.target.value })}
                    placeholder="e.g. 1234, 5678, 9012" disabled={addQSaving || full} style={{ margin: "2px" }} />
                </Form.Group>
                <div className="d-flex gap-3">
                  <Form.Group className="flex-fill">
                    <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Marks</Form.Label>
                    <Form.Control size="sm" type="number" value={addQForm.marks}
                      onChange={(e) => setAddQForm({ ...addQForm, marks: e.target.value })}
                      placeholder="Marks per question" disabled={addQSaving || full} style={{ margin: "2px" }} />
                  </Form.Group>
                  <Form.Group className="flex-fill">
                    <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Negative Marks</Form.Label>
                    <Form.Control size="sm" type="number" value={addQForm.negativeMarks}
                      onChange={(e) => setAddQForm({ ...addQForm, negativeMarks: e.target.value })}
                      placeholder="0" disabled={addQSaving || full} style={{ margin: "2px" }} />
                  </Form.Group>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button size="sm" onClick={() => setShowAddQuestionsModal(false)} disabled={addQSaving}
                  style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Cancel</Button>
                <Button size="sm" onClick={handleAddQuestions}
                  disabled={addQSaving || !addQForm.questionIds.trim() || !addQForm.marks || full || overCap}
                  style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", margin: "2px" }}>
                  {addQSaving ? <><Spinner role="status" animation="border" size="sm" className="me-1" />Adding...</> : "Add"}
                </Button>
              </Modal.Footer>
            </>
          );
        })()}
      </Modal>

      {/* Remove Tags Modal */}
      <Modal show={showRemoveTagsModal} onHide={() => setShowRemoveTagsModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "#2196F3", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>Question Tags</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {removeTagsData?.tagTypes?.length > 0 ? (
            removeTagsData.tagTypes.map((tt: any) => (
              <div key={tt.id} className="mb-3">
                <label style={{ fontWeight: 600, fontSize: "0.85rem" }}>{tt.name}</label>
                <div className="d-flex flex-wrap gap-1 mt-1">
                  {tt.tags.map((t: any) => (
                    <Badge key={t.qtmId} bg="info" style={{ fontSize: "0.78rem", padding: "5px 8px" }}>
                      {t.name}
                      <a href="#remove" onClick={(e) => { e.preventDefault(); handleQMRemoveTag(t.qtmId); }}
                        style={{ color: "var(--text-on-brand)", marginLeft: "6px", textDecoration: "none", fontWeight: "bold" }} title="Remove">&#10005;</a>
                    </Badge>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted">No tags assigned to this question.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowRemoveTagsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Question Rules Modal */}
      <Modal show={showRulesModal} onHide={() => setShowRulesModal(false)} size="xl" dialogClassName="modal-dialog-scrollable">
        <Modal.Header closeButton style={{ backgroundColor: "var(--status-info)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "0.95rem" }}>
            {quiz?.name} — Question Rules
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rulesLoading ? (
            <div className="text-center py-3"><Spinner role="status" animation="border" size="sm" /> Loading rules...</div>
          ) : !rulesData ? (
            <p className="text-muted">Failed to load rules</p>
          ) : (
            <>
              {/* Papers view */}
              {showPapersView && rulesData?.papers?.length > 0 && (
                <div style={{ border: "2px solid var(--brand-primary)", borderRadius: "8px", padding: "16px", marginBottom: "16px", backgroundColor: "#e7f1ff" }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "12px" }}>
                    <h6 style={{ fontSize: "0.9rem", fontWeight: 700, margin: 0 }}>Test Papers</h6>
                    <Button size="sm" onClick={() => setShowPapersView(false)} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px", fontSize: "0.75rem", padding: "2px 8px" }}>Hide</Button>
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: "4px" }}>
                    {rulesData.papers.map((p: any, idx: number) => (
                      <Dropdown key={p.paperId} style={{ display: "inline-block", margin: "2px" }}>
                        <Dropdown.Toggle size="sm"
                          style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", fontSize: "0.8rem", padding: "5px 12px" }}>
                          SET {idx + 1}
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ fontSize: "0.8rem" }}>
                          <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/paper/${p.paperId}`, "_blank")}>Paper</Dropdown.Item>
                          <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/booklet/${p.paperId}`, "_blank")}>Booklet</Dropdown.Item>
                          <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/exam-attendance?paperId=${p.paperId}`, "_blank")}>Exam Attendance</Dropdown.Item>
                          <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/answer-key/${p.paperId}`, "_blank")}>Answer Key</Dropdown.Item>
                          <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/consolidated-sheet?paperId=${p.paperId}`, "_blank")}>Consolidated Sheet</Dropdown.Item>
                          <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/answer-sheet?paperId=${p.paperId}`, "_blank")}>Answer Sheet</Dropdown.Item>
                          <Dropdown.Item onClick={() => window.open(`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/supported-docs/cover-sheet?paperId=${p.paperId}`, "_blank")}>Cover Sheet</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    ))}
                  </div>
                </div>
              )}

              {/* Section tree with rules */}
              {rulesData.sections?.map((sec: any) => (
                <div key={sec.sectionId} style={{ border: "1px solid var(--border-default)", borderRadius: "8px", padding: "12px", marginBottom: "12px", backgroundColor: "var(--surface-page)" }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: "8px" }}>
                    <div>
                      <span style={{ backgroundColor: "var(--status-info)", color: "var(--text-on-brand)", padding: "2px 10px", borderRadius: "4px", fontSize: "0.8rem", fontWeight: 600, marginRight: "8px" }}>
                        {sec.name}
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 4px" }}>
                        Questions: <strong>{sec.totalQuestions || 0}</strong>
                      </span>
                      <span style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 4px" }}>
                        Marks: <strong>{sec.totalMarks || 0}</strong>
                      </span>
                    </div>
                    <Button size="sm"
                      disabled={rulesData?.papersExist}
                      onClick={() => setRuleForm({ sectionId: sec.sectionId, numberOfQuestion: "" as any, marksPerQuestion: "" as any, negativeMarks: "" as any, questionCategories: [], tags: [] })}
                      title={rulesData?.papersExist ? "Cannot add rules when test papers already exist" : "Add a new rule"}
                      style={{ backgroundColor: rulesData?.papersExist ? "#6c757d" : "#28a745", color: "var(--text-on-brand)", border: `1px solid ${rulesData?.papersExist ? "#6c757d" : "#28a745"}`, margin: "2px", fontSize: "0.75rem", padding: "3px 10px" }}>
                      + Add Rule
                    </Button>
                  </div>

                  {/* Existing rules for this section */}
                  {sec.rules?.length > 0 ? sec.rules.map((rule: any) => {
                    const parsed = (() => { try { return JSON.parse(rule.ruleJsonWithValue || rule.ruleJson || "{}"); } catch { return {}; } })();
                    return (
                      <div key={rule.ruleId} style={{ border: "1px solid var(--border-default)", borderRadius: "6px", padding: "10px", marginBottom: "8px", backgroundColor: "var(--surface-card)" }}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div style={{ fontSize: "0.8rem" }}>
                            <div style={{ marginBottom: "4px" }}>
                              <strong>Questions:</strong> <Badge bg="info" style={{ margin: "0 2px" }}>{parsed.numberOfQuestion || 0}</Badge>
                              <strong style={{ marginLeft: "8px" }}>Marks/Q:</strong> <Badge bg="info" style={{ margin: "0 2px" }}>{parsed.marksPerQuestion || 0}</Badge>
                              <strong style={{ marginLeft: "8px" }}>Negative:</strong> <Badge bg="info" style={{ margin: "0 2px" }}>{parsed.negativeMarks || 0}</Badge>
                            </div>
                            {parsed.filter?.questionCategories?.length > 0 && (
                              <div style={{ marginTop: "4px" }}>
                                <strong>Categories:</strong>{" "}
                                {parsed.filter.questionCategories.map((c: string, i: number) => (
                                  <Badge key={i} bg="primary" style={{ margin: "0 2px" }}>{c}</Badge>
                                ))}
                              </div>
                            )}
                            {parsed.filter?.tags?.length > 0 && (
                              <div style={{ marginTop: "4px" }}>
                                <strong>Tags:</strong>{" "}
                                {parsed.filter.tags.map((t: string, i: number) => (
                                  <Badge key={i} bg="secondary" style={{ margin: "0 2px" }}>{t}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                          <Button size="sm" onClick={() => handleDeleteRule(rule.ruleId)}
                            disabled={rulesData?.papersExist}
                            title={rulesData?.papersExist ? "Rule cannot be deleted as test papers are already generated" : "Delete this rule"}
                            style={{ backgroundColor: rulesData?.papersExist ? "#6c757d" : "#dc3545", color: "var(--text-on-brand)", border: `1px solid ${rulesData?.papersExist ? "#6c757d" : "#dc3545"}`, margin: "2px", fontSize: "0.7rem", padding: "2px 8px" }}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    );
                  }) : (
                    <p className="text-muted" style={{ fontSize: "0.8rem", marginBottom: "4px" }}>No rules defined for this section</p>
                  )}
                </div>
              ))}

              {/* Add/Edit Rule Form */}
              {ruleForm && (
                <div style={{ border: "2px solid var(--status-info)", borderRadius: "8px", padding: "16px", marginTop: "12px", backgroundColor: "#e8f4f8" }}>
                  <h6 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: "12px" }}>
                    {rulesData.sections?.find((s: any) => s.sectionId === ruleForm.sectionId)?.name || "Section"} — Question Rules
                  </h6>

                  <Badge bg="danger" style={{ margin: "2px 0 8px 0", fontSize: "0.75rem" }}>Filters</Badge>

                  {/* Number of Questions */}
                  <div className="row align-items-center mb-2">
                    <div className="col-4"><Form.Label style={{ fontSize: "0.8rem", fontWeight: 600, margin: "2px" }}>Number of Questions</Form.Label></div>
                    <div className="col-4"><Form.Control size="sm" type="number" min={0} value={ruleForm.numberOfQuestion} onChange={(e) => setRuleForm({ ...ruleForm, numberOfQuestion: Number(e.target.value) })} style={{ margin: "2px" }} /></div>
                  </div>

                  {/* Reusable tag selector component */}
                  <QuestionTagSelector
                    tagTypes={rulesData.tagTypes || []}
                    questionCategories={rulesData.questionCategories || []}
                    selectedCategories={ruleForm.questionCategories}
                    onCategoriesChange={(cats) => setRuleForm({ ...ruleForm, questionCategories: cats })}
                    tagSelections={ruleForm._tagSelections || {}}
                    onTagSelectionsChange={(sels) => setRuleForm({ ...ruleForm, _tagSelections: sels })}
                  />

                  <hr style={{ margin: "12px 0" }} />
                  <Badge bg="danger" style={{ margin: "2px 0 8px 0", fontSize: "0.75rem" }}>Marks</Badge>

                  {/* Marks per Correct Answer */}
                  <div className="row align-items-center mb-2">
                    <div className="col-4"><Form.Label style={{ fontSize: "0.8rem", fontWeight: 600, margin: "2px" }}>Marks per Correct Answer</Form.Label></div>
                    <div className="col-3"><Form.Control size="sm" type="number" min={0} step="0.5" value={ruleForm.marksPerQuestion} onChange={(e) => setRuleForm({ ...ruleForm, marksPerQuestion: Number(e.target.value) })} style={{ margin: "2px" }} /></div>
                  </div>

                  {/* Negative Marks per Wrong Answer */}
                  <div className="row align-items-center mb-2">
                    <div className="col-4"><Form.Label style={{ fontSize: "0.8rem", fontWeight: 600, margin: "2px" }}>Negative Marks per Wrong Answer</Form.Label></div>
                    <div className="col-3"><Form.Control size="sm" type="number" min={0} step="0.5" value={ruleForm.negativeMarks} onChange={(e) => setRuleForm({ ...ruleForm, negativeMarks: Number(e.target.value) })} style={{ margin: "2px" }} /></div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <Button size="sm" onClick={() => setRuleForm(null)} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px", fontSize: "0.8rem", padding: "4px 12px" }}>Close</Button>
                    <Button size="sm" onClick={handleSaveRule} disabled={ruleSaving || ruleForm.numberOfQuestion <= 0}
                      style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px", fontSize: "0.8rem", padding: "4px 12px" }}>
                      {ruleSaving ? "Saving..." : "Create Rule"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {rulesData?.papersExist ? (
            <>
              <Button size="sm" onClick={() => { setNumPapers(1); setShowMakePapersModal(true); }}
                style={{ backgroundColor: "#e67e22", color: "var(--text-on-brand)", border: "1px solid #e67e22", margin: "2px", fontSize: "0.8rem", fontWeight: 600 }}>
                Test papers already exist. Do you want to add more?
              </Button>
              <Button size="sm" onClick={() => setShowPapersView(true)} style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)", margin: "2px" }}>
                Show Papers ({rulesData.papers?.length || 0})
              </Button>
              <Button size="sm" onClick={handleDeletePapers} style={{ backgroundColor: "var(--status-danger)", color: "var(--text-on-brand)", border: "1px solid var(--status-danger)", margin: "2px" }}>
                Delete Papers
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={() => { setNumPapers(1); setShowMakePapersModal(true); }}
              style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}
              disabled={!rulesData?.sections?.some((s: any) => s.rules?.length > 0)}>
              Make Test Papers
            </Button>
          )}
          <Button size="sm" onClick={() => setShowRulesModal(false)} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Make Test Papers Modal */}
      <Modal show={showMakePapersModal} onHide={() => setShowMakePapersModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "0.95rem" }}>Test Paper Maker</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center gap-3" style={{ margin: "8px 0" }}>
            <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600, whiteSpace: "nowrap", margin: "2px" }}>Number Of Test Papers</Form.Label>
            <Form.Control type="number" min={1} max={50} value={numPapers || ""} onChange={(e) => setNumPapers(e.target.value === "" ? 0 : Number(e.target.value))} style={{ maxWidth: "120px", margin: "2px" }} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button size="sm" onClick={() => setShowMakePapersModal(false)} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Close</Button>
          <Button size="sm" onClick={handleMakeTestPapers} disabled={makingPapers || numPapers <= 0}
            style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
            {makingPapers ? "Generating..." : "Make Test Papers"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sets Modal (Java: TestSetsNew.jsp + TestAttachedQuestions.jsp) */}
      <Modal show={showSetsModal} onHide={() => setShowSetsModal(false)} size="xl" centered dialogClassName="quiz-sets-modal-xl">
        <Modal.Header closeButton style={{ backgroundColor: "#343a40", color: "var(--text-on-brand)", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: "1rem" }}>
            {setViewMode === "list" ? "Question Paper Sets" : setViewMode === "view" ? `View Set: ${selectedSet?.set?.name}` : setViewMode === "edit" ? `Edit Set: ${selectedSet?.set?.name}` : "Create New Set"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: "0.85rem", maxHeight: "80vh", overflowY: "auto" }}>
          {setsLoading ? (
            <div className="text-center py-4"><Spinner role="status" animation="border" /></div>
          ) : setViewMode === "list" ? (
            <>
              {/* Hero Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, var(--brand-primary-subtle) 0%, var(--brand-primary-subtle) 100%)",
                  borderRadius: "12px",
                  padding: "20px 24px",
                  marginBottom: "20px",
                  border: "1px solid var(--brand-primary-subtle)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#1e1b4b", letterSpacing: "-0.01em" }}>
                    Question Paper Sets
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "2px" }}>
                    Generate, review and manage randomized sets for this test.
                  </div>
                </div>
                <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                  <div className="qs-stat-card" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--brand-primary-hover)", lineHeight: 1 }}>
                      {testSets.length}
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "2px" }}>
                      Total Sets
                    </div>
                  </div>
                  <div style={{ width: "1px", height: "36px", background: "var(--brand-primary-subtle)" }} />
                  <div className="qs-stat-card" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--status-success)", lineHeight: 1 }}>
                      {testSets.filter((s) => !s.consumed).length}
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "2px" }}>
                      Available
                    </div>
                  </div>
                  <div style={{ width: "1px", height: "36px", background: "var(--brand-primary-subtle)" }} />
                  <div className="qs-stat-card" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--status-danger)", lineHeight: 1 }}>
                      {testSets.filter((s) => s.consumed).length}
                    </div>
                    <div style={{ fontSize: "0.7rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "2px" }}>
                      Consumed
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={startCreateSet}
                    className="qs-add-btn"
                    style={{ fontSize: "0.8rem", fontWeight: 600, padding: "8px 18px", color: "var(--text-on-brand)", borderRadius: "8px", marginLeft: "8px" }}
                  >
                    <span style={{ marginRight: "6px", fontSize: "1rem", fontWeight: 700 }}>+</span>
                    Add New Set
                  </Button>
                </div>
              </div>

              {/* Empty state */}
              {testSets.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "60px 20px",
                    border: "2px dashed var(--border-strong)",
                    borderRadius: "12px",
                    backgroundColor: "var(--surface-card)",
                    color: "var(--text-muted)",
                  }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, var(--brand-primary-subtle) 0%, var(--brand-primary-subtle) 100%)",
                      margin: "0 auto 16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.75rem",
                    }}
                  >
                    &#128209;
                  </div>
                  <div style={{ fontWeight: 700, fontSize: "1rem", color: "var(--text-strong)", marginBottom: "6px" }}>No sets created yet</div>
                  <div style={{ fontSize: "0.85rem", marginBottom: "20px" }}>
                    Click the button below to generate your first question paper set.
                  </div>
                  <Button
                    size="sm"
                    onClick={startCreateSet}
                    className="qs-add-btn"
                    style={{ fontSize: "0.85rem", fontWeight: 600, padding: "10px 22px", color: "var(--text-on-brand)", borderRadius: "8px" }}
                  >
                    <span style={{ marginRight: "6px", fontSize: "1.1rem", fontWeight: 700 }}>+</span>
                    Create First Set
                  </Button>
                </div>
              ) : (
                /* Card Grid */
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                    gap: "14px",
                  }}
                >
                  {testSets.map((s, idx) => (
                    <div
                      key={s.id}
                      className="qs-set-card"
                      style={{
                        background: "var(--surface-card)",
                        borderRadius: "12px",
                        border: "1px solid var(--border-default)",
                        borderLeft: `4px solid ${s.consumed ? "#dc2626" : "#16a34a"}`,
                        padding: "18px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                        position: "relative",
                      }}
                    >

                      {/* Top row: S.No badge + Status */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: "var(--gradient-brand)",
                              color: "var(--text-on-brand)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.85rem",
                              fontWeight: 700,
                            }}
                          >
                            {idx + 1}
                          </div>
                          <span
                            style={{
                              padding: "3px 8px",
                              fontSize: "0.7rem",
                              fontFamily: "monospace",
                              backgroundColor: "var(--surface-sunken)",
                              color: "var(--text-body)",
                              borderRadius: "4px",
                              fontWeight: 600,
                            }}
                          >
                            ID #{s.id}
                          </span>
                        </div>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 10px",
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            borderRadius: "12px",
                            backgroundColor: s.consumed ? "#fee2e2" : "#dcfce7",
                            color: s.consumed ? "#991b1b" : "#166534",
                            border: `1px solid ${s.consumed ? "#fecaca" : "#bbf7d0"}`,
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: s.consumed ? "#dc2626" : "#16a34a",
                              boxShadow: `0 0 0 3px ${s.consumed ? "rgba(220,38,38,0.15)" : "rgba(22,163,74,0.15)"}`,
                            }}
                          />
                          {s.consumed ? "Consumed" : "Available"}
                        </span>
                      </div>

                      {/* Name */}
                      <div
                        style={{
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          color: "var(--text-strong)",
                          marginBottom: "16px",
                          lineHeight: 1.4,
                          minHeight: "40px",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                        title={s.name}
                      >
                        {s.name}
                      </div>

                      {/* Actions */}
                      <div style={{ display: "flex", gap: "6px", paddingTop: "12px", borderTop: "1px solid var(--border-subtle)", flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={() => viewSetQuestions(s.id, "view")}
                          title="View set questions"
                          className="qs-action-btn"
                          style={{
                            flex: "1 1 70px",
                            border: "1px solid var(--status-info-border)",
                            backgroundColor: "var(--status-info-bg)",
                            color: "var(--brand-primary-hover)",
                            padding: "7px 12px",
                            borderRadius: "8px",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "5px",
                          }}
                        >
                          <span>&#128269;</span> View
                        </button>

                        {/* Download dropdown (Java parity: Show Questions / QP / QP+Solutions / Solutions) */}
                        <Dropdown as="div" style={{ flex: "1 1 110px" }}>
                          <Dropdown.Toggle
                            as="button"
                            type="button"
                            title="Download options"
                            className="qs-action-btn"
                            style={{
                              width: "100%",
                              border: "1px solid var(--brand-primary-subtle)",
                              backgroundColor: "var(--brand-primary-subtle)",
                              color: "var(--brand-primary-hover)",
                              padding: "7px 12px",
                              borderRadius: "8px",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "5px",
                            }}
                          >
                            <span>&#128229;</span> Download
                          </Dropdown.Toggle>
                          <Dropdown.Menu
                            renderOnMount
                            popperConfig={{ strategy: "fixed" }}
                            style={{ fontSize: "0.8rem", padding: "6px", minWidth: "240px", borderRadius: "10px", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.15)", zIndex: 2000 }}
                          >
                            <Dropdown.Item
                              href={`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/sets/${s.id}/show-questions?type=0`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ borderRadius: "6px", padding: "7px 10px", display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              <span style={{ fontSize: "1rem" }}>&#128196;</span>
                              <span>Show Questions</span>
                            </Dropdown.Item>
                            <Dropdown.Item
                              href={`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/sets/${s.id}/document/download?type=0`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ borderRadius: "6px", padding: "7px 10px", display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              <span style={{ fontSize: "1rem" }}>&#128221;</span>
                              <span>Question Paper</span>
                            </Dropdown.Item>
                            <Dropdown.Item
                              href={`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/sets/${s.id}/document/download?type=1`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ borderRadius: "6px", padding: "7px 10px", display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              <span style={{ fontSize: "1rem" }}>&#128218;</span>
                              <span>Question Paper With Solutions</span>
                            </Dropdown.Item>
                            <Dropdown.Item
                              href={`${Config.nodeSiteUrl}v1/quick-quiz/${testId}/sets/${s.id}/document/download?type=2`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ borderRadius: "6px", padding: "7px 10px", display: "flex", alignItems: "center", gap: "8px" }}
                            >
                              <span style={{ fontSize: "1rem" }}>&#128172;</span>
                              <span>Solutions Only</span>
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>

                        {!s.consumed && (
                          <>
                            <button
                              type="button"
                              onClick={() => viewSetQuestions(s.id, "edit")}
                              title="Edit set"
                              className="qs-action-btn"
                              style={{
                                flex: "1 1 70px",
                                border: "1px solid var(--status-warning-border)",
                                backgroundColor: "var(--status-warning-bg)",
                                color: "var(--status-warning-text)",
                                padding: "7px 12px",
                                borderRadius: "8px",
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "5px",
                              }}
                            >
                              <span>&#9998;</span> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => { if (window.confirm("Are you sure you want to delete this set?")) handleDeleteSet(s.id); }}
                              title="Delete set"
                              className="qs-action-btn"
                              style={{
                                border: "1px solid var(--status-danger-border)",
                                backgroundColor: "var(--status-danger-bg)",
                                color: "var(--status-danger)",
                                padding: "7px 10px",
                                borderRadius: "8px",
                                fontSize: "0.78rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span>&#128465;</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3" style={{ gap: "10px", flexWrap: "wrap" }}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setSetViewMode("list")}
                  style={{ fontSize: "0.8rem", fontWeight: 600, padding: "6px 14px", borderRadius: "8px" }}
                >
                  &larr; Back to Sets
                </Button>
                {(setViewMode === "edit" || setViewMode === "create") && (
                  <Button
                    size="sm"
                    onClick={shuffleAllSections}
                    title="Randomize the order of every question across all sections"
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      padding: "7px 16px",
                      borderRadius: "8px",
                      background: "var(--gradient-brand)",
                      border: "none",
                      color: "var(--text-on-brand)",
                      boxShadow: "0 4px 12px rgba(99,102,241,0.35)",
                    }}
                  >
                    <span style={{ marginRight: "6px" }}>&#127922;</span>
                    Shuffle All Questions
                  </Button>
                )}
              </div>

              {(setViewMode === "edit" || setViewMode === "create") && (
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600, fontSize: "0.85rem" }}>Set Name</Form.Label>
                  <Form.Control size="sm" value={setFormName} onChange={(e) => setSetFormName(e.target.value)} placeholder="Enter set name" />
                </Form.Group>
              )}

              {setFormSections.map((sec, sIdx) => (
                <div key={sec.sectionId} className="mb-3" style={{ background: "var(--surface-card)", borderRadius: "10px", border: "1px solid var(--border-default)", overflow: "hidden" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 14px",
                      background: "linear-gradient(135deg, var(--surface-page) 0%, var(--surface-sunken) 100%)",
                      borderBottom: "1px solid var(--border-default)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "6px",
                          background: "var(--gradient-brand)",
                          color: "var(--text-on-brand)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                        }}
                      >
                        {sIdx + 1}
                      </span>
                      <strong style={{ fontSize: "0.9rem", color: "var(--text-strong)" }}>{sec.sectionName}</strong>
                      <span style={{ fontSize: "0.7rem", color: "var(--text-muted)", fontWeight: 500 }}>
                        ({sec.questions.length} question{sec.questions.length === 1 ? "" : "s"})
                      </span>
                    </div>
                    {(setViewMode === "edit" || setViewMode === "create") && (
                      <div style={{ display: "flex", gap: "6px" }}>
                        <button
                          type="button"
                          onClick={() => resetSectionOrder(sIdx)}
                          title="Reset order to original sequence"
                          style={{
                            border: "1px solid var(--border-default)",
                            backgroundColor: "var(--surface-card)",
                            color: "var(--text-body)",
                            padding: "5px 12px",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>&#8634;</span> Reset
                        </button>
                        <button
                          type="button"
                          onClick={() => shuffleSectionQuestions(sIdx)}
                          title="Shuffle the order of questions in this section"
                          style={{
                            border: "1px solid var(--brand-primary-subtle)",
                            backgroundColor: "var(--brand-primary-subtle)",
                            color: "var(--brand-primary-hover)",
                            padding: "5px 12px",
                            borderRadius: "6px",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <span>&#127922;</span> Shuffle
                        </button>
                      </div>
                    )}
                  </div>
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "60px" }}>Order</th>
                        <th style={{ width: "50px" }}>Q#</th>
                        <th>Question</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sec.questions.map((q, qIdx) => (
                        <tr key={q.mappingId}>
                          <td>
                            {setViewMode === "view" ? (
                              q.setOrder
                            ) : (
                              <Form.Control
                                size="sm"
                                type="number"
                                value={q.setOrder}
                                onChange={(e) => {
                                  const val = Number(e.target.value);
                                  setSetFormSections((prev) => {
                                    const updated = [...prev];
                                    const sec2 = { ...updated[sIdx], questions: [...updated[sIdx].questions] };
                                    sec2.questions[qIdx] = { ...sec2.questions[qIdx], setOrder: val };
                                    updated[sIdx] = sec2;
                                    return updated;
                                  });
                                }}
                                style={{ width: "50px", padding: "2px 4px", fontSize: "0.8rem" }}
                              />
                            )}
                          </td>
                          <td>{qIdx + 1}</td>
                          <td style={{ fontSize: "0.8rem" }}>
                            <div dangerouslySetInnerHTML={{ __html: q.questionName || q.stemStatement || `Question #${q.mappingId}` }} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {(setViewMode === "edit" || setViewMode === "create") && (
            <Button variant="primary" size="sm" onClick={handleSaveSet} disabled={setSaving || !setFormName.trim()} style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)", margin: "2px" }}>
              {setSaving ? <><Spinner role="status" animation="border" size="sm" className="me-1" />Saving...</> : "Save Set"}
            </Button>
          )}
          <Button variant="secondary" size="sm" onClick={() => setShowSetsModal(false)} style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Batch Picker Modal — always shown. Admin picks one batch before the
          preview/PDF flow runs so each PDF is scoped to that batch only. */}
      <Modal
        show={showBatchPicker}
        onHide={() => setShowBatchPicker(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700 }}>
            Select a batch to generate Admission Tickets
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: 12, color: "var(--text-body)", fontSize: "0.85rem" }}>
            This test has <strong>{hallTicketTotalCount.toLocaleString()}</strong> mapped student{hallTicketTotalCount === 1 ? "" : "s"}.
            Pick a batch below to issue tickets for that batch only.
          </div>
          {hallTicketBatches.length === 0 ? (
            <div style={{ color: "var(--text-muted)" }}>No batches found for this test.</div>
          ) : (
            <div style={{ maxHeight: "55vh", overflowY: "auto", border: "1px solid var(--border-default)", borderRadius: 6 }}>
              <table className="table table-sm mb-0" style={{ fontSize: "0.85rem" }}>
                <thead style={{ background: "var(--surface-page)", position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ width: 40 }}></th>
                    <th>Batch</th>
                    <th style={{ width: 130, textAlign: "right" }}>Students</th>
                  </tr>
                </thead>
                <tbody>
                  {hallTicketBatches.map((b) => {
                    const overCap = b.studentCount > 1500;
                    return (
                      <tr
                        key={b.batchId}
                        onClick={() => !overCap && setHallTicketSelectedBatchId(b.batchId)}
                        style={{
                          cursor: overCap ? "not-allowed" : "pointer",
                          background: hallTicketSelectedBatchId === b.batchId ? "#eff6ff" : undefined,
                          opacity: overCap ? 0.55 : 1,
                        }}
                      >
                        <td>
                          <input
                            type="radio"
                            name="hallTicketBatch"
                            checked={hallTicketSelectedBatchId === b.batchId}
                            onChange={() => setHallTicketSelectedBatchId(b.batchId)}
                            disabled={overCap}
                          />
                        </td>
                        <td>{b.batchName || `Batch #${b.batchId}`}</td>
                        <td style={{ textAlign: "right" }}>
                          {b.studentCount.toLocaleString()}
                          {overCap && (
                            <span style={{ marginLeft: 8, color: "var(--status-warning-text)", fontSize: "0.75rem" }}>
                              (over 1,500 cap)
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowBatchPicker(false)}
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d" }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmBatchSelection}
            disabled={!hallTicketSelectedBatchId}
            style={{ backgroundColor: "var(--brand-primary)", color: "var(--text-on-brand)", border: "1px solid var(--brand-primary)" }}
          >
            Continue
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Answer Key upload/manage modal */}
      <AnswerKeyModal
        show={showAnswerKeyModal}
        testId={Number(testId)}
        testName={quiz?.name}
        onClose={() => setShowAnswerKeyModal(false)}
      />

      {/* Hall Ticket Preview Modal — show the rendered HTML first, then
          the admin confirms with "Generate PDF" to actually download. */}
      <Modal
        show={showHallTicketPreview}
        onHide={() => setShowHallTicketPreview(false)}
        size="xl"
        centered
        backdrop={hallTicketPdfLoading ? "static" : true}
      >
        <Modal.Header closeButton={!hallTicketPdfLoading}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
            <span>Admission Ticket — Preview</span>
            {hallTicketSelectedBatchId && (
              <span style={{
                fontSize: "0.8rem",
                fontWeight: 600,
                padding: "2px 10px",
                borderRadius: 12,
                background: "var(--status-success-bg)",
                color: "var(--status-success-text)",
                border: "1px solid var(--status-success-border)",
              }}>
                {hallTicketBatches.find((b) => b.batchId === hallTicketSelectedBatchId)?.batchName || `Batch #${hallTicketSelectedBatchId}`}
              </span>
            )}
            {hallTicketStudentCount > 0 && (
              <span style={{
                fontSize: "0.8rem",
                fontWeight: 600,
                padding: "2px 10px",
                borderRadius: 12,
                background: hallTicketStudentCount > 1000 ? "#fef3c7" : "#dbeafe",
                color: hallTicketStudentCount > 1000 ? "#92400e" : "#1e40af",
                border: `1px solid ${hallTicketStudentCount > 1000 ? "#fde68a" : "#bfdbfe"}`,
              }}>
                {hallTicketStudentCount.toLocaleString()} student{hallTicketStudentCount === 1 ? "" : "s"}
                {hallTicketStudentCount > 1000 && " (large — PDF may take up to 5 min)"}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, background: "var(--surface-sunken)" }}>
          {hallTicketPreviewHtml === null ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, gap: 10, color: "var(--text-muted)" }}>
              <Spinner role="status" animation="border" size="sm" />
              <span>Rendering preview…</span>
            </div>
          ) : (
            <iframe
              title="Hall Ticket Preview"
              srcDoc={hallTicketPreviewHtml}
              style={{ width: "100%", height: "72vh", border: 0, background: "var(--surface-card)" }}
              sandbox="allow-same-origin"
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Form.Check
              type="switch"
              id="hall-ticket-with-photo"
              label="Include student photos"
              checked={hallTicketWithPhoto}
              disabled={hallTicketPdfLoading || hallTicketLoading}
              onChange={(e) => {
                const next = e.target.checked;
                setHallTicketWithPhoto(next);
                loadHallTicketPreview(hallTicketSelectedBatchId, next);
              }}
              style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-strong)" }}
            />
            <small style={{ color: "var(--text-muted)" }}>
              One A5 page per student. Review the layout, then click <strong>Generate PDF</strong> to download.
            </small>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              onClick={() => setShowHallTicketPreview(false)}
              disabled={hallTicketPdfLoading}
              style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d" }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleConfirmHallTicketPdf}
              disabled={hallTicketPdfLoading || hallTicketPreviewHtml === null}
              style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)" }}
            >
              {hallTicketPdfLoading ? (
                <><Spinner role="status" animation="border" size="sm" className="me-1" />Generating PDF…</>
              ) : "Generate PDF"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Mark Foil Preview Modal — mirrors the hall-ticket preview flow.
          Renders the templated HTML inside an iframe so the admin can verify
          layout before paying for the PDF render. */}
      <Modal
        show={showMarkFoilPreview}
        onHide={() => setShowMarkFoilPreview(false)}
        size="xl"
        centered
        backdrop={markFoilPdfLoading ? "static" : true}
      >
        <Modal.Header closeButton={!markFoilPdfLoading}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700, display: "flex", alignItems: "center", gap: 10 }}>
            <span>Mark Foil — Preview</span>
            {markFoilPageCount > 0 && (
              <span style={{
                fontSize: "0.8rem",
                fontWeight: 600,
                padding: "2px 10px",
                borderRadius: 12,
                background: "var(--status-info-bg)",
                color: "var(--brand-primary-active)",
                border: "1px solid #bfdbfe",
              }}>
                {markFoilPageCount} page{markFoilPageCount === 1 ? "" : "s"}
              </span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, background: "var(--surface-sunken)" }}>
          {markFoilError ? (
            <div style={{ padding: 24, color: "var(--status-danger)", background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", borderRadius: 8, margin: 16 }}>
              {markFoilError}
            </div>
          ) : markFoilPreviewHtml === null ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, gap: 10, color: "var(--text-muted)" }}>
              <Spinner role="status" animation="border" size="sm" />
              <span>Rendering preview…</span>
            </div>
          ) : (
            <iframe
              title="Mark Foil Preview"
              srcDoc={markFoilPreviewHtml}
              style={{ width: "100%", height: "72vh", border: 0, background: "var(--surface-card)" }}
              sandbox="allow-same-origin"
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <small style={{ color: "var(--text-muted)" }}>
            One A4 page per course (students chunked 40/page). Review, then click <strong>Generate PDF</strong>.
          </small>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              onClick={() => setShowMarkFoilPreview(false)}
              disabled={markFoilPdfLoading}
              style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d" }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={handleConfirmMarkFoilPdf}
              disabled={markFoilPdfLoading || markFoilPreviewHtml === null || !!markFoilError}
              style={{ backgroundColor: "var(--status-success)", color: "var(--text-on-brand)", border: "1px solid var(--status-success)" }}
            >
              {markFoilPdfLoading ? (
                <><Spinner role="status" animation="border" size="sm" className="me-1" />Generating PDF…</>
              ) : "Generate PDF"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Clone Test Modal */}
      <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)} centered>
        <Modal.Header closeButton style={{ background: "linear-gradient(135deg, #6f42c1 0%, var(--brand-accent) 100%)", color: "var(--text-on-brand)" }}>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 700 }}>Clone to a New Test</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Source info */}
          <div style={{
            background: "linear-gradient(135deg, var(--brand-primary-subtle) 0%, var(--brand-primary-subtle) 100%)",
            border: "1px solid var(--brand-primary-subtle)",
            borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: "0.84rem",
          }}>
            <div style={{ display: "flex", gap: 16 }}>
              <span><span style={{ color: "var(--text-muted)", fontWeight: 500 }}>Test Id</span> <strong>{testId}</strong></span>
              <span><span style={{ color: "var(--text-muted)", fontWeight: 500 }}>Test Name</span> <strong>{quiz?.name}</strong></span>
            </div>
            {quiz && (
              <div style={{ marginTop: 6, fontSize: "0.78rem", color: "var(--text-muted)" }}>
                {quiz.categoryName && <span style={{ marginRight: 12 }}>{quiz.categoryName}</span>}
                {(quiz as any).totalQuestions != null && <span>{(quiz as any).totalQuestions || quiz.sections?.reduce((s: number, sec: any) => s + (sec.totalQuestions || 0), 0) || 0} questions</span>}
              </div>
            )}
          </div>

          {/* Clone result */}
          {cloneResult && (
            <div style={{
              background: "var(--status-success-bg)", border: "1px solid #86efac", borderRadius: 8,
              padding: "12px 16px", marginBottom: 16, fontSize: "0.84rem", color: "var(--status-success-text)",
            }}>
              <strong>Cloned successfully!</strong>
              <div style={{ marginTop: 4 }}>
                New Test ID: <strong>{cloneResult.testId}</strong> — {cloneResult.name}
                {!cloneForm.excludeQuestions && <span> ({cloneResult.totalQuestions} questions)</span>}
              </div>
              <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                <a
                  href={`/quick-quiz-maker?mode=view&testId=${cloneResult.testId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block", padding: "4px 12px", borderRadius: 6,
                    fontSize: "0.78rem", fontWeight: 600, textDecoration: "none",
                    background: "var(--brand-primary)", color: "var(--text-on-brand)",
                  }}
                >
                  View Cloned Test
                </a>
                <a
                  href={`/quick-quiz-maker?mode=edit&testId=${cloneResult.testId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block", padding: "4px 12px", borderRadius: 6,
                    fontSize: "0.78rem", fontWeight: 600, textDecoration: "none",
                    background: "var(--surface-card)", color: "var(--brand-primary)", border: "1px solid var(--brand-primary)",
                  }}
                >
                  Edit Cloned Test
                </a>
              </div>
            </div>
          )}

          {cloneError && (
            <div style={{
              background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", borderRadius: 8,
              padding: "10px 14px", marginBottom: 14, fontSize: "0.82rem", color: "var(--status-danger)",
            }}>
              {cloneError}
            </div>
          )}

          {!cloneResult && (
            <>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>Test Name</label>
                <input
                  type="text"
                  value={cloneForm.name}
                  onChange={(e) => setCloneForm({ ...cloneForm, name: e.target.value })}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.88rem" }}
                  placeholder="New test name"
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>Test Date</label>
                <input
                  type="date"
                  value={cloneForm.testDate}
                  onChange={(e) => setCloneForm({ ...cloneForm, testDate: e.target.value })}
                  style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.88rem" }}
                />
              </div>
              <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>Start Date</label>
                  <input
                    type="datetime-local"
                    value={cloneForm.startDate}
                    onChange={(e) => setCloneForm({ ...cloneForm, startDate: e.target.value })}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.84rem" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: "block", fontWeight: 600, fontSize: "0.84rem", marginBottom: 4 }}>End Date</label>
                  <input
                    type="datetime-local"
                    value={cloneForm.endDate}
                    onChange={(e) => setCloneForm({ ...cloneForm, endDate: e.target.value })}
                    style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1.5px solid var(--border-default)", fontSize: "0.84rem" }}
                  />
                </div>
              </div>
              <div style={{
                padding: "10px 14px", background: "var(--surface-page)", borderRadius: 8,
                border: "1px solid var(--border-default)", marginBottom: 8,
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, fontSize: "0.84rem", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={cloneForm.excludeQuestions}
                    onChange={(e) => setCloneForm({ ...cloneForm, excludeQuestions: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: "#6f42c1", cursor: "pointer" }}
                  />
                  Exclude Questions
                </label>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4, paddingLeft: 26 }}>
                  Clone only test metadata (name, category, dates, settings). No sections or questions will be copied.
                </div>
              </div>
              <div style={{
                padding: "10px 14px", background: "var(--surface-page)", borderRadius: 8,
                border: "1px solid var(--border-default)", marginBottom: 8,
              }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0, fontSize: "0.84rem", fontWeight: 600 }}>
                  <input
                    type="checkbox"
                    checked={cloneForm.excludeCourseMappings}
                    onChange={(e) => setCloneForm({ ...cloneForm, excludeCourseMappings: e.target.checked })}
                    style={{ width: 18, height: 18, accentColor: "#6f42c1", cursor: "pointer" }}
                  />
                  Exclude Course Mappings
                </label>
                <div style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 4, paddingLeft: 26 }}>
                  Skip copying the source test's course / program / batch / term / school mappings. Leave unchecked to carry mappings into the new test.
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            size="sm"
            style={{ backgroundColor: "#6c757d", color: "var(--text-on-brand)", border: "1px solid #6c757d", margin: "2px" }}
            onClick={() => setShowCloneModal(false)}
          >
            {cloneResult ? "Close" : "Cancel"}
          </Button>
          {!cloneResult && (
            <Button
              variant="primary"
              size="sm"
              style={{ backgroundColor: "#6f42c1", color: "var(--text-on-brand)", border: "1px solid #6f42c1", margin: "2px" }}
              onClick={handleClone}
              disabled={cloneSaving || !cloneForm.name.trim()}
            >
              {cloneSaving ? <><Spinner role="status" animation="border" size="sm" className="me-1" />Cloning...</> : "Clone Test"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default QuizViewPanel;
